import { readPanelControlDraft, restorePanelDraftImages, type PanelControlDraft } from '../utils/panelControlDraft';
import { remapPanelScopedValues, derivePanelControlGroups, splitPanelAnswersForSubmit, applyEarthingSourceIds, reconcilePanelAnswers, type PanelControlViewGroup, isPanelControlNode, panelSectionsByNode, isCurrentPanelGroup, panelQuestionKey } from '../utils/panelControl';
import type { ControlTaskControlGroup, ControlGroupAnswers } from '../services/controlTaskService';
import {
  NETWORK_TYPE_OPTIONS,
  getNetworkTypeValue,
  toNetworkTypeAnswer,
  type NetworkType,
} from '../../../utils/networkType';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Tag,
  Tabs,
  TimePicker,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import {
  deleteControlTaskAnswers,
  fetchControlTaskAnswers,
  fetchControlTaskBulkAnswers,
  fetchControlTaskBulkCapacity,
  fetchControlTaskCorrectionForm,
  publishBulkControlTaskReport,
  publishControlTaskCorrectionReport,
  publishControlTaskReport,
  saveControlTaskCorrectionAnswers,
  saveControlTaskAnswers,
  saveControlTaskBulkAnswers,
  type ControlTaskBulkCapacity,
  type EarthingMeasurement,
  type EarthingMeasurementsByNode,
} from '../services/controlTaskService';
import {
  fetchFieldInspectionControlTask,
  fetchFieldInspectionControlTaskByReportFormat,
} from '../services/fieldInspectionService';
import type { RcFile } from 'antd/es/upload';
import { CloseOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import { CheckListIcon, InformationCircleIcon } from 'hugeicons-react';
import { apiFetch } from 'services/apiClient';
import ProposalService from '../../../services/ProposalService';
import DocumentService from '../../../services/DocumentService';
import NonconformityReasonService, {
  INonconformityReason,
  INonconformityReasonPayload,
} from '../../../services/NonconformityReasonService';
import { getInspectionTask } from '../../inspection-tasks/services/inspection-taskService';
import { hasRole, normalizeTurkish, resolveParameterUnitLabel } from '../../../utils';
import EarthingMeasurementsTable, {
  type EarthingMeasurementsTableHandle,
} from '../components/EarthingMeasurementsTable';
import RcdMeasurementsTable, { type RcdMeasurementsTableHandle } from '../components/RcdMeasurementsTable';
import type { RcdMeasurement, RcdMeasurementsByNode } from '../types/rcdMeasurement';
import {
  normalizeEarthingMeasurementDidNotTrip,
  prepareEarthingMeasurementsForSubmit,
} from '../utils/earthingMeasurements';
import { completeRcdMeasurements, normalizeRcdMeasurementsByNode, prepareRcdMeasurementsForSubmit } from '../utils/rcdMeasurements';

dayjs.extend(customParseFormat);
dayjs.locale('tr');

const getQuestionKey = (question: any): string | undefined => {
    if (!question || question.id === null || question.id === undefined) {
      return undefined;
    }
    return String(question.id);
  };

const ISTANBUL_TIME_ZONE = 'Europe/Istanbul';

const formatIstanbulCalendarDate = (value: Date): string | null => {
  if (Number.isNaN(value.getTime())) {
    return null;
  }
  const dateParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: ISTANBUL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const year = dateParts.find((part) => part.type === 'year')?.value;
  const month = dateParts.find((part) => part.type === 'month')?.value;
  const day = dateParts.find((part) => part.type === 'day')?.value;
  return year && month && day ? `${year}-${month}-${day}` : null;
};

export const resolveFutureInspectionTaskDate = (inspectionTask: any, now = new Date()): string | null => {
  const scheduledAt = inspectionTask?.date ?? inspectionTask?.appointment?.startDate;
  if (!scheduledAt) {
    return null;
  }
  const scheduledDate = formatIstanbulCalendarDate(new Date(scheduledAt));
  const currentDate = formatIstanbulCalendarDate(now);
  return scheduledDate && currentDate && scheduledDate > currentDate ? scheduledDate : null;
};

const { Title, Text } = Typography;
const { Search, TextArea } = Input;
const { Option } = Select;

const questionTypeMap: Record<string, { label: string; color: string }> = {
  MULTIPLE: { label: 'Çoktan Seçmeli', color: 'blue' },
  FREETEXT: { label: 'Serbest Metin', color: 'purple' },
  NETWORK_TYPE: { label: 'Şebeke Tipi', color: 'cyan' },
  TITLE: { label: 'Ünvan', color: 'default' },
  ADDRESS: { label: 'Adres', color: 'default' },
  SGK_REGISTRATION_NUMBER: { label: 'SGK Sicil Numarası', color: 'default' },
  PHONE: { label: 'Telefon', color: 'default' },
  CONTRACT_ID: { label: 'ISG Katip Sözleşme ID', color: 'default' },
  EMAIL: { label: 'E-posta', color: 'default' },
  IDENTITY_NUMBER: { label: 'TC Kimlik No', color: 'cyan' },
  REGISTRATION_DATE: { label: 'Kalıcı Kayıt Tarihi', color: 'cyan' },
  WORK_EQUIPMENT: { label: 'İş Ekipmanı', color: 'cyan' },
  REPORT_NUMBER: { label: 'Rapor Numarası', color: 'default' },
  REPORT_DATE: { label: 'Rapor Tarihi', color: 'gold' },
  CONTROL_START_DATE: { label: 'Kontrol Başlangıç Tarihi', color: 'gold' },
  CONTROL_END_DATE: { label: 'Kontrol Bitiş Tarihi', color: 'gold' },
  NEXT_CONTROL_DATE: { label: 'Sonraki Kontrol Tarihi', color: 'gold' },
  STARTED_AT: { label: 'Başlangıç Saati', color: 'geekblue' },
  FINISHED_AT: { label: 'Bitiş Saati', color: 'geekblue' },
  NUMBER: { label: 'Sayı', color: 'green' },
  DATE: { label: 'Tarih', color: 'gold' },
  TIME: { label: 'Saat', color: 'geekblue' },
  YEAR: { label: 'Yıl', color: 'cyan' },
  IMAGE: { label: 'Görsel', color: 'magenta' },
  HVAC: { label: 'Havalandırma/Klima', color: 'orange' },
  PLUMBING: { label: 'Tesisat', color: 'volcano' },
  HYDRANT: { label: 'Hidrant', color: 'red' },
  CABINET: { label: 'Dolap', color: 'magenta' },
  FUNCTION: { label: 'Fonksiyon', color: 'lime' },
  TABLE: { label: 'Tablo Sorusu', color: 'blue' },
};

type MultipleAnswerValue = {
  id: string | null;
  value?: string | null;
  abbreviation?: string | null;
  description?: string | null;
  nonconformityReasonId?: number | string | null;
  nonconformity_reason_id?: number | string | null;
  isTrue?: boolean | null;
  images?: MultipleAnswerImageValue[];
  documentId?: number | string | null;
  dataUrl?: string | null;
  imageUrl?: string | null;
  imagePreviewUrl?: string | null;
  imageName?: string | null;
  imageType?: string | null;
  type?: 'MULTIPLE';
};

type MultipleAnswerImageValue = {
  key: string;
  title: string;
  required?: boolean;
  documentId?: number | string | null;
  dataUrl?: string | null;
  imageUrl?: string | null;
  imagePreviewUrl?: string | null;
  imageName?: string | null;
  imageType?: string | null;
};

type MultipleAnswerPayload = MultipleAnswerValue | MultipleAnswerValue[];

const MULTIPLE_TYPE = 'MULTIPLE';

const normalizeAnswerImageFields = (answer: any): MultipleAnswerImageValue[] => {
  const rawFields = answer?.imageFields;
  let parsed: any = rawFields;
  if (typeof rawFields === 'string') {
    if (!rawFields.trim()) {
      parsed = [];
    } else {
      try {
        parsed = JSON.parse(rawFields);
      } catch {
        parsed = [];
      }
    }
  }
  if (!Array.isArray(parsed)) {
    return [];
  }
  return parsed
    .map((item: any, index: number) => {
      const title = ensureStringValue(item?.title).trim();
      if (!title) {
        return null;
      }
      return {
        key: ensureStringValue(item?.key).trim() || String(index + 1),
        title,
        required: item?.required === true,
      } as MultipleAnswerImageValue;
    })
    .filter(Boolean) as MultipleAnswerImageValue[];
};

const hasMultipleAnswerImageValue = (image?: MultipleAnswerImageValue | null): boolean =>
  !!(image?.documentId || image?.imageUrl || image?.imagePreviewUrl || image?.dataUrl);

type StaticValueFormEntry = {
  id?: number;
  name: string;
  functionTag?: string;
  value?: string;
  [key: string]: any;
};

type FunctionSourceValue = {
  text: string;
  numeric?: number;
};

type FunctionEvaluationContext = {
  questionValues: Record<string, FunctionSourceValue>;
  attributeValues: Record<string, FunctionSourceValue>;
  toolValues: Record<string, FunctionSourceValue>;
};

type FunctionEvaluationResult = {
  displayValue: string;
  numericValue?: number;
  booleanValue?: boolean;
};

const OTHER_CONTROL_AREA_VALUE = 'OTHER' as const;
const SHARED_BULK_INPUT_MODE = 'SHARED';

type ControlAreaValue = number | typeof OTHER_CONTROL_AREA_VALUE;

type NoteMap = Record<number, string>;
type PendingMultipleAnswerImageMap = Record<string, RcFile>;
type AnswersByOrderMap = Record<string, Record<string, any>>;

export type BulkDataGroup = {
  id: string;
  label: string;
  signature: string;
  orderKeys: string[];
  answers: Record<string, any>;
};

export type BulkFormStatusKind = 'empty' | 'group' | 'custom';

export type BulkFormStatus = {
  orderKey: string;
  orderNo: number;
  status: BulkFormStatusKind;
  label: string;
  hasAnswers: boolean;
  groupId?: string;
  groupLabel?: string;
  sourceGroupId?: string;
  sourceGroupLabel?: string;
};

type BulkPendingGroupEdit = {
  orderKey: string;
  groupId: string;
  groupLabel: string;
  orderKeys: string[];
};

type AnswerEditContext = {
  scopeKey?: string;
  answers: Record<string, any>;
  onAnswerChange: (question: any, rawValue: any, answerOptions?: any[]) => void;
  hideNonconformityActions?: boolean;
  hideFalseMultipleAnswerImages?: boolean;
};

type ControlAreaOption = {
  id: ControlAreaValue;
  name: string;
  isOther?: boolean;
};

export type IncompleteQuestionIssue = {
  key: string;
  questionKey: string;
  displayOrder: number;
  displayLabel: string;
  sortIndex: number;
  questionTitle: string;
  reason: 'REQUIRED' | 'MISSING_FALSE_DESCRIPTION' | 'MISSING_REQUIRED_IMAGE';
  reasonLabel: string;
  bulkOrderNo?: number;
};

const QUESTION_ANCHOR_PREFIX = 'control-task-question-';

const ensureStringValue = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return typeof value === 'string' ? value : String(value);
};

const CONTROL_CRITERIA_NODE_TYPE = 'CONTROL_CRITERIA_AND_TESTS';
const RCD_NODE_TYPE = 'rcd';
const EARTHING_NODE_TYPE = 'earthinginformation';

const isControlCriteriaNode = (node: any): boolean =>
  ensureStringValue(node?.type).trim().toUpperCase() === CONTROL_CRITERIA_NODE_TYPE;

export const isRcdNode = (node: any): boolean =>
  [RCD_NODE_TYPE, 'earthing_information'].includes(ensureStringValue(node?.type).trim().toLowerCase());

export const isEarthingNode = (node: any): boolean =>
  [EARTHING_NODE_TYPE, 'earthing'].includes(ensureStringValue(node?.type).trim().toLowerCase());

export const shouldRenderRcdMeasurementsTable = (
  node: any,
  isBulkReportEntryEnabled: boolean,
  isCorrectionMode: boolean,
  responseKind?: string,
): boolean => isRcdNode(node) && !isBulkReportEntryEnabled && !isCorrectionMode
  && (!responseKind || responseKind === 'INITIAL');

export const shouldRenderEarthingMeasurementsTable = (
  node: any,
  isBulkReportEntryEnabled: boolean,
  isCorrectionMode: boolean,
  responseKind?: string,
): boolean => isEarthingNode(node) && !isBulkReportEntryEnabled && !isCorrectionMode
  && (!responseKind || responseKind === 'INITIAL');

const normalizeEarthingMeasurementsByNode = (value: unknown): EarthingMeasurementsByNode => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return Object.entries(value as Record<string, unknown>).reduce<EarthingMeasurementsByNode>(
    (result, [nodeId, measurements]) => {
      if (!Array.isArray(measurements)) {
        return result;
      }
      result[nodeId] = measurements
        .filter((measurement): measurement is EarthingMeasurement =>
          !!measurement && typeof measurement === 'object' && !Array.isArray(measurement),
        )
        .map(normalizeEarthingMeasurementDidNotTrip);
      return result;
    },
    {},
  );
};

export const resolveReportNetworkType = (
  answers: Record<string, unknown>,
  questionBundlesById: Record<string, { question?: { questionType?: string } }>,
): NetworkType | undefined => {
  const resolved = new Set<NetworkType>();
  Object.entries(questionBundlesById).forEach(([questionId, bundle]) => {
    if (bundle?.question?.questionType !== 'NETWORK_TYPE') {
      return;
    }
    const networkType = getNetworkTypeValue(answers[questionId]);
    if (networkType) {
      resolved.add(networkType);
    }
  });
  return resolved.size === 1 ? Array.from(resolved)[0] : undefined;
};

export const shouldRenderControlTaskNoteInput = (
  node: any,
  normalizedQuestionSearchText: string,
  showOnlyRequiredQuestions: boolean,
  hideNotes = false,
): boolean =>
  node?.type === 'NOTE' &&
  !normalizedQuestionSearchText &&
  !showOnlyRequiredQuestions &&
  !hideNotes;

export const resolveBulkReportCountLabel = (bulkReportTableTitle?: unknown): string => {
  const normalizedTitle = ensureStringValue(bulkReportTableTitle).trim();
  if (!normalizedTitle) {
    return 'Rapor Sayısı';
  }
  return /sayısı$/i.test(normalizedTitle) || /sayisi$/i.test(normalizedTitle)
    ? normalizedTitle
    : `${normalizedTitle} Sayısı`;
};

export const resolveBulkFormDisplayLabel = (
  orderNo: number | string,
  bulkReportUnitLabel?: unknown,
): string => {
  const normalizedOrderNo = Math.max(1, Math.floor(Number(orderNo) || 1));
  const normalizedLabel = ensureStringValue(bulkReportUnitLabel).trim();
  return normalizedLabel ? `${normalizedOrderNo}.${normalizedLabel}` : `Form ${normalizedOrderNo}`;
};

const clampBulkActiveOrder = (
  value: number | string | undefined,
  count: number,
): number => {
  const normalizedCount = Math.max(1, Math.floor(Number(count) || 1));
  const orderNo = Math.max(1, Math.floor(Number(value) || 1));
  return Math.min(orderNo, normalizedCount);
};

export const resolveBulkActiveAnswers = (
  activeOrder: number | string | undefined,
  bulkSharedAnswers: Record<string, any>,
  bulkAnswersByOrder: Record<string, Record<string, any>>,
): Record<string, any> => {
  return {
    ...bulkSharedAnswers,
    ...(bulkAnswersByOrder[String(activeOrder)] ?? {}),
  };
};

const cloneAnswerValue = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map((item) => cloneAnswerValue(item));
  }
  if (value && typeof value === 'object') {
    return { ...value };
  }
  return value;
};

const cloneAnswerMap = (source?: Record<string, any>): Record<string, any> => {
  const next: Record<string, any> = {};
  Object.entries(source ?? {}).forEach(([questionId, value]) => {
    next[questionId] = cloneAnswerValue(value);
  });
  return next;
};

export const resolveBulkReportOrderKeys = (bulkReportCount: unknown): string[] => {
  const count = Math.max(1, Math.floor(Number(bulkReportCount) || 1));
  return Array.from({ length: count }, (_, index) => String(index + 1));
};

type BulkReportAnswerMapInput = {
  activeOrder: number | string | undefined;
  activeAnswers: Record<string, any>;
  bulkSharedAnswers: Record<string, any>;
  bulkAnswersByOrder: Record<string, Record<string, any>>;
  bulkReportCount: number;
  isSharedQuestion?: (questionId: string) => boolean;
};

export const synchronizeBulkReportAnswerMaps = ({
  activeOrder,
  activeAnswers,
  bulkSharedAnswers,
  bulkAnswersByOrder,
  bulkReportCount,
  isSharedQuestion,
}: BulkReportAnswerMapInput): {
  sharedAnswers: Record<string, any>;
  answersByOrder: AnswersByOrderMap;
} => {
  const orderKeys = resolveBulkReportOrderKeys(bulkReportCount);
  const sharedAnswers = cloneAnswerMap(bulkSharedAnswers);
  const answersByOrder: AnswersByOrderMap = {};
  orderKeys.forEach((orderKey) => {
    answersByOrder[orderKey] = cloneAnswerMap(bulkAnswersByOrder[orderKey]);
  });

  const applyAnswer = (target: Record<string, any>, questionId: string, value: any) => {
    if (value === undefined) {
      delete target[questionId];
      return;
    }
    target[questionId] = cloneAnswerValue(value);
  };

  const normalizedActiveOrder = String(Math.min(Math.max(1, Math.floor(Number(activeOrder) || 1)), orderKeys.length));

  Object.entries(activeAnswers ?? {}).forEach(([questionId, value]) => {
    if (isSharedQuestion?.(questionId)) {
      applyAnswer(sharedAnswers, questionId, value);
      return;
    }
    applyAnswer(answersByOrder[normalizedActiveOrder], questionId, value);
  });

  return { sharedAnswers, answersByOrder };
};

export const resolveBulkValidationAnswerMaps = (
  input: BulkReportAnswerMapInput,
): Array<{ orderKey: string; orderNo: number; answers: Record<string, any> }> => {
  const orderKeys = resolveBulkReportOrderKeys(input.bulkReportCount);
  const { sharedAnswers, answersByOrder } = synchronizeBulkReportAnswerMaps(input);
  return orderKeys.map((orderKey) => ({
    orderKey,
    orderNo: Number(orderKey),
    answers: {
      ...cloneAnswerMap(sharedAnswers),
      ...cloneAnswerMap(answersByOrder[orderKey]),
    },
  }));
};

export const omitSharedQuestionAnswers = (
  answers: Record<string, any>,
  sharedQuestionIds: Iterable<string>,
): Record<string, any> => {
  const next = { ...answers };
  Array.from(sharedQuestionIds).forEach((questionId) => {
    delete next[String(questionId)];
  });
  return next;
};

const BULK_GROUP_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const resolveBulkGroupLabel = (index: number): string => {
  if (index < BULK_GROUP_LABELS.length) {
    return `Veri Grubu ${BULK_GROUP_LABELS[index]}`;
  }
  return `Veri Grubu ${index + 1}`;
};

const normalizeForBulkSignature = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeForBulkSignature(item));
  }
  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort((first, second) => first.localeCompare(second, 'tr'))
      .reduce<Record<string, any>>((acc, key) => {
        acc[key] = normalizeForBulkSignature(value[key]);
        return acc;
      }, {});
  }
  return value;
};

export const isBulkAnswerValueEmpty = (value: any): boolean => {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length === 0 || value.every((item) => isBulkAnswerValueEmpty(item));
  }
  if (value && typeof value === 'object') {
    if (Array.isArray(value.rows)) {
      return value.rows.every((row: any) => {
        const cells = row?.cells && typeof row.cells === 'object' ? row.cells : {};
        return Object.values(cells).every((cellValue) => isBulkAnswerValueEmpty(cellValue));
      });
    }
    return Object.values(value).every((item) => isBulkAnswerValueEmpty(item));
  }
  return false;
};

export const hasNonEmptyBulkAnswers = (answers: Record<string, any> | undefined): boolean => {
  return Object.values(answers ?? {}).some((value) => !isBulkAnswerValueEmpty(value));
};

export const extractBulkCriteriaAnswers = (
  answers: Record<string, any> | undefined,
  criteriaQuestionIds: Iterable<string>,
): Record<string, any> => {
  const questionIds = new Set(Array.from(criteriaQuestionIds).map((questionId) => String(questionId)));
  const next: Record<string, any> = {};
  if (!questionIds.size) {
    return next;
  }
  Object.entries(answers ?? {}).forEach(([questionId, value]) => {
    if (!questionIds.has(String(questionId)) || isBulkAnswerValueEmpty(value)) {
      return;
    }
    next[questionId] = cloneAnswerValue(value);
  });
  return next;
};

export const createBulkAnswerSignature = (answers: Record<string, any>): string => {
  const normalized = normalizeForBulkSignature(answers);
  return JSON.stringify(normalized);
};

export const resolveBulkDataGroups = (
  orderKeys: string[],
  answersByOrder: AnswersByOrderMap,
  criteriaQuestionIds: Iterable<string>,
): BulkDataGroup[] => {
  const grouped = new Map<
    string,
    {
      orderKeys: string[];
      answers: Record<string, any>;
      firstOrderIndex: number;
    }
  >();

  orderKeys.forEach((orderKey, index) => {
    const criteriaAnswers = extractBulkCriteriaAnswers(answersByOrder[orderKey], criteriaQuestionIds);
    if (!Object.keys(criteriaAnswers).length) {
      return;
    }
    const signature = createBulkAnswerSignature(criteriaAnswers);
    const existing = grouped.get(signature);
    if (existing) {
      existing.orderKeys.push(orderKey);
      return;
    }
    grouped.set(signature, {
      orderKeys: [orderKey],
      answers: criteriaAnswers,
      firstOrderIndex: index,
    });
  });

  return Array.from(grouped.entries())
    .filter(([, group]) => group.orderKeys.length >= 2)
    .sort(([, first], [, second]) => first.firstOrderIndex - second.firstOrderIndex)
    .map(([signature, group], index) => ({
      id: `group:${signature}`,
      label: resolveBulkGroupLabel(index),
      signature,
      orderKeys: group.orderKeys,
      answers: cloneAnswerMap(group.answers),
    }));
};

export const resolveBulkFormStatuses = (
  orderKeys: string[],
  answersByOrder: AnswersByOrderMap,
  criteriaQuestionIds: Iterable<string>,
  dataGroups: BulkDataGroup[],
  overrideSourcesByOrder: Record<string, string> = {},
): BulkFormStatus[] => {
  const groupBySignature = new Map(dataGroups.map((group) => [group.signature, group]));
  const groupById = new Map(dataGroups.map((group) => [group.id, group]));

  return orderKeys.map((orderKey) => {
    const orderNo = Number(orderKey);
    const answersForOrder = answersByOrder[orderKey] ?? {};
    const hasAnswers = hasNonEmptyBulkAnswers(answersForOrder);
    const criteriaAnswers = extractBulkCriteriaAnswers(answersForOrder, criteriaQuestionIds);
    const sourceGroupId = overrideSourcesByOrder[orderKey];
    if (!Object.keys(criteriaAnswers).length) {
      return {
        orderKey,
        orderNo,
        status: 'empty',
        label: 'Boş',
        hasAnswers,
      };
    }

    const group = groupBySignature.get(createBulkAnswerSignature(criteriaAnswers));
    if (group) {
      return {
        orderKey,
        orderNo,
        status: 'group',
        label: group.label,
        hasAnswers,
        groupId: group.id,
        groupLabel: group.label,
      };
    }

    const sourceGroup = sourceGroupId ? groupById.get(sourceGroupId) : undefined;
    return {
      orderKey,
      orderNo,
      status: 'custom',
      label: 'Özelleştirildi',
      hasAnswers,
      sourceGroupId,
      sourceGroupLabel: sourceGroup?.label,
    };
  });
};

export const applyBulkCriteriaAnswersToOrders = (
  answersByOrder: AnswersByOrderMap,
  orderKeys: string[],
  criteriaAnswers: Record<string, any>,
  criteriaQuestionIds: Iterable<string>,
  options?: {
    transformValue?: (questionId: string, value: any) => any;
  },
): AnswersByOrderMap => {
  const criteriaIds = new Set(Array.from(criteriaQuestionIds).map((questionId) => String(questionId)));
  if (!criteriaIds.size) {
    return answersByOrder;
  }
  const next: AnswersByOrderMap = {};
  Object.entries(answersByOrder ?? {}).forEach(([orderKey, answers]) => {
    next[orderKey] = cloneAnswerMap(answers);
  });
  orderKeys.forEach((orderKey) => {
    const target = cloneAnswerMap(next[orderKey]);
    criteriaIds.forEach((questionId) => {
      delete target[questionId];
    });
    Object.entries(criteriaAnswers).forEach(([questionId, value]) => {
      if (!criteriaIds.has(String(questionId)) || isBulkAnswerValueEmpty(value)) {
        return;
      }
      const transformedValue = options?.transformValue
        ? options.transformValue(String(questionId), value)
        : value;
      if (isBulkAnswerValueEmpty(transformedValue)) {
        return;
      }
      target[questionId] = cloneAnswerValue(transformedValue);
    });
    next[orderKey] = target;
  });
  return next;
};

const MULTIPLE_ANSWER_IMAGE_FIELD_KEYS = [
  'documentId',
  'dataUrl',
  'imageUrl',
  'imagePreviewUrl',
  'imageName',
  'imageType',
  'images',
] as const;

const stripMultipleAnswerImageFields = (value: any): any => {
  if (!value || typeof value !== 'object') {
    return value;
  }
  const next = { ...value };
  MULTIPLE_ANSWER_IMAGE_FIELD_KEYS.forEach((field) => {
    delete next[field];
  });
  return next;
};

const isFalseLikeBooleanValue = (value: any): boolean => {
  if (value === false || value === 0) {
    return true;
  }
  return typeof value === 'string' && ['false', '0'].includes(value.trim().toLowerCase());
};

export const stripFalseMultipleAnswerImages = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map((item) => (
      isFalseLikeBooleanValue(item?.isTrue) ? stripMultipleAnswerImageFields(item) : cloneAnswerValue(item)
    ));
  }
  if (value && typeof value === 'object' && isFalseLikeBooleanValue(value?.isTrue)) {
    return stripMultipleAnswerImageFields(value);
  }
  return cloneAnswerValue(value);
};

export const sanitizeBulkAssignmentCriteriaAnswers = (
  criteriaAnswers: Record<string, any>,
): Record<string, any> => {
  const next: Record<string, any> = {};
  Object.entries(criteriaAnswers ?? {}).forEach(([questionId, value]) => {
    next[questionId] = stripFalseMultipleAnswerImages(value);
  });
  return next;
};

export const resolveCommonBulkCriteriaAnswers = (
  orderKeys: string[],
  answersByOrder: AnswersByOrderMap,
  criteriaQuestionIds: Iterable<string>,
): Record<string, any> => {
  if (!orderKeys.length) {
    return {};
  }
  const criteriaIds = Array.from(criteriaQuestionIds).map((questionId) => String(questionId));
  if (!criteriaIds.length) {
    return {};
  }
  const firstAnswers = extractBulkCriteriaAnswers(answersByOrder[orderKeys[0]], criteriaIds);
  const common: Record<string, any> = {};
  criteriaIds.forEach((questionId) => {
    const value = firstAnswers[questionId];
    if (isBulkAnswerValueEmpty(value)) {
      return;
    }
    const signature = createBulkAnswerSignature({ [questionId]: value });
    const existsOnEveryOrder = orderKeys.every((orderKey) => {
      const criteriaAnswers = extractBulkCriteriaAnswers(answersByOrder[orderKey], criteriaIds);
      if (isBulkAnswerValueEmpty(criteriaAnswers[questionId])) {
        return false;
      }
      return createBulkAnswerSignature({ [questionId]: criteriaAnswers[questionId] }) === signature;
    });
    if (existsOnEveryOrder) {
      common[questionId] = cloneAnswerValue(value);
    }
  });
  return common;
};

export const resolveFixedTableRowCount = (question: any): number | undefined => {
  const raw = question?.fixedRowCount ?? question?.fixed_row_count;
  const numeric = Number(raw);
  return Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : undefined;
};

export const normalizeTableRowsToFixedCount = (
  rowsInput: unknown,
  fixedRowCount: number,
  createDefaultCells: () => Record<string, any> = () => ({}),
): any[] => {
  const count = Math.max(0, Math.floor(Number(fixedRowCount) || 0));
  const rows = Array.isArray(rowsInput) ? rowsInput : [];
  return Array.from({ length: count }, (_, index) => {
    const existing = rows[index];
    if (existing && typeof existing === 'object') {
      return {
        ...existing,
        rowOrder: index + 1,
        cells: (existing as any).cells || {},
      };
    }
    return {
      rowOrder: index + 1,
      cells: createDefaultCells(),
    };
  });
};

const normalizeQuestionDescriptionFields = (question: any) => {
  if (!question || typeof question !== 'object') {
    return question;
  }
  const normalizedDescription = ensureStringValue(
    question?.description ?? question?.questionDescription,
  ).trim();
  if (!normalizedDescription) {
    return question;
  }
  return {
    ...question,
    description: normalizedDescription,
    questionDescription: normalizedDescription,
  };
};

const renderQuestionDescriptionTooltip = (description: string) => (
  <span style={{ whiteSpace: 'pre-line' }}>{description}</span>
);

const toOptionalBoolean = (value: any): boolean | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
    return undefined;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
  }
  return undefined;
};

const toOptionalNumber = (value: any): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const normalizeStaticValueList = (input: any): StaticValueFormEntry[] => {
  if (!Array.isArray(input)) {
    return [];
  }
  return input.map((item: any) => {
    const source = item && typeof item === 'object' ? { ...item } : {};
    const id = toOptionalNumber(source.id);
    const rawName = source.name != null ? String(source.name) : '';
    const rawTag = source.functionTag != null ? String(source.functionTag) : '';
    const functionTag = rawTag.trim().length ? rawTag.trim() : undefined;
    const value = ensureStringValue(source.value);
    return {
      ...source,
      id,
      name: rawName,
      functionTag,
      value,
    } as StaticValueFormEntry;
  });
};

const buildStaticValueKey = (item: StaticValueFormEntry, index: number): string => {
  if (item.id !== undefined) {
    return `id:${item.id}`;
  }
  if (item.functionTag) {
    return `tag:${item.functionTag}`;
  }
  if (item.name) {
    return `name:${item.name.toLowerCase()}`;
  }
  return `index:${index}`;
};

const prepareStaticValuesForSubmit = (values: StaticValueFormEntry[]): any[] | undefined => {
  if (!values.length) {
    return undefined;
  }
  return values.map((item) => {
    const payload: Record<string, any> = {};
    Object.entries(item).forEach(([key, rawValue]) => {
      if (key.startsWith('__')) {
        return;
      }
      if (rawValue === undefined) {
        return;
      }
      if (key === 'value') {
        if (rawValue === null) {
          payload[key] = null;
          return;
        }
        if (typeof rawValue === 'string') {
          const trimmed = rawValue.trim();
          payload[key] = trimmed.length ? trimmed : null;
          return;
        }
        payload[key] = rawValue;
        return;
      }
      payload[key] = rawValue;
    });
    if (!('name' in payload)) {
      payload.name = '';
    }
    if (!('value' in payload)) {
      payload.value = null;
    }
    return payload;
  });
};

const normalizeFunctionTag = (value: any): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.toUpperCase();
};

const tryParseNumericValue = (value: any): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/\s+/g, '').replace(',', '.');
    if (!normalized) {
      return undefined;
    }
    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  return undefined;
};

const normalizeFunctionNumericValue = (value: number): number => {
  const rounded = Number(value.toFixed(12));
  return Object.is(rounded, -0) ? 0 : rounded;
};

const roundFunctionValue = (value: number, digits = 0): number => {
  if (!Number.isFinite(value)) {
    return value;
  }
  const normalizedDigits = Number.isFinite(digits) ? Math.min(Math.max(Math.trunc(digits), 0), 12) : 0;
  const factor = 10 ** normalizedDigits;
  return normalizeFunctionNumericValue(Math.round((value + Number.EPSILON) * factor) / factor);
};

const deriveBooleanFromFunctionResult = (result?: FunctionEvaluationResult): boolean | undefined => {
  if (!result) {
    return undefined;
  }
  if (result.booleanValue !== undefined) {
    return result.booleanValue;
  }
  if (result.numericValue !== undefined) {
    return result.numericValue !== 0;
  }
  const numeric = tryParseNumericValue(result.displayValue);
  if (numeric !== undefined) {
    return numeric !== 0;
  }
  const normalized = result.displayValue.trim().toLowerCase();
  if (!normalized) {
    return undefined;
  }
  if (['true', '1', 'evet', 'doğru', 'dogru'].includes(normalized)) {
    return true;
  }
  if (['false', '0', 'hayır', 'hayir', 'yanlış', 'yanlis'].includes(normalized)) {
    return false;
  }
  return undefined;
};

const insertImplicitMultiplication = (input: string): string => {
  if (!input) {
    return input;
  }
  let normalized = input;
  normalized = normalized.replace(/(\d|\)|>)(\s*)(<@)/g, '$1*$3');
  normalized = normalized.replace(/(<@[^@]+@>)(\s*)(\d|\()/g, '$1*$3');
  normalized = normalized.replace(/(\d|\))(\s*)(\()/g, '$1*$3');
  normalized = normalized.replace(/(\))(\s*)(\d)/g, '$1*$3');
  normalized = normalized.replace(/(<@[^@]+@>)(\s*)(<@)/g, '$1*$3');
  return normalized;
};

const buildStaticFunctionValueMap = (values: StaticValueFormEntry[]): Record<string, FunctionSourceValue> => {
  const map: Record<string, FunctionSourceValue> = {};
  values.forEach((item) => {
    const tag = normalizeFunctionTag(item.functionTag);
    if (!tag) {
      return;
    }
    const rawText = ensureStringValue(item.value).trim();
    if (!rawText) {
      return;
    }
    map[tag] = {
      text: rawText,
      numeric: tryParseNumericValue(rawText),
    };
  });
  return map;
};

const buildQuestionFunctionValue = (
  question: any,
  storedValue: any,
  answerOptions: any[] = [],
): FunctionSourceValue | undefined => {
  if (storedValue === null || storedValue === undefined) {
    return undefined;
  }
  const type = question?.questionType;
  switch (type) {
    case 'NETWORK_TYPE': {
      const text = getNetworkTypeValue(storedValue);
      return text ? { text } : undefined;
    }
    case 'NUMBER':
    case 'YEAR': {
      const numeric = typeof storedValue === 'number' ? storedValue : Number(storedValue);
      if (!Number.isFinite(numeric)) {
        return undefined;
      }
      return { text: String(numeric), numeric };
    }
    case 'MULTIPLE': {
      const normalizedValues = normalizeMultipleAnswers(storedValue);
      if (!normalizedValues.length) {
        return undefined;
      }
      const resolvedTitles = normalizedValues
        .map((normalized) =>
          normalized.id
            ? ensureStringValue(
                answerOptions.find((opt: any) => String(opt?.id) === normalized.id)?.title ?? normalized.value ?? '',
              )
            : ensureStringValue(normalized.value ?? ''),
        )
        .map((title) => title.trim())
        .filter((title) => title.length > 0);
      if (!resolvedTitles.length) {
        return undefined;
      }
      const trimmed = resolvedTitles.join(', ');
      const numericCandidate = resolvedTitles.find((title) => tryParseNumericValue(title) !== undefined);
      const numeric = numericCandidate ? tryParseNumericValue(numericCandidate) : undefined;
      if (!trimmed) {
        return undefined;
      }
      return { text: trimmed, numeric };
    }
    case 'IMAGE':
      return undefined;
    default: {
      if (typeof storedValue === 'string') {
        const trimmed = storedValue.trim();
        if (!trimmed) {
          return undefined;
        }
        return { text: trimmed, numeric: tryParseNumericValue(trimmed) };
      }
      if (typeof storedValue === 'number' && Number.isFinite(storedValue)) {
        return { text: String(storedValue), numeric: storedValue };
      }
      return undefined;
    }
  }
};

const createFunctionEvaluationContext = (
  answerMap: Record<string, any>,
  bundlesById: Record<string, QuestionBundle>,
  attributeValues: StaticValueFormEntry[],
  toolValues: StaticValueFormEntry[],
): FunctionEvaluationContext => {
  const questionValues: Record<string, FunctionSourceValue> = {};
  Object.entries(bundlesById).forEach(([questionId, bundle]) => {
    const question = bundle?.question;
    if (!question) {
      return;
    }
    const tag = normalizeFunctionTag(question.functionTag);
    if (!tag) {
      return;
    }
    const sourceValue = buildQuestionFunctionValue(question, answerMap[questionId], bundle?.answers);
    if (sourceValue) {
      questionValues[tag] = sourceValue;
    }
  });
  return {
    questionValues,
    attributeValues: buildStaticFunctionValueMap(attributeValues),
    toolValues: buildStaticFunctionValueMap(toolValues),
  };
};

const resolveFunctionSource = (
  context: FunctionEvaluationContext,
  group: 'T' | 'P' | 'B',
  tag: string,
): FunctionSourceValue | undefined => {
  switch (group) {
    case 'T':
      return context.questionValues[tag];
    case 'P':
      return context.attributeValues[tag];
    case 'B':
      return context.toolValues[tag];
    default:
      return undefined;
  }
};

const evaluateFunctionTemplate = (
  template: string,
  context: FunctionEvaluationContext,
): FunctionEvaluationResult | undefined => {
  const normalizedTemplate = ensureStringValue(template).trim();
  if (!normalizedTemplate) {
    return undefined;
  }

  const tokenRegex = /<@\s*([TPB])\s*:\s*([^@]+?)\s*@>/gi;
  let evaluableExpression = insertImplicitMultiplication(normalizedTemplate);
  let displayExpression = normalizedTemplate;

  evaluableExpression = evaluableExpression.replace(tokenRegex, (match, group: string, rawTag: string) => {
    const normalizedTag = normalizeFunctionTag(rawTag);
    if (!normalizedTag) {
      return '0';
    }
    const source = resolveFunctionSource(context, group as 'T' | 'P' | 'B', normalizedTag);
    if (!source) {
      return '0';
    }
    if (source.numeric !== undefined) {
      return String(source.numeric);
    }
    const numeric = tryParseNumericValue(source.text);
    if (numeric !== undefined) {
      return String(numeric);
    }
    return source.text ?? '';
  });

  displayExpression = displayExpression.replace(tokenRegex, (match, group: string, rawTag: string) => {
    const normalizedTag = normalizeFunctionTag(rawTag);
    if (!normalizedTag) {
      return '';
    }
    const source = resolveFunctionSource(context, group as 'T' | 'P' | 'B', normalizedTag);
    return source?.text ?? '';
  });

  const expressionWithNormalizedFunctions = evaluableExpression.replace(/\bround\b/gi, 'round');
  const hasRoundFunction = /\bround\b/.test(expressionWithNormalizedFunctions);
  const sanitizedExpression = (hasRoundFunction
    ? expressionWithNormalizedFunctions
    : expressionWithNormalizedFunctions.replace(/(\d),(\d)/g, '$1.$2'))
    .replace(/\s+/g, ' ')
    .trim();
  let numericValue: number | undefined;
  let booleanValue: boolean | undefined;

  const expressionWithoutAllowedFunctions = sanitizedExpression.replace(/\bround\b/g, '');
  if (
    sanitizedExpression
    && /^[0-9+\-*/().,<>=!&|%?:\s]+$/.test(expressionWithoutAllowedFunctions)
  ) {
    try {
      // eslint-disable-next-line no-new-func
      const fn = new Function('round', `return (${sanitizedExpression});`);
      const result = fn(roundFunctionValue);
      if (typeof result === 'number' && Number.isFinite(result)) {
        numericValue = normalizeFunctionNumericValue(result);
      } else if (typeof result === 'boolean') {
        booleanValue = result;
      }
    } catch (error) {
      console.warn('ControlTaskPage: function evaluation failed', error);
    }
  }

  let displayValue: string | undefined;
  if (numericValue !== undefined) {
    displayValue = String(numericValue);
  } else if (booleanValue !== undefined) {
    displayValue = booleanValue ? 'true' : 'false';
  } else {
    displayValue = displayExpression.replace(/\s+/g, ' ').trim();
  }

  if (!displayValue) {
    return undefined;
  }

  return {
    displayValue,
    numericValue: numericValue ?? tryParseNumericValue(displayValue),
    booleanValue,
  };
};

const evaluateFunctionQuestion = (
  question: any,
  context: FunctionEvaluationContext,
): FunctionEvaluationResult | undefined => {
  const template = ensureStringValue(question?.functionText).trim();
  if (!template) {
    return undefined;
  }
  return evaluateFunctionTemplate(template, context);
};

const createMultipleAnswerFromOption = (option: any): MultipleAnswerValue | undefined => {
  if (!option || option.id === null || option.id === undefined) {
    return undefined;
  }
  return {
    id: String(option.id),
    value: option?.title ?? null,
    abbreviation: option?.abbreviation ?? null,
    description: null,
    isTrue: toOptionalBoolean(option?.isTrue) ?? null,
    type: MULTIPLE_TYPE,
  };
};

const extractDocumentUrl = (documentPayload: any): string | null =>
  documentPayload?.preSignUrl ??
  documentPayload?.preSignedUrl ??
  documentPayload?.url ??
  documentPayload?.documentUrl ??
  null;

const isQuestionMultiSelectEnabled = (question: any): boolean =>
  question?.questionType === MULTIPLE_TYPE && question?.allowMultipleAnswers === true;

const normalizeMultipleAnswer = (value: any): MultipleAnswerValue | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }
  const raw = value as Record<string, any>;
  const id = raw.id === null || raw.id === undefined || raw.id === '' ? null : String(raw.id);
  const result: MultipleAnswerValue = {
    id,
    type: MULTIPLE_TYPE,
  };
  if ('value' in raw) {
    const text = raw.value === null || raw.value === undefined ? null : String(raw.value);
    result.value = text;
  }
  if ('abbreviation' in raw) {
    const text = raw.abbreviation === null || raw.abbreviation === undefined ? null : String(raw.abbreviation);
    result.abbreviation = text;
  }
  if ('description' in raw) {
    const text = raw.description === null || raw.description === undefined ? null : String(raw.description);
    result.description = text;
  }
  if ('isTrue' in raw) {
    result.isTrue = toOptionalBoolean(raw.isTrue) ?? null;
  }
  if (Array.isArray(raw.images)) {
    result.images = raw.images
      .map((image: any, index: number) => {
        const title = ensureStringValue(image?.title).trim();
        const key = ensureStringValue(image?.key).trim() || String(index + 1);
        if (!title && !hasMultipleAnswerImageValue(image)) {
          return null;
        }
        return {
          key,
          title: title || `Görsel ${index + 1}`,
          required: image?.required === true,
          documentId: image?.documentId ?? null,
          dataUrl: image?.dataUrl ?? null,
          imageUrl: image?.imageUrl ?? image?.url ?? image?.preSignUrl ?? image?.preSignedUrl ?? null,
          imagePreviewUrl: image?.imagePreviewUrl ?? null,
          imageName: image?.imageName ?? image?.name ?? null,
          imageType: image?.imageType ?? image?.mimeType ?? image?.contentType ?? null,
        } as MultipleAnswerImageValue;
      })
      .filter(Boolean) as MultipleAnswerImageValue[];
  }
  if ('documentId' in raw || 'document_id' in raw || 'docId' in raw || 'docID' in raw) {
    const documentId = raw.documentId ?? raw.document_id ?? raw.docId ?? raw.docID;
    result.documentId = documentId === null || documentId === undefined || documentId === '' ? null : documentId;
  }
  if ('dataUrl' in raw) {
    const dataUrl = raw.dataUrl;
    result.dataUrl = dataUrl === null || dataUrl === undefined ? null : String(dataUrl);
  }
  if ('imageUrl' in raw || 'url' in raw || 'preSignUrl' in raw || 'preSignedUrl' in raw) {
    const imageUrl = raw.imageUrl ?? raw.url ?? raw.preSignUrl ?? raw.preSignedUrl;
    result.imageUrl = imageUrl === null || imageUrl === undefined ? null : String(imageUrl);
  }
  if ('imagePreviewUrl' in raw) {
    const imagePreviewUrl = raw.imagePreviewUrl;
    result.imagePreviewUrl = imagePreviewUrl === null || imagePreviewUrl === undefined ? null : String(imagePreviewUrl);
  }
  if ('imageName' in raw || 'name' in raw) {
    const imageName = raw.imageName ?? raw.name;
    result.imageName = imageName === null || imageName === undefined ? null : String(imageName);
  }
  if ('imageType' in raw || 'mimeType' in raw || 'contentType' in raw) {
    const imageType = raw.imageType ?? raw.mimeType ?? raw.contentType;
    result.imageType = imageType === null || imageType === undefined ? null : String(imageType);
  }
  if (typeof raw.type === 'string') {
    result.type = raw.type as any;
  }
  return result;
};

const normalizeMultipleAnswers = (value: any): MultipleAnswerValue[] => {
  if (Array.isArray(value)) {
    const unique = new Map<string, MultipleAnswerValue>();
    value.forEach((item) => {
      const normalized = normalizeMultipleAnswer(item);
      if (!normalized || !normalized.id) {
        return;
      }
      unique.set(normalized.id, normalized);
    });
    return Array.from(unique.values());
  }
  const single = normalizeMultipleAnswer(value);
  if (!single || !single.id) {
    return [];
  }
  return [single];
};

const copyMultipleAnswerImageFields = (target: MultipleAnswerValue, source?: MultipleAnswerValue | null) => {
  if (!source) {
    return target;
  }
  if (source.documentId !== undefined) {
    target.documentId = source.documentId;
  }
  if (source.dataUrl !== undefined) {
    target.dataUrl = source.dataUrl;
  }
  if (source.imageUrl !== undefined) {
    target.imageUrl = source.imageUrl;
  }
  if (source.imagePreviewUrl !== undefined) {
    target.imagePreviewUrl = source.imagePreviewUrl;
  }
  if (source.imageName !== undefined) {
    target.imageName = source.imageName;
  }
  if (source.imageType !== undefined) {
    target.imageType = source.imageType;
  }
  if (source.images !== undefined) {
    target.images = source.images;
  }
  return target;
};

const buildMultipleAnswerPayload = (
  rawValue: any,
  answerOptions?: any[],
  allowMultipleAnswers = false,
): MultipleAnswerPayload | undefined => {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return undefined;
  }
  if (allowMultipleAnswers) {
    const toPayloadItem = (candidate: any): MultipleAnswerValue | undefined => {
      if (candidate === null || candidate === undefined || candidate === '') {
        return undefined;
      }
      if (typeof candidate === 'object') {
        const normalized = normalizeMultipleAnswer(candidate);
        if (!normalized || !normalized.id) {
          return undefined;
        }
        const option = answerOptions?.find((opt: any) => String(opt?.id) === normalized.id);
        return copyMultipleAnswerImageFields({
          id: normalized.id,
          value: option?.title ?? normalized.value ?? null,
          abbreviation: option?.abbreviation ?? normalized.abbreviation ?? null,
          description: normalized.description ?? null,
          isTrue: toOptionalBoolean(option?.isTrue) ?? normalized.isTrue ?? null,
          type: MULTIPLE_TYPE,
        }, normalized);
      }
      const id = String(candidate);
      const option = answerOptions?.find((opt: any) => String(opt?.id) === id);
      return (
        createMultipleAnswerFromOption(option) ?? {
          id,
          value: null,
          abbreviation: null,
          description: null,
          isTrue: null,
          type: MULTIPLE_TYPE,
        }
      );
    };

    const rawItems = Array.isArray(rawValue) ? rawValue : [rawValue];
    const deduped = new Map<string, MultipleAnswerValue>();
    rawItems.forEach((item) => {
      const payloadItem = toPayloadItem(item);
      if (!payloadItem || !payloadItem.id) {
        return;
      }
      deduped.set(payloadItem.id, payloadItem);
    });
    if (!deduped.size) {
      return undefined;
    }
    return Array.from(deduped.values());
  }

  if (typeof rawValue === 'object') {
    const normalized = normalizeMultipleAnswer(rawValue);
    if (!normalized) {
      return undefined;
    }
    if (normalized.id) {
      const option = answerOptions?.find((opt: any) => String(opt?.id) === normalized.id);
      return copyMultipleAnswerImageFields({
        id: normalized.id,
        value: option?.title ?? normalized.value ?? null,
        abbreviation: option?.abbreviation ?? normalized.abbreviation ?? null,
        description: normalized.description ?? null,
        isTrue: toOptionalBoolean(option?.isTrue) ?? normalized.isTrue ?? null,
        type: MULTIPLE_TYPE,
      }, normalized);
    }
    return copyMultipleAnswerImageFields({
      id: null,
      value: normalized.value ?? null,
      abbreviation: normalized.abbreviation ?? null,
      description: normalized.description ?? null,
      isTrue: normalized.isTrue ?? null,
      type: MULTIPLE_TYPE,
    }, normalized);
  }
  const id = String(rawValue);
  const option = answerOptions?.find((opt: any) => String(opt?.id) === id);
  return (
    createMultipleAnswerFromOption(option) ?? {
      id,
      value: null,
      abbreviation: null,
      description: null,
      isTrue: null,
      type: MULTIPLE_TYPE,
    }
  );
};

const getDefaultMultipleAnswerPayload = (
  question: any,
  answerOptions: any[] | undefined,
): MultipleAnswerPayload | undefined => {
  if (!Array.isArray(answerOptions)) {
    return undefined;
  }
  const option = answerOptions.find((answer: any) => answer?.isDefault || answer?.default);
  const payload = createMultipleAnswerFromOption(option);
  if (!payload) {
    return undefined;
  }
  return isQuestionMultiSelectEnabled(question) ? [payload] : payload;
};

const getMultipleSelectionIds = (value: any): string[] =>
  normalizeMultipleAnswers(value)
    .map((item) => item.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);

const buildMultipleAnswerSubmitPayload = (
  currentValue: any,
  question: any,
  answerOptions: any[] | undefined,
  selectedControlAreaId: ControlAreaValue | undefined,
): MultipleAnswerPayload | undefined => {
  const canUseDefault =
    isQuestionIncludedInInspectorForm(question) &&
    isQuestionMatchedWithSelectedControlArea(question, selectedControlAreaId);
  const rawValue =
    currentValue ??
    (canUseDefault ? getDefaultMultipleAnswerPayload(question, answerOptions) : undefined);

  return buildMultipleAnswerPayload(
    rawValue,
    answerOptions,
    isQuestionMultiSelectEnabled(question),
  );
};

const formatAnswerPreview = (value: any): string => {
  if (Array.isArray(value)) {
    return value.map(formatAnswerPreview).filter(Boolean).join(', ');
  }
  if (value && typeof value === 'object') {
    const direct = getNetworkTypeValue(value) ?? value.title ?? value.value ?? value.label ?? value.abbreviation ?? value.description;
    if (direct !== null && direct !== undefined && String(direct).trim()) {
      return String(direct).trim();
    }
    return JSON.stringify(value);
  }
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
};

const buildPendingMultipleAnswerImageKey = (questionKey: string, answerId: string, imageKey = 'legacy'): string =>
  `${questionKey}:${answerId}:${imageKey}`;

const stripNonconformityReasonLink = <T extends Record<string, any>>(value: T): T => {
  const next = { ...value };
  delete next.nonconformityReasonId;
  delete next.nonconformity_reason_id;
  return next;
};

export const applyNonconformityReasonToMultipleAnswer = (
  currentValue: MultipleAnswerPayload,
  answerOptions: any[],
  reasonText: string,
): MultipleAnswerPayload => {
  const normalizedReasonText = ensureStringValue(reasonText).trim();
  const isFalseAnswer = (item: MultipleAnswerValue) => {
    const selectedOption = answerOptions.find(
      (answer: any) => String(answer?.id) === String(item?.id),
    );
    return (
      toOptionalBoolean(selectedOption?.isTrue) ?? toOptionalBoolean(item?.isTrue)
    ) === false;
  };
  const applyReason = (item: MultipleAnswerValue): MultipleAnswerValue => {
    const next = stripNonconformityReasonLink(item);
    return isFalseAnswer(item) && normalizedReasonText
      ? { ...next, description: normalizedReasonText }
      : next;
  };

  return Array.isArray(currentValue)
    ? currentValue.map(applyReason)
    : applyReason(currentValue);
};

const stripTransientMultipleAnswerFields = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(stripTransientMultipleAnswerFields);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  const next = stripNonconformityReasonLink(value);
  if (Array.isArray(next.images)) {
    next.images = next.images.map((image: any) => {
      if (!image || typeof image !== 'object') {
        return image;
      }
      const nextImage = { ...image };
      delete nextImage.imagePreviewUrl;
      delete nextImage.dataUrl;
      return nextImage;
    });
  }
  delete next.imagePreviewUrl;
  delete next.dataUrl;
  return next;
};

const getColSpan = (value: any): number => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 24;
  if (n <= 4) return Math.max(1, Math.min(24, Math.round(24 / n)));
  if (n > 24) return 24;
  return Math.round(n);
};

const toPositiveNumber = (value: unknown): number | undefined => {
  if (value == null) {
    return undefined;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return undefined;
  }
  return numeric;
};

type InspectionNoteAttachmentPreviewKind = 'IMAGE' | 'PDF' | 'FILE';

type InspectionNoteAttachment = {
  key: string;
  documentId?: number;
  file?: File;
  name: string;
  type?: string;
  size?: number;
  url?: string;
  previewUrl?: string;
};

export const normalizeInspectionNoteAttachmentDocumentIds = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const seen = new Set<number>();
  value.forEach((item) => {
    const documentId = toPositiveNumber(item);
    if (documentId !== undefined) {
      seen.add(documentId);
    }
  });
  return Array.from(seen);
};

export const resolveInspectionNoteAttachmentPreviewKind = (
  type?: string | null,
  name?: string | null,
): InspectionNoteAttachmentPreviewKind => {
  const normalizedType = ensureStringValue(type).trim().toLowerCase();
  const normalizedName = ensureStringValue(name).trim().toLowerCase();
  if (normalizedType.startsWith('image/') || /\.(avif|bmp|gif|heic|jpeg|jpg|png|svg|webp)$/.test(normalizedName)) {
    return 'IMAGE';
  }
  if (normalizedType === 'application/pdf' || normalizedName.endsWith('.pdf')) {
    return 'PDF';
  }
  return 'FILE';
};

export const hasClipboardFiles = (clipboardData: Pick<DataTransfer, 'files'> | null | undefined): boolean =>
  Boolean(clipboardData?.files && clipboardData.files.length > 0);

const buildInspectionNoteAttachmentKey = (prefix: string, documentId?: number): string =>
  documentId !== undefined ? `${prefix}-${documentId}` : `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const resolveInspectionNoteAttachmentName = (documentPayload: any, fallbackDocumentId: number): string => {
  const candidate = [
    documentPayload?.originalFileName,
    documentPayload?.originalFilename,
    documentPayload?.fileName,
    documentPayload?.filename,
    documentPayload?.name,
    documentPayload?.code,
  ].find((value) => typeof value === 'string' && value.trim());
  return typeof candidate === 'string' ? candidate.trim() : `Ek #${fallbackDocumentId}`;
};

const resolveInspectionNoteAttachmentType = (documentPayload: any): string => {
  const candidate = [documentPayload?.contentType, documentPayload?.mimeType, documentPayload?.type]
    .find((value) => typeof value === 'string' && value.trim());
  return typeof candidate === 'string' ? candidate.trim() : '';
};

const revokeInspectionNoteAttachmentPreviewUrl = (attachment: InspectionNoteAttachment) => {
  if (attachment.previewUrl?.startsWith('blob:')) {
    URL.revokeObjectURL(attachment.previewUrl);
  }
};

const resolveResponseReportFormatId = (response: any): number | null =>
  toPositiveNumber(response?.reportFormatId) ?? null;

type CorrectionCaseType = 'CORRECTION' | 'FOLLOW_UP';

const normalizeCorrectionCaseType = (value: unknown): CorrectionCaseType | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim().toUpperCase();
  return normalized === 'FOLLOW_UP' || normalized === 'CORRECTION'
    ? normalized
    : undefined;
};

const correctionCaseTypeLabelMap: Record<CorrectionCaseType, { label: string; color: string }> = {
  CORRECTION: { label: 'DÜZELTME KONTROLÜ', color: 'purple' },
  FOLLOW_UP: { label: 'TAKİP KONTROLÜ', color: 'geekblue' },
};

const resolveCorrectionCaseTypeLabel = (caseType?: CorrectionCaseType) =>
  correctionCaseTypeLabelMap[caseType ?? 'CORRECTION'];

const normalizeControlAreaId = (value: any): ControlAreaValue | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    if (trimmed.toUpperCase() === OTHER_CONTROL_AREA_VALUE) {
      return OTHER_CONTROL_AREA_VALUE;
    }
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }
  if (typeof value === 'object' && 'id' in value) {
    return normalizeControlAreaId((value as any).id);
  }
  return undefined;
};

export const resolveBulkActiveControlAreaSelection = (
  responses: Array<{ orderNo?: unknown; selectedControlAreaId?: unknown; customControlAreaName?: unknown }>,
  activeOrderNo: number,
): { selectedControlAreaId: ControlAreaValue | undefined; customControlAreaName: string } => {
  const activeResponse = responses.find((item) => toPositiveNumber(item?.orderNo) === activeOrderNo);
  const selectedControlAreaId = normalizeControlAreaId(activeResponse?.selectedControlAreaId);

  return {
    selectedControlAreaId,
    customControlAreaName:
      selectedControlAreaId === OTHER_CONTROL_AREA_VALUE
        ? ensureStringValue(activeResponse?.customControlAreaName)
        : '',
  };
};

const isNumericControlAreaId = (value: ControlAreaValue | undefined): value is number =>
  typeof value === 'number';

const normalizeNotesMap = (input: any): NoteMap => {
  if (!input || typeof input !== 'object') {
    return {};
  }
  const normalized: NoteMap = {};
  Object.entries(input).forEach(([key, value]) => {
    const numericKey = toPositiveNumber(key);
    if (numericKey !== undefined) {
      normalized[numericKey] = typeof value === 'string' ? value : value == null ? '' : String(value);
    }
  });
  return normalized;
};

const normalizeElapsedSeconds = (value: any): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }
  return Math.floor(numeric);
};

const formatElapsedSeconds = (totalSeconds: number): string => {
  const safe = normalizeElapsedSeconds(totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const isAnswerDescriptionRequired = (answer: any): boolean =>
  answer?.isDescriptionRequired === true || answer?.descriptionRequired === true;

export const requiresMultipleAnswerDescription = (
  answer: any,
  storedAnswer: MultipleAnswerValue | undefined,
  isControlCriteriaQuestion: boolean,
): boolean =>
  isAnswerDescriptionRequired(answer) ||
  (isControlCriteriaQuestion &&
    (toOptionalBoolean(answer?.isTrue) ?? toOptionalBoolean(storedAnswer?.isTrue)) === false);

const buildQuestionRenderKey = (question: any, nodeId: number | string | undefined, index: number): string => {
  if (question?.id !== null && question?.id !== undefined) {
    return String(question.id);
  }
  return `${nodeId ?? 'node'}-${index}`;
};

const buildQuestionAnchorId = (questionKey: string): string => `${QUESTION_ANCHOR_PREFIX}${questionKey}`;

const isQuestionIncludedInInspectorForm = (question: any): boolean =>
  question?.excludeFromInspectorForm !== true;

const getQuestionControlAreaIds = (question: any): number[] =>
  Array.isArray(question?.controlAreas)
    ? question.controlAreas
        .map((area: any) => normalizeControlAreaId(area))
        .filter((id: ControlAreaValue | undefined): id is number => typeof id === 'number')
    : [];

const compareControlAreaOptions = (first: ControlAreaOption, second: ControlAreaOption): number => {
  if (first.isOther === true && second.isOther !== true) {
    return 1;
  }
  if (first.isOther !== true && second.isOther === true) {
    return -1;
  }
  const nameCompare = first.name.localeCompare(second.name, 'tr', { numeric: true, sensitivity: 'base' });
  if (nameCompare !== 0) {
    return nameCompare;
  }
  return String(first.id).localeCompare(String(second.id), 'tr', { numeric: true, sensitivity: 'base' });
};

const isQuestionMatchedWithSelectedControlArea = (
  question: any,
  selectedControlAreaId: ControlAreaValue | undefined,
): boolean => {
  if (!isNumericControlAreaId(selectedControlAreaId)) {
    return true;
  }
  const questionControlAreaIds = getQuestionControlAreaIds(question);
  if (!questionControlAreaIds.length) {
    return true;
  }
  return questionControlAreaIds.includes(selectedControlAreaId);
};

export const shouldHideControlTaskQuestionForSelectedControlArea = (
  question: any,
  selectedControlAreaId: ControlAreaValue | undefined,
): boolean =>
  isNumericControlAreaId(selectedControlAreaId) &&
  !isQuestionMatchedWithSelectedControlArea(question, selectedControlAreaId);

const sortByOrder = (a: any, b: any) => {
  const first = Number.isFinite(Number(a?.sortOrder)) ? Number(a.sortOrder) : 0;
  const second = Number.isFinite(Number(b?.sortOrder)) ? Number(b.sortOrder) : 0;
  return first - second;
};

const extractNodeOrderPrefix = (title: any): string | undefined => {
  const rawTitle = ensureStringValue(title).trim();
  const match = rawTitle.match(/^(\d+(?:\.\d+)*)\s*[-.)]/);
  return match?.[1];
};

const buildQuestionDisplayLabel = (node: any, questionDisplayIndex: number): string => {
  const nodeOrderPrefix = extractNodeOrderPrefix(node?.title);
  return nodeOrderPrefix
    ? `${nodeOrderPrefix}.${questionDisplayIndex}.`
    : `${questionDisplayIndex}.`;
};

const isVisibleInReportForm = (node: any): boolean => node?.showInReportForm !== false;

const filterNodesWithAncestors = (items: any[], predicate: (node: any) => boolean): any[] => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const byId = new Map<number, any>();
  items.forEach((item) => {
    if (typeof item?.id === 'number') {
      byId.set(item.id, item);
    }
  });

  const includeIds = new Set<number>();
  const includeWithAncestors = (item: any) => {
    let current = item;
    while (current && typeof current?.id === 'number' && !includeIds.has(current.id)) {
      includeIds.add(current.id);
      const parentId = current?.parentId;
      current = typeof parentId === 'number' ? byId.get(parentId) : undefined;
    }
  };

  items.forEach((item) => {
    if (predicate(item)) {
      includeWithAncestors(item);
    }
  });

  return items.filter((item) => typeof item?.id === 'number' && includeIds.has(item.id));
};

type QuestionBundle = { question: any; answers: any[]; tableQuestions?: QuestionBundle[] };

export type ControlTaskAutoFillValues = {
  CONTROL_START_DATE: string;
  CONTROL_END_DATE: string;
  REPORT_DATE: string;
  STARTED_AT: string;
  FINISHED_AT: string;
};

const CONTROL_TASK_AUTO_FILL_QUESTION_TYPES = new Set<keyof ControlTaskAutoFillValues>([
  'CONTROL_START_DATE',
  'CONTROL_END_DATE',
  'REPORT_DATE',
  'STARTED_AT',
  'FINISHED_AT',
]);

export const canViewControlTaskAutoFill = (): boolean =>
  hasRole('ADMIN') || hasRole('SA');

const resolveControlTaskAutoFillDurationMinutes = (
  fieldControlTime: unknown,
  reportCount = 1,
): number | null => {
  if (
    fieldControlTime === null ||
    fieldControlTime === undefined ||
    (typeof fieldControlTime !== 'number' && typeof fieldControlTime !== 'string') ||
    (typeof fieldControlTime === 'string' && !fieldControlTime.trim())
  ) {
    return null;
  }
  const numericDuration = Number(
    typeof fieldControlTime === 'string'
      ? fieldControlTime.trim().replace(',', '.')
      : fieldControlTime,
  );
  if (
    !Number.isFinite(numericDuration) ||
    numericDuration < 0 ||
    !Number.isFinite(reportCount) ||
    reportCount <= 0
  ) {
    return null;
  }

  return Math.round(numericDuration * reportCount);
};

export const resolveControlTaskAutoFillValues = (
  fieldControlTime: unknown,
  referenceTime: ReturnType<typeof dayjs>,
  reportCount = 1,
): ControlTaskAutoFillValues | null => {
  const durationMinutes = resolveControlTaskAutoFillDurationMinutes(fieldControlTime, reportCount);
  if (durationMinutes === null || !referenceTime?.isValid()) {
    return null;
  }

  const normalizedReferenceTime = referenceTime.startOf('minute');
  const today = normalizedReferenceTime.format('YYYY-MM-DD');
  return {
    CONTROL_START_DATE: today,
    CONTROL_END_DATE: today,
    REPORT_DATE: today,
    STARTED_AT: normalizedReferenceTime.subtract(durationMinutes, 'minute').format('HH:mm'),
    FINISHED_AT: normalizedReferenceTime.format('HH:mm'),
  };
};

export const isControlTaskQuestionRequired = (
  question: any,
  isOtherControlAreaSelected = false,
): boolean =>
  isQuestionIncludedInInspectorForm(question) &&
  (question?.isRequired === true ||
    (isOtherControlAreaSelected && question?.questionType === 'WORK_EQUIPMENT'));

export const shouldShowControlTaskQuestionForRequiredFilter = (
  question: any,
  showOnlyRequiredQuestions: boolean,
  isOtherControlAreaSelected = false,
): boolean =>
  !showOnlyRequiredQuestions ||
  isControlTaskQuestionRequired(question, isOtherControlAreaSelected);

export const applyControlTaskAutoFillAnswers = (
  sourceAnswers: Record<string, any>,
  questionBundlesById: Record<string, QuestionBundle>,
  values: ControlTaskAutoFillValues,
  selectedControlAreaId: ControlAreaValue | undefined,
  includeQuestion?: (questionId: string, bundle: QuestionBundle) => boolean,
): Record<string, any> => {
  const next = { ...sourceAnswers };
  Object.entries(questionBundlesById).forEach(([questionId, bundle]) => {
    const question = bundle?.question;
    const questionType = question?.questionType as keyof ControlTaskAutoFillValues | undefined;
    if (
      !questionType ||
      !CONTROL_TASK_AUTO_FILL_QUESTION_TYPES.has(questionType) ||
      !isQuestionIncludedInInspectorForm(question) ||
      !isQuestionMatchedWithSelectedControlArea(question, selectedControlAreaId) ||
      (includeQuestion && !includeQuestion(questionId, bundle))
    ) {
      return;
    }
    next[questionId] = values[questionType];
  });
  return next;
};

export const applyControlTaskAutoFillToBulkAnswers = ({
  sharedAnswers,
  answersByOrder,
  orderKeys,
  questionBundlesById,
  values,
  selectedControlAreaId,
  isSharedQuestion,
}: {
  sharedAnswers: Record<string, any>;
  answersByOrder: AnswersByOrderMap;
  orderKeys: string[];
  questionBundlesById: Record<string, QuestionBundle>;
  values: ControlTaskAutoFillValues;
  selectedControlAreaId: ControlAreaValue | undefined;
  isSharedQuestion: (questionId: string, bundle: QuestionBundle) => boolean;
}): { sharedAnswers: Record<string, any>; answersByOrder: AnswersByOrderMap } => {
  const nextSharedAnswers = applyControlTaskAutoFillAnswers(
    sharedAnswers,
    questionBundlesById,
    values,
    selectedControlAreaId,
    isSharedQuestion,
  );
  const nextAnswersByOrder: AnswersByOrderMap = { ...answersByOrder };
  orderKeys.forEach((orderKey) => {
    nextAnswersByOrder[orderKey] = applyControlTaskAutoFillAnswers(
      answersByOrder[orderKey] ?? {},
      questionBundlesById,
      values,
      selectedControlAreaId,
      (questionId, bundle) => !isSharedQuestion(questionId, bundle),
    );
  });
  return {
    sharedAnswers: nextSharedAnswers,
    answersByOrder: nextAnswersByOrder,
  };
};

export const hasControlTaskAutoFillQuestions = (
  questionBundlesById: Record<string, QuestionBundle>,
  selectedControlAreaId: ControlAreaValue | undefined,
): boolean =>
  Object.values(questionBundlesById).some((bundle) => {
    const question = bundle?.question;
    return (
      CONTROL_TASK_AUTO_FILL_QUESTION_TYPES.has(question?.questionType) &&
      isQuestionIncludedInInspectorForm(question) &&
      isQuestionMatchedWithSelectedControlArea(question, selectedControlAreaId)
    );
  });

type RequiredQuestionForValidation = {
  key: string;
  question: any;
  order: number;
  defaultValue?: any;
};

type VisibleQuestionMeta = {
  displayOrder: number;
  displayLabel: string;
  sortIndex: number;
  questionTitle: string;
};

export const collectIncompleteQuestionIssues = ({
  answersForSubmit,
  questionBundlesById,
  requiredQuestions,
  selectedControlAreaId,
  visibleQuestionMetaByKey,
  bulkOrderNo,
  bulkReportTableTitle,
  includedQuestionIds,
  nonconformityDescriptionQuestionIds,
}: {
  answersForSubmit: Record<string, any>;
  questionBundlesById: Record<string, QuestionBundle>;
  requiredQuestions: RequiredQuestionForValidation[];
  selectedControlAreaId: ControlAreaValue | undefined;
  visibleQuestionMetaByKey: Map<string, VisibleQuestionMeta>;
  bulkOrderNo?: number;
  bulkReportTableTitle?: unknown;
  includedQuestionIds?: Iterable<string>;
  nonconformityDescriptionQuestionIds?: Iterable<string>;
}): IncompleteQuestionIssue[] => {
  const issueByKey = new Map<string, IncompleteQuestionIssue>();
  const includedQuestionSet = includedQuestionIds
    ? new Set(Array.from(includedQuestionIds).map((questionId) => String(questionId)))
    : null;
  const shouldIncludeQuestion = (questionId: string) =>
    !includedQuestionSet || includedQuestionSet.has(String(questionId));
  const nonconformityDescriptionQuestionSet = new Set(
    Array.from(nonconformityDescriptionQuestionIds ?? []).map((questionId) => String(questionId)),
  );
  const normalizedBulkReportTableTitle = ensureStringValue(bulkReportTableTitle).trim();

  const upsertIssue = (
    questionKey: string,
    questionTitle: string,
    fallbackOrder: number,
    reason: IncompleteQuestionIssue['reason'],
    reasonLabel: string,
  ) => {
    const visibleMeta = visibleQuestionMetaByKey.get(questionKey);
    const displayOrder = visibleMeta?.displayOrder ?? fallbackOrder;
    const baseDisplayLabel = visibleMeta?.displayLabel ?? `${fallbackOrder}.`;
    const baseSortIndex = visibleMeta?.sortIndex ?? fallbackOrder;
    const bulkReportLabel = normalizedBulkReportTableTitle
      ? `${bulkOrderNo}.${normalizedBulkReportTableTitle}`
      : `${bulkOrderNo}. Rapor`;
    const displayLabel = bulkOrderNo ? `${bulkReportLabel} - ${baseDisplayLabel}` : baseDisplayLabel;
    const sortIndex = bulkOrderNo
      ? bulkOrderNo * 100000 + baseSortIndex
      : baseSortIndex;
    const title = visibleMeta?.questionTitle || ensureStringValue(questionTitle).trim() || 'Soru';
    const issueKey = bulkOrderNo
      ? `${bulkOrderNo}:${questionKey}:${reason}`
      : `${questionKey}:${reason}`;
    issueByKey.set(issueKey, {
      key: issueKey,
      questionKey,
      displayOrder,
      displayLabel,
      sortIndex,
      questionTitle: title,
      reason,
      reasonLabel,
      bulkOrderNo,
    });
  };

  const isRequiredQuestionMissing = (
    item: RequiredQuestionForValidation,
    questionKey: string,
  ) => {
    let isMissing = false;
    if (item.question?.questionType === 'MULTIPLE') {
      const storedIds = getMultipleSelectionIds(answersForSubmit[questionKey]);
      const fallbackIds = getMultipleSelectionIds(item.defaultValue);
      isMissing = storedIds.length === 0 && fallbackIds.length === 0;
    } else if (item.question?.questionType === 'NETWORK_TYPE') {
      isMissing = getNetworkTypeValue(answersForSubmit[questionKey]) === undefined;
    } else if (item.question?.questionType === 'TABLE') {
      const value = answersForSubmit[questionKey];
      isMissing = !Array.isArray(value?.rows) || value.rows.length === 0;
    } else {
      const value = answersForSubmit[questionKey];
      if (value === undefined || value === null) {
        isMissing = true;
      } else if (typeof value === 'string') {
        isMissing = value.trim().length === 0;
      }
    }
    return isMissing;
  };

  requiredQuestions.forEach((item) => {
    const questionKey = item.key || buildQuestionRenderKey(item.question, undefined, item.order - 1);
    if (!questionKey || !shouldIncludeQuestion(questionKey)) {
      return;
    }

    if (isRequiredQuestionMissing(item, questionKey)) {
      upsertIssue(
        questionKey,
        item.question?.title,
        item.order,
        'REQUIRED',
        '',
      );
    }
  });

  Object.entries(questionBundlesById).forEach(([questionId, bundle]) => {
    if (!shouldIncludeQuestion(questionId)) {
      return;
    }
    if (!isQuestionIncludedInInspectorForm(bundle?.question)) {
      return;
    }
    if (!isQuestionMatchedWithSelectedControlArea(bundle?.question, selectedControlAreaId)) {
      return;
    }
    if (bundle?.question?.questionType !== 'MULTIPLE') {
      return;
    }
    const rawValue = answersForSubmit[questionId];
    const storedItems = isQuestionMultiSelectEnabled(bundle?.question)
      ? normalizeMultipleAnswers(rawValue)
      : (() => {
          const single = normalizeMultipleAnswer(rawValue);
          return single?.id ? [single] : [];
        })();
    const defaultValue = getDefaultMultipleAnswerPayload(bundle?.question, bundle?.answers);
    const defaultItems = Array.isArray(defaultValue)
      ? defaultValue
      : defaultValue?.id
        ? [defaultValue]
        : [];
    const selectedItems = storedItems.length ? storedItems : defaultItems;
    selectedItems.forEach((item) => {
      if (!item?.id) {
        return;
      }
      const selectedOption = Array.isArray(bundle?.answers)
        ? bundle.answers.find((answer: any) => String(answer?.id) === String(item.id))
        : undefined;
      const requiredImageFields = normalizeAnswerImageFields(selectedOption).filter((image) => image.required === true);
      if (!requiredImageFields.length) {
        return;
      }
      const storedImagesByKey = new Map((item.images ?? []).map((image) => [String(image.key), image] as const));
      const missingImage = requiredImageFields.find((field) => !hasMultipleAnswerImageValue(storedImagesByKey.get(String(field.key))));
      if (!missingImage) {
        return;
      }
      const fallbackOrder = visibleQuestionMetaByKey.get(questionId)?.displayOrder ?? Number.MAX_SAFE_INTEGER;
      upsertIssue(
        questionId,
        bundle?.question?.title,
        Number.isFinite(fallbackOrder) ? fallbackOrder : Number.MAX_SAFE_INTEGER,
        'MISSING_REQUIRED_IMAGE',
        `Zorunlu görsel eklenmemiş: ${missingImage.title}`,
      );
    });
    const isControlCriteriaQuestion = nonconformityDescriptionQuestionSet.has(String(questionId));
    if (isQuestionMultiSelectEnabled(bundle?.question)) {
      selectedItems.forEach((item) => {
        const selectedOption = Array.isArray(bundle?.answers)
          ? bundle.answers.find((answer: any) => String(answer?.id) === String(item?.id))
          : undefined;
        const isNonconforming =
          (toOptionalBoolean(selectedOption?.isTrue) ?? toOptionalBoolean(item?.isTrue)) === false;
        if (!isControlCriteriaQuestion || !isNonconforming) {
          return;
        }
        if (ensureStringValue(item?.description).trim().length > 0) {
          return;
        }
        const fallbackOrder = visibleQuestionMetaByKey.get(questionId)?.displayOrder ?? Number.MAX_SAFE_INTEGER;
        upsertIssue(
          questionId,
          bundle?.question?.title,
          Number.isFinite(fallbackOrder) ? fallbackOrder : Number.MAX_SAFE_INTEGER,
          'MISSING_FALSE_DESCRIPTION',
          'Olumsuz cevap için açıklama girilmemiş',
        );
      });
      return;
    }
    const stored = normalizeMultipleAnswer(answersForSubmit[questionId]);
    const defaultSingle = Array.isArray(defaultValue) ? defaultValue[0] : defaultValue;
    const selectedId = stored?.id ?? defaultSingle?.id ?? null;
    if (!selectedId) {
      return;
    }

    const selectedOption = Array.isArray(bundle?.answers)
      ? bundle.answers.find((answer: any) => String(answer?.id) === String(selectedId))
      : undefined;
    const requiresDescription = requiresMultipleAnswerDescription(
      selectedOption,
      stored,
      isControlCriteriaQuestion,
    );
    if (!requiresDescription) {
      return;
    }

    const description = ensureStringValue(stored?.description).trim();
    if (description.length > 0) {
      return;
    }

    const fallbackOrder = visibleQuestionMetaByKey.get(questionId)?.displayOrder ?? Number.MAX_SAFE_INTEGER;
    upsertIssue(
      questionId,
      bundle?.question?.title,
      Number.isFinite(fallbackOrder) ? fallbackOrder : Number.MAX_SAFE_INTEGER,
      'MISSING_FALSE_DESCRIPTION',
      (toOptionalBoolean(selectedOption?.isTrue) ?? toOptionalBoolean(stored?.isTrue)) === false
        ? 'Olumsuz cevap için açıklama girilmemiş'
        : 'Cevap açıklaması girilmemiş',
    );
  });

  return Array.from(issueByKey.values()).sort((a, b) => {
    if (a.sortIndex === b.sortIndex) {
      return a.questionTitle.localeCompare(b.questionTitle, 'tr');
    }
    return a.sortIndex - b.sortIndex;
  });
};

export const buildIncompleteIssueErrorMessage = (issue: IncompleteQuestionIssue): string => {
  const issueLabel = `${issue.displayLabel} ${issue.questionTitle}`.trim();
  if (issue.reason === 'MISSING_FALSE_DESCRIPTION') {
    return `Seçilen cevap için açıklama zorunlu: ${issueLabel}`;
  }
  if (issue.reason === 'MISSING_REQUIRED_IMAGE') {
    return `${issue.reasonLabel}: ${issueLabel}`;
  }
  return `Lütfen tüm zorunlu soruları yanıtlayınız. Eksik: ${issueLabel}`;
};

type CustomControlAreaNameInputProps = {
  value: string;
  onCommit: (value: string) => void;
};

const CustomControlAreaNameInput: React.FC<CustomControlAreaNameInputProps> = React.memo(({
  value,
  onCommit,
}) => {
  const [draftValue, setDraftValue] = useState(value);

  useEffect(() => {
    setDraftValue(value);
  }, [value]);

  const handleBlur = useCallback(() => {
    onCommit(draftValue);
  }, [draftValue, onCommit]);

  return (
    <Input
      value={draftValue}
      maxLength={255}
      showCount
      placeholder='MAKİNA ADI - EKİPMAN ÖZELLİĞİ gibi'
      onChange={(event) => setDraftValue(event.target.value)}
      onBlur={handleBlur}
    />
  );
});

const ControlTaskPage: React.FC = () => {
  const { proposalId, taskId, attributeId, order } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state || {}) as {
    attributeName?: string;
    task?: any;
    qty?: any;
    correctionCaseId?: number;
    correctionCaseType?: string;
    bulkGroupId?: number;
    bulkReportCount?: number;
  };
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const correctionCaseIdNumber = toPositiveNumber(
    searchParams.get('correctionCaseId') ?? locationState.correctionCaseId,
  );
  const isCorrectionMode = correctionCaseIdNumber !== undefined;

  const proposalIdNumber = toPositiveNumber(proposalId);
  const taskIdNumber = toPositiveNumber(taskId);
  const attributeIdNumber = toPositiveNumber(attributeId);
  const orderNumber = toPositiveNumber(order);
  const initialBulkGroupId = toPositiveNumber(searchParams.get('bulkGroupId') ?? locationState.bulkGroupId);
  const initialBulkReportCount = toPositiveNumber(searchParams.get('bulkReportCount') ?? locationState.bulkReportCount) ?? orderNumber ?? 1;
  const isBulkReportRoute =
    initialBulkGroupId !== undefined ||
    searchParams.has('bulkReportCount') ||
    locationState.bulkReportCount !== undefined;

  const panelDraftStorageKey = useMemo(() => {
    const userId = localStorage.getItem('id');
    return userId && !isCorrectionMode && !isBulkReportRoute
      ? `kontrolx:panel-draft:${userId}:${proposalIdNumber}:${taskIdNumber}:${attributeIdNumber}:${orderNumber}`
      : undefined;
  }, [isCorrectionMode, isBulkReportRoute, proposalIdNumber, taskIdNumber, attributeIdNumber, orderNumber]);
  const loadedPanelDraftKeyRef = useRef<string>();
  const panelDraftStorageWarningRef = useRef(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reportFormat, setReportFormat] = useState<any | null>(null);
  const [nodes, setNodes] = useState<any[]>([]);
  const [itemsByNode, setItemsByNode] = useState<Record<number, QuestionBundle[]>>({});
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [controlGroups, setControlGroups] = useState<ControlTaskControlGroup[]>([]);
  const [controlGroupAnswers, setControlGroupAnswers] = useState<ControlGroupAnswers>({});
  const [panelLoadError, setPanelLoadError] = useState(false);
  const [correctionPanelQuestions, setCorrectionPanelQuestions] = useState<Record<string, number[]>>({});
  const [sourceControlGroupAnswers, setSourceControlGroupAnswers] = useState<ControlGroupAnswers>({});
  const applyControlGroups = useCallback((groups: ControlTaskControlGroup[] | undefined) => {
    const next = Array.isArray(groups) ? groups : [];
    setControlGroups(next);
    setControlGroupAnswers(Object.fromEntries(next.map((group) => [String(group.id), group.answers ?? {}])));
    setPanelLoadError(false);
  }, []);
  const [earthingMeasurementsByNode, setEarthingMeasurementsByNode] =
    useState<EarthingMeasurementsByNode>({});
  const earthingTableRefs = useRef<Record<string, EarthingMeasurementsTableHandle | null>>({});
  const [rcdMeasurementsByNode, setRcdMeasurementsByNode] = useState<RcdMeasurementsByNode>({});
  const rcdTableRefs = useRef<Record<string, RcdMeasurementsTableHandle | null>>({});
  const [rcdMeasurementsLoadError, setRcdMeasurementsLoadError] = useState(false);
  const [measurementResponseKind, setMeasurementResponseKind] = useState<string>();
  const savingRequestRef = useRef(false);
  const [openTimePickerKey, setOpenTimePickerKey] = useState<string | null>(null);
  const [yearDraftValues, setYearDraftValues] = useState<Record<string, string>>({});
  const [pendingMultipleAnswerImages, setPendingMultipleAnswerImages] = useState<PendingMultipleAnswerImageMap>({});
  const [attributeStaticValues, setAttributeStaticValues] = useState<StaticValueFormEntry[]>([]);
  const [toolStaticValues, setToolStaticValues] = useState<StaticValueFormEntry[]>([]);
  const [savedResponseMeta, setSavedResponseMeta] = useState<{ id: number; updatedAt?: string } | null>(null);
  const [responseReportFormatId, setResponseReportFormatId] = useState<number | null | undefined>(undefined);
  const [correctionCaseType, setCorrectionCaseType] = useState<CorrectionCaseType | undefined>(() =>
    normalizeCorrectionCaseType(locationState.correctionCaseType ?? locationState.task?.caseType),
  );
  const [saving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [controlAreas, setControlAreas] = useState<ControlAreaOption[]>([]);
  const [controlAreasLoading, setControlAreasLoading] = useState<boolean>(false);
  const [controlAreasError, setControlAreasError] = useState<string | null>(null);
  const [selectedControlAreaId, setSelectedControlAreaId] = useState<ControlAreaValue | undefined>(undefined);
  const [customControlAreaName, setCustomControlAreaName] = useState<string>('');
  const [noteContents, setNoteContents] = useState<NoteMap>({});
  const [inspectionNote, setInspectionNote] = useState<string>('');
  const [inspectionNoteAttachments, setInspectionNoteAttachments] = useState<InspectionNoteAttachment[]>([]);
  const inspectionNoteAttachmentsRef = useRef<InspectionNoteAttachment[]>([]);
  const [inspectionNoteAttachmentPreview, setInspectionNoteAttachmentPreview] = useState<InspectionNoteAttachment | null>(null);
  const [persistedQuestionMetadataById, setPersistedQuestionMetadataById] = useState<Record<string, any>>({});
  const [proposal, setProposal] = useState<any | null>(null);
  const [inspectionTask, setInspectionTask] = useState<any | null>(null);
  const [attributeDetail, setAttributeDetail] = useState<any | null>(null);
  const [bulkGroupId, setBulkGroupId] = useState<number | undefined>(initialBulkGroupId);
  const [bulkReportCount, setBulkReportCount] = useState<number>(initialBulkReportCount);
  const [bulkCapacity, setBulkCapacity] = useState<ControlTaskBulkCapacity | null>(null);
  const [bulkCapacityLoading, setBulkCapacityLoading] = useState<boolean>(false);
  const [bulkCapacityError, setBulkCapacityError] = useState<string | null>(null);
  const [bulkActiveOrder, setBulkActiveOrder] = useState<number>(orderNumber ?? 1);
  const [bulkSharedAnswers, setBulkSharedAnswers] = useState<Record<string, any>>({});
  const [bulkAnswersByOrder, setBulkAnswersByOrder] = useState<AnswersByOrderMap>({});
  const [selectedBulkOrderKeys, setSelectedBulkOrderKeys] = useState<string[]>([]);
  const [bulkAssignmentDrawerOpen, setBulkAssignmentDrawerOpen] = useState<boolean>(false);
  const [bulkAssignmentOrderKeys, setBulkAssignmentOrderKeys] = useState<string[]>([]);
  const [bulkAssignmentDraftAnswers, setBulkAssignmentDraftAnswers] = useState<Record<string, any>>({});
  const [bulkOverrideSourcesByOrder, setBulkOverrideSourcesByOrder] = useState<Record<string, string>>({});
  const [bulkPendingGroupEdit, setBulkPendingGroupEdit] = useState<BulkPendingGroupEdit | null>(null);
  const [elapsedSecondsDisplay, setElapsedSecondsDisplay] = useState<number>(0);
  const [correctionQuestionIds, setCorrectionQuestionIds] = useState<Set<number> | null>(null);
  const [sourceAnswersByQuestionId, setSourceAnswersByQuestionId] = useState<Record<string, any>>({});
  const [questionSearchText, setQuestionSearchText] = useState<string>('');
  const [questionSearchOpen, setQuestionSearchOpen] = useState<boolean>(false);
  const [showOnlyRequiredQuestions, setShowOnlyRequiredQuestions] = useState<boolean>(false);
  const canViewAutoFillButton = useMemo(() => canViewControlTaskAutoFill(), []);
  const futureInspectionTaskDate = useMemo(
    () => resolveFutureInspectionTaskDate(inspectionTask),
    [inspectionTask],
  );
  const bulkAssignmentDrawerBodyRef = useRef<HTMLDivElement | null>(null);

  const questionBundlesById = useMemo(() => {
    const map: Record<string, QuestionBundle> = {};
    Object.values(itemsByNode).forEach((bundles) => {
      bundles.forEach((bundle) => {
        const questionId = bundle?.question?.id;
        if (questionId !== null && questionId !== undefined) {
          map[String(questionId)] = bundle;
        }
      });
    });
    return map;
  }, [itemsByNode]);

  const panelNodeSections = useMemo(() => panelSectionsByNode(nodes), [nodes]);
  const panelSectionByQuestion = useMemo(() => {
    const result = new Map<string, number>();
    Object.entries(itemsByNode).forEach(([nodeId, bundles]) => {
      const section = panelNodeSections.get(Number(nodeId));
      if (section == null) return;
      bundles.forEach((bundle) => {
        const key = getQuestionKey(bundle.question);
        if (key) result.set(key, section);
      });
    });
    return result;
  }, [itemsByNode, panelNodeSections]);
  const classicQuestionBundles = useMemo(() => Object.fromEntries(
    Object.entries(questionBundlesById).filter(([key]) => !panelSectionByQuestion.has(key)),
  ), [questionBundlesById, panelSectionByQuestion]);

  const activeNetworkType = useMemo(
    () => resolveReportNetworkType(answers, questionBundlesById),
    [answers, questionBundlesById],
  );

  const isBulkReportEntryEnabled = useMemo(() => {
    const value =
      attributeDetail?.bulkReportEntry ??
      attributeDetail?.bulk_report_entry ??
      inspectionTask?.attribute?.bulkReportEntry ??
      inspectionTask?.attribute?.bulk_report_entry;
    return !isCorrectionMode && (value === true || value === 'true' || value === 1 || value === '1');
  }, [attributeDetail, inspectionTask, isCorrectionMode]);

  const isSpecialMeasurementEntryEnabled = !isBulkReportEntryEnabled && !isCorrectionMode
    && (!measurementResponseKind || measurementResponseKind === 'INITIAL');

  const bulkReportUnitLabel = useMemo(() => {
    const unit = attributeDetail?.unit ?? inspectionTask?.attribute?.unit;
    const normalizedUnit = ensureStringValue(unit).trim();
    return normalizedUnit ? resolveParameterUnitLabel(normalizedUnit) : undefined;
  }, [attributeDetail?.unit, inspectionTask?.attribute?.unit]);
  const bulkReportCountLabel = useMemo(
    () => resolveBulkReportCountLabel(reportFormat?.bulkReportTableTitle),
    [reportFormat?.bulkReportTableTitle],
  );

  const bulkReportCountLimit = useMemo(() => {
    const value = Number(bulkCapacity?.maximumReportCount);
    return Number.isFinite(value) && value >= 0 ? Math.floor(value) : undefined;
  }, [bulkCapacity]);
  const hasBulkCapacity = bulkReportCountLimit !== undefined && !bulkCapacityLoading && !bulkCapacityError;

  const isSharedBulkQuestion = useCallback((question: any): boolean => {
    const mode = ensureStringValue(question?.bulkInputMode).trim().toUpperCase();
    return mode === SHARED_BULK_INPUT_MODE;
  }, []);

  useEffect(() => {
    if (!isBulkReportRoute || responseReportFormatId !== undefined) {
      return;
    }
    setResponseReportFormatId(null);
  }, [isBulkReportRoute, responseReportFormatId]);

  const buildDefaultTableCells = useCallback((tableQuestions?: QuestionBundle[]) => {
    const cells: Record<string, any> = {};
    (Array.isArray(tableQuestions) ? tableQuestions : []).forEach((bundle) => {
      const column = bundle?.question;
      const columnType = String(column?.questionType ?? column?.columnType ?? '').toUpperCase();
      if (columnType !== 'MULTIPLE') {
        return;
      }
      const options = Array.isArray(bundle?.answers) ? bundle.answers : Array.isArray((column as any)?.options) ? (column as any).options : [];
      const option = options.find((answer: any) => answer?.isDefault || answer?.default);
      const payload = createMultipleAnswerFromOption(option);
      if (payload && !Array.isArray(payload) && column?.id !== undefined && column?.id !== null) {
        cells[String(column.id)] = payload;
      }
    });
    return cells;
  }, []);

  const normalizeFixedTableAnswerValue = useCallback((bundle: QuestionBundle, value: any) => {
    const fixedRowCount = resolveFixedTableRowCount(bundle?.question);
    if (!fixedRowCount) {
      return value;
    }
    return {
      ...(value && typeof value === 'object' && !Array.isArray(value) ? value : {}),
      rows: normalizeTableRowsToFixedCount(
        value?.rows,
        fixedRowCount,
        () => buildDefaultTableCells(bundle?.tableQuestions),
      ),
    };
  }, [buildDefaultTableCells]);

  useEffect(() => {
    if (!Object.keys(questionBundlesById).length) {
      return;
    }
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(questionBundlesById).forEach(([questionId, bundle]) => {
        if (bundle?.question?.questionType !== 'TABLE') {
          return;
        }
        const fixedRowCount = resolveFixedTableRowCount(bundle.question);
        if (!fixedRowCount) {
          return;
        }
        const currentValue = next[questionId];
        const rows = Array.isArray(currentValue?.rows) ? currentValue.rows : [];
        const needsNormalization =
          rows.length !== fixedRowCount ||
          rows.some((row: any, index: number) => row?.rowOrder !== index + 1 || !row?.cells);
        if (!needsNormalization) {
          return;
        }
        next[questionId] = normalizeFixedTableAnswerValue(bundle, currentValue);
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [answers, normalizeFixedTableAnswerValue, questionBundlesById]);

  const selectedControlArea = useMemo(() => {
    if (selectedControlAreaId === undefined) {
      return undefined;
    }
    return controlAreas.find((area) => area.id === selectedControlAreaId);
  }, [selectedControlAreaId, controlAreas]);

  const isOtherControlAreaSelected =
    selectedControlAreaId === OTHER_CONTROL_AREA_VALUE || selectedControlArea?.isOther === true;
  const selectedControlAreaIdForSubmit = isOtherControlAreaSelected
    ? OTHER_CONTROL_AREA_VALUE
    : selectedControlAreaId;

  useEffect(() => {
    if (selectedControlAreaId !== OTHER_CONTROL_AREA_VALUE) {
      return;
    }
    const otherControlArea = controlAreas.find(
      (area) => area.isOther === true && isNumericControlAreaId(area.id),
    );
    if (otherControlArea) {
      setSelectedControlAreaId(otherControlArea.id);
    }
  }, [controlAreas, selectedControlAreaId]);

  const shouldRequireControlAreaSelection = useMemo(
    () => !controlAreasLoading && !controlAreasError && controlAreas.length > 0,
    [controlAreas, controlAreasError, controlAreasLoading],
  );

  const shouldBlockReportQuestions = shouldRequireControlAreaSelection && selectedControlAreaId === undefined;

  const highlightedControlAreaQuestionIds = useMemo(() => {
    if (!isNumericControlAreaId(selectedControlAreaId)) {
      return new Set<number>();
    }
    const matches = new Set<number>();
    Object.values(itemsByNode).forEach((bundles) => {
      (bundles || []).forEach((bundle) => {
        const questionId = toPositiveNumber(bundle?.question?.id);
        if (!questionId) {
          return;
        }
        const questionAreas = Array.isArray(bundle?.question?.controlAreas)
          ? bundle.question.controlAreas
          : [];
        const hasMatch = questionAreas.some(
          (area: any) => normalizeControlAreaId(area) === selectedControlAreaId,
        );
        if (hasMatch) {
          matches.add(questionId);
        }
      });
    });
    return matches;
  }, [itemsByNode, selectedControlAreaId]);

  const proposalCompanyName = useMemo(() => {
    const rawName = ensureStringValue(proposal?.company?.name).trim();
    return rawName.length ? rawName : undefined;
  }, [proposal]);

  const proposalCompanyPhone = useMemo(() => {
    const rawPhone = ensureStringValue(proposal?.company?.phoneNumber).trim();
    return rawPhone.length ? rawPhone : undefined;
  }, [proposal]);

  const proposalCompanyEmail = useMemo(() => {
    const rawEmail = ensureStringValue(proposal?.company?.email).trim();
    return rawEmail.length ? rawEmail : undefined;
  }, [proposal]);

  const proposalAddressDisplay = useMemo(() => {
    const address = proposal?.address ?? null;
    if (!address) {
      return undefined;
    }
    const parts = [
      ensureStringValue(address.addressLine1).trim(),
      ensureStringValue(address.addressLine2).trim(),
      ensureStringValue(address.state).trim(),
      ensureStringValue(address.city).trim(),
    ].filter((part) => part.length);
    return parts.length ? parts.join(' ') : undefined;
  }, [proposal]);

  const proposalSgkNumber = useMemo(() => {
    const rawNumber = ensureStringValue(proposal?.address?.sgkNumber).trim();
    return rawNumber.length ? rawNumber : undefined;
  }, [proposal]);

  const inspectionTaskAccount = useMemo(() => {
    return (
      inspectionTask?.account ??
      inspectionTask?.inspector?.account ??
      inspectionTask?.appointment?.account ??
      inspectionTask?.appointment?.inspector?.account ??
      inspectionTask?.inspector ??
      null
    );
  }, [inspectionTask]);

  const inspectionTaskFirstName = useMemo(() => {
    const rawFirstName = ensureStringValue(inspectionTaskAccount?.firstName).trim();
    if (rawFirstName) {
      return rawFirstName;
    }
    const fullName = ensureStringValue(inspectionTaskAccount?.fullName ?? inspectionTaskAccount?.name).trim();
    if (!fullName) {
      return undefined;
    }
    const parts = fullName.split(/\s+/);
    return parts.length ? parts[0] : undefined;
  }, [inspectionTaskAccount]);

  const inspectionTaskLastName = useMemo(() => {
    const rawLastName = ensureStringValue(inspectionTaskAccount?.lastName).trim();
    if (rawLastName) {
      return rawLastName;
    }
    const fullName = ensureStringValue(inspectionTaskAccount?.fullName ?? inspectionTaskAccount?.name).trim();
    if (!fullName) {
      return undefined;
    }
    const parts = fullName.split(/\s+/);
    return parts.length > 1 ? parts.slice(1).join(' ') : undefined;
  }, [inspectionTaskAccount]);

  const inspectionTaskProfessions = useMemo(() => {
    const fromTask =
      inspectionTask?.inspector?.professions ??
      inspectionTask?.inspectorProfessions ??
      inspectionTask?.professions;
    if (Array.isArray(fromTask)) {
      return fromTask;
    }
    const fromAccount = inspectionTaskAccount?.professions;
    return Array.isArray(fromAccount) ? fromAccount : [];
  }, [inspectionTask, inspectionTaskAccount]);

  const inspectionTaskProfession = useMemo(() => {
    const names = inspectionTaskProfessions
      .map((item: any) => {
        if (item && typeof item === 'object') {
          return ensureStringValue(item?.profession?.name ?? item?.professionName ?? item?.name).trim();
        }
        return ensureStringValue(item).trim();
      })
      .filter((value) => value.length);
    return names.length ? names.join(', ') : undefined;
  }, [inspectionTaskProfessions]);

  const inspectionTaskRegistrationNumber = useMemo(() => {
    const values = inspectionTaskProfessions
      .map((item: any) => {
        if (item && typeof item === 'object') {
          return ensureStringValue(item?.registrationNumber ?? item?.registrationNo ?? item?.registration).trim();
        }
        return ensureStringValue(item).trim();
      })
      .filter((value) => value.length);
    return values.length ? values.join(', ') : undefined;
  }, [inspectionTaskProfessions]);

  const inspectionTaskDiplomaNumber = useMemo(() => {
    const values = inspectionTaskProfessions
      .map((item: any) => {
        if (item && typeof item === 'object') {
          return ensureStringValue(item?.diplomaNumber ?? item?.diplomaNo).trim();
        }
        return ensureStringValue(item).trim();
      })
      .filter((value) => value.length);
    return values.length ? values.join(', ') : undefined;
  }, [inspectionTaskProfessions]);

  const inspectionTaskContractId = useMemo(() => {
    const raw = ensureStringValue(
      inspectionTask?.appointment?.contractId ??
        inspectionTask?.contractId ??
        inspectionTask?.agreementNo ??
        inspectionTask?.appointment?.agreementNo,
    ).trim();
    return raw.length ? raw : undefined;
  }, [inspectionTask]);

  const { answersWithFunctions, functionAnswerMatches } = useMemo(() => {
    if (!Object.keys(questionBundlesById).length) {
      return { answersWithFunctions: answers, functionAnswerMatches: {} as Record<string, { answerId?: string; title?: string }> };
    }
    const base = { ...answers };
    const context = createFunctionEvaluationContext(
      base,
      questionBundlesById,
      attributeStaticValues,
      toolStaticValues,
    );
    const matches: Record<string, { answerId?: string; title?: string }> = {};
    let changed = false;

    const functionEntries = Object.entries(questionBundlesById)
      .filter(([, bundle]) => bundle?.question?.questionType === 'FUNCTION')
      .sort((a, b) => Number(a[0]) - Number(b[0]));

    functionEntries.forEach(([questionId, bundle]) => {
      const result = evaluateFunctionQuestion(bundle.question, context);
      const tag = normalizeFunctionTag(bundle.question?.functionTag);
      if (!result || !result.displayValue) {
        if (base[questionId] !== undefined) {
          delete base[questionId];
          changed = true;
        }
        if (tag) {
          delete context.questionValues[tag];
        }
        return;
      }
      if (base[questionId] !== result.displayValue) {
        base[questionId] = result.displayValue;
        changed = true;
      }
      if (tag) {
        context.questionValues[tag] = {
          text: result.displayValue,
          numeric: result.numericValue ?? tryParseNumericValue(result.displayValue),
        };
      }
      if (Array.isArray(bundle.answers) && bundle.answers.length) {
        let matched = false;
        bundle.answers.some((answer: any) => {
          const template = ensureStringValue(answer?.functionText).trim();
          if (!template) {
            return false;
          }
          const evaluation = evaluateFunctionTemplate(template, context);
          const booleanResult = deriveBooleanFromFunctionResult(evaluation);
          if (booleanResult === true) {
            const title = ensureStringValue(answer?.title).trim();
            matches[questionId] = {
              answerId: answer?.id !== null && answer?.id !== undefined ? String(answer.id) : undefined,
              title: title || undefined,
            };
            matched = true;
            return true;
          }
          return false;
        });
        if (!matched) {
          const fallback = bundle.answers.find((answer: any) => answer?.isTrue);
          if (fallback) {
            const title = ensureStringValue(fallback?.title).trim();
            matches[questionId] = {
              answerId: fallback?.id !== null && fallback?.id !== undefined ? String(fallback.id) : undefined,
              title: title || undefined,
            };
          }
        }
      }
    });

    return {
      answersWithFunctions: changed ? base : answers,
      functionAnswerMatches: matches,
    };
  }, [
    answers,
    questionBundlesById,
    attributeStaticValues,
    toolStaticValues,
  ]);

  useEffect(() => {
    if (isBulkReportEntryEnabled) {
      return;
    }
    if (!proposalIdNumber || !taskIdNumber || !attributeIdNumber || !orderNumber) {
      return;
    }
    const intervalId = window.setInterval(() => {
      setElapsedSecondsDisplay((prev) => prev + 1);
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [proposalIdNumber, taskIdNumber, attributeIdNumber, orderNumber, isBulkReportEntryEnabled]);

  const handleDraftFieldBlur = useCallback(() => undefined, []);

  useEffect(() => {
    if (!Object.keys(questionBundlesById).length) {
      return;
    }
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(prev).forEach(([key, value]) => {
        const bundle = questionBundlesById[key];
        if (!bundle) {
          return;
        }
        if (bundle.question?.questionType === 'MULTIPLE' && value !== undefined) {
          const normalized = buildMultipleAnswerPayload(
            value,
            bundle.answers,
            isQuestionMultiSelectEnabled(bundle.question),
          );
          if (!normalized) {
            delete next[key];
            changed = true;
            return;
          }
          const existingJson = JSON.stringify(value);
          const normalizedJson = JSON.stringify(normalized);
          if (existingJson !== normalizedJson) {
            next[key] = Array.isArray(normalized) ? normalized.map((item) => ({ ...item })) : { ...normalized };
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [questionBundlesById]);

  useEffect(() => {
    if (!Object.keys(questionBundlesById).length) {
      return;
    }
    const todayValue = dayjs().format('YYYY-MM-DD');
    setAnswers((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(questionBundlesById).forEach(([questionId, bundle]) => {
        if (
          bundle?.question?.questionType === 'DATE' &&
          isCurrentDayEnabled(bundle?.question?.isCurrentDay) &&
          next[questionId] === undefined
        ) {
          next[questionId] = todayValue;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [questionBundlesById]);

  useEffect(() => {
    if (!correctionCaseIdNumber) {
      setCorrectionQuestionIds(null);
      setSourceAnswersByQuestionId({});
      setCorrectionCaseType(undefined);
      replaceInspectionNoteAttachments([]);
      return;
    }
    let ignore = false;
    const loadCorrectionForm = async () => {
      try {
        const response = await fetchControlTaskCorrectionForm(correctionCaseIdNumber);
        if (ignore) {
          return;
        }
        const ids = Array.isArray(response?.questionIds)
          ? response.questionIds
              .map((id) => toPositiveNumber(id))
              .filter((id): id is number => id !== undefined)
          : [];
        setCorrectionQuestionIds(new Set(ids));
        setSourceAnswersByQuestionId(response?.sourceAnswers || {});
        const correctionCase = response?.correctionCase;
        setCorrectionCaseType(normalizeCorrectionCaseType(correctionCase?.caseType) ?? 'CORRECTION');
        const sourceProposalId = toPositiveNumber(correctionCase?.proposalId);
        const sourceTaskId = toPositiveNumber(correctionCase?.sourceTaskId);
        const sourceAttributeId = toPositiveNumber(correctionCase?.sourceAttributeId);
        const sourceOrderNo = toPositiveNumber(correctionCase?.sourceOrderNo);
        let sourceResponse: any = null;
        if (sourceProposalId && sourceTaskId && sourceAttributeId && sourceOrderNo) {
          sourceResponse = await fetchControlTaskAnswers(
            sourceProposalId,
            sourceTaskId,
            sourceAttributeId,
            sourceOrderNo,
          );
          if (ignore) {
            return;
          }
        }
        const sourceAnswers = sourceResponse?.answers;
        if (sourceAnswers && typeof sourceAnswers === 'object' && !Array.isArray(sourceAnswers)) {
          setAnswers((prev) => ({ ...prev, ...sourceAnswers }));
        }
        if (sourceResponse && sourceResponse.selectedControlAreaId !== undefined) {
          if (sourceResponse.selectedControlAreaId === null) {
            setSelectedControlAreaId(undefined);
          } else {
            const sourceControlAreaId = normalizeControlAreaId(sourceResponse.selectedControlAreaId);
            if (sourceControlAreaId !== undefined) {
              setSelectedControlAreaId(sourceControlAreaId);
            }
          }
        }
        if (sourceResponse && sourceResponse.customControlAreaName !== undefined) {
          setCustomControlAreaName(ensureStringValue(sourceResponse.customControlAreaName));
        }
        if (sourceResponse) {
          setAttributeStaticValues(normalizeStaticValueList(sourceResponse.attributeStaticValues));
          setToolStaticValues(normalizeStaticValueList(sourceResponse.toolStaticValues));
          setNoteContents(normalizeNotesMap(sourceResponse.notes));
          setInspectionNote(ensureStringValue(sourceResponse.inspectionNote));
        }
        applyControlGroups(response?.correctionResponse?.controlGroups);
        setSourceControlGroupAnswers(response?.sourceControlGroupAnswers ?? {});
        const panelQuestions: Record<string, number[]> = {};
        (response?.correctionCase?.questions ?? []).forEach((question) => {
          if (question.sourceControlGroupId != null && question.questionId != null) {
            (panelQuestions[String(question.sourceControlGroupId)] ??= []).push(question.questionId);
          }
        });
        setCorrectionPanelQuestions(panelQuestions);
        const persisted = response?.correctionResponse?.answers;
        if (persisted && typeof persisted === 'object' && !Array.isArray(persisted)) {
          setAnswers((prev) => ({ ...prev, ...persisted }));
        }
        if (response?.correctionResponse) {
          const correctionResponse = response.correctionResponse;
          if (correctionResponse.selectedControlAreaId !== undefined) {
            if (correctionResponse.selectedControlAreaId === null) {
              setSelectedControlAreaId(undefined);
            } else {
              const correctionControlAreaId = normalizeControlAreaId(correctionResponse.selectedControlAreaId);
              if (correctionControlAreaId !== undefined) {
                setSelectedControlAreaId(correctionControlAreaId);
              }
            }
          }
          if (correctionResponse.customControlAreaName !== undefined) {
            setCustomControlAreaName(ensureStringValue(correctionResponse.customControlAreaName));
          }
          setAttributeStaticValues(normalizeStaticValueList(correctionResponse.attributeStaticValues));
          setToolStaticValues(normalizeStaticValueList(correctionResponse.toolStaticValues));
          setNoteContents(normalizeNotesMap(correctionResponse.notes));
          setInspectionNote(ensureStringValue(correctionResponse.inspectionNote));
        }
        const attachmentSource = response?.correctionResponse ?? sourceResponse;
        const hydratedAttachments = await hydrateInspectionNoteAttachments(
          attachmentSource?.inspectionNoteAttachmentDocumentIds,
        );
        if (ignore) {
          return;
        }
        replaceInspectionNoteAttachments(hydratedAttachments);
        setResponseReportFormatId(resolveResponseReportFormatId(response?.correctionResponse));
        if (response?.correctionResponse?.id != null) {
          setSavedResponseMeta({
            id: response.correctionResponse.id,
            updatedAt: response.correctionResponse.updatedAt,
          });
        }
      } catch (err) {
        if (!ignore) {
          console.error('ControlTaskPage: failed to load correction form', err);
          message.error('Düzeltme formu yüklenemedi.');
          setCorrectionQuestionIds(new Set());
          setSourceAnswersByQuestionId({});
          setPanelLoadError(true);
          setResponseReportFormatId(null);
        }
      }
    };
    loadCorrectionForm();
    return () => {
      ignore = true;
    };
  }, [correctionCaseIdNumber, applyControlGroups]);

  useEffect(() => {
    if (isCorrectionMode && correctionQuestionIds === null) {
      setLoading(true);
      return;
    }
    if (!attributeIdNumber) {
      setError('Geçersiz parametre bilgisi. Parametre kimliği bulunamadı.');
      setReportFormat(null);
      setNodes([]);
      setItemsByNode({});
      setLoading(false);
      return;
    }
    if (responseReportFormatId === undefined) {
      setLoading(true);
      return;
    }

    let ignore = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = responseReportFormatId
          ? await fetchFieldInspectionControlTaskByReportFormat(responseReportFormatId)
          : await fetchFieldInspectionControlTask(attributeIdNumber);
        const latest = response?.reportFormat;
        if (ignore) {
          return;
        }
        if (!latest?.id) {
          setReportFormat(null);
          setNodes([]);
          setItemsByNode({});
          setNoteContents({});
          setError('Bu parametre için aktif rapor formatı bulunamadı.');
          return;
        }
        setReportFormat(latest);

        const rawNodes = Array.isArray(response?.nodes) ? response.nodes : [];
        const normalized = rawNodes.map((node: any) => ({
          ...node,
          parentId:
            node?.parentId ??
            (typeof node?.parent === 'object' ? node.parent?.id : node?.parent),
        }));
        const directVisibleNodeIds = new Set(
          normalized
            .filter(isVisibleInReportForm)
            .map((node: any) => node?.id)
            .filter((id: unknown): id is number => typeof id === 'number'),
        );
        const visibleNodes = filterNodesWithAncestors(normalized, isVisibleInReportForm);
        visibleNodes.sort(sortByOrder);

        const bundles: Record<number, QuestionBundle[]> = {};
        const rawItemsByNode = response?.itemsByNode ?? {};
        Object.entries(rawItemsByNode).forEach(([nodeIdRaw, bundleList]) => {
          const nodeId = toPositiveNumber(nodeIdRaw);
          if (!nodeId) {
            return;
          }
          if (!directVisibleNodeIds.has(nodeId)) {
            return;
          }
          const normalizedBundles: QuestionBundle[] = Array.isArray(bundleList)
            ? bundleList
                .map((bundle: any) => ({
                  question: normalizeQuestionDescriptionFields(bundle?.question),
                  answers: Array.isArray(bundle?.answers) ? bundle.answers : [],
                  tableQuestions: Array.isArray(bundle?.tableQuestions) ? bundle.tableQuestions : [],
                }))
                .filter((bundle: QuestionBundle) => bundle?.question)
            : [];
          normalizedBundles.sort((a, b) => sortByOrder(a?.question, b?.question));
          normalizedBundles.forEach((bundle) => {
            bundle.answers = Array.isArray(bundle.answers)
              ? bundle.answers.sort(sortByOrder)
              : [];
          });
          bundles[nodeId] = normalizedBundles;
        });

        const defaultMultipleAnswers: Record<string, MultipleAnswerPayload> = {};
        Object.values(bundles).forEach((questionItems) => {
          questionItems.forEach((bundle) => {
            const q = bundle?.question;
            if (q?.questionType === 'MULTIPLE' && Array.isArray(bundle.answers)) {
              if (q?.id != null) {
                const payload = getDefaultMultipleAnswerPayload(q, bundle.answers);
                if (payload) {
                  defaultMultipleAnswers[String(q.id)] = payload;
                }
              }
            }
          });
        });

        if (ignore) {
          return;
        }
        const finalVisibleNodes = visibleNodes;
        finalVisibleNodes.sort(sortByOrder);
        setNodes(finalVisibleNodes);
        setItemsByNode(bundles);
        if (Object.keys(defaultMultipleAnswers).length) {
          setAnswers((prev) => {
            let changed = false;
            const next = { ...prev };
            Object.entries(defaultMultipleAnswers).forEach(([questionId, value]) => {
              if (next[questionId] === undefined) {
                next[questionId] = Array.isArray(value) ? value.map((item) => ({ ...item })) : { ...value };
                changed = true;
              }
            });
            return changed ? next : prev;
          });
        }
      } catch (err) {
        if (!ignore) {
          console.error('ControlTaskPage load failed', err);
          setError('Rapor soruları yüklenirken bir hata oluştu.');
          setReportFormat(null);
          setNodes([]);
          setItemsByNode({});
          setNoteContents({});
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [attributeIdNumber, correctionQuestionIds, isCorrectionMode, responseReportFormatId]);

  useEffect(() => {
    if (isBulkReportRoute || isCorrectionMode) {
      return;
    }
    if (!proposalIdNumber || !taskIdNumber || !attributeIdNumber || !orderNumber) {
      return;
    }

    let ignore = false;

    const loadPersistedAnswers = async () => {
      try {
        const response = await fetchControlTaskAnswers(
          proposalIdNumber,
          taskIdNumber,
          attributeIdNumber,
          orderNumber,
        );
        if (ignore) {
          return;
        }
        applyControlGroups(response?.controlGroups);
        const persisted = response?.answers;
        if (persisted && typeof persisted === 'object' && !Array.isArray(persisted)) {
          setAnswers((prev) => ({ ...prev, ...persisted }));
        }
        if (!isCorrectionMode) {
          setEarthingMeasurementsByNode(
            normalizeEarthingMeasurementsByNode(response?.earthingMeasurementsByNode),
          );
          setRcdMeasurementsByNode(normalizeRcdMeasurementsByNode(response?.rcdMeasurementsByNode));
          setMeasurementResponseKind(response?.responseKind);
        }
        const answersWithMetadata = Array.isArray(response?.answersWithMetadata)
          ? response.answersWithMetadata
          : [];
        const metadataRecord: Record<string, any> = {};
        answersWithMetadata.forEach((item: any) => {
          const questionId =
            item?.questionId !== undefined && item?.questionId !== null
              ? String(item.questionId)
              : undefined;
          if (questionId) {
            metadataRecord[questionId] = item;
          }
        });
        setPersistedQuestionMetadataById(metadataRecord);
        if (answersWithMetadata.length) {
          const metadataByQuestionId = new Map<string, any>();
          answersWithMetadata.forEach((item: any) => {
            const questionId =
              item?.questionId !== undefined && item?.questionId !== null
                ? String(item.questionId)
                : undefined;
            if (questionId) {
              metadataByQuestionId.set(questionId, item);
            }
          });
          if (metadataByQuestionId.size) {
            setItemsByNode((prev) => {
              let changed = false;
              const next: Record<number, QuestionBundle[]> = {};
              Object.entries(prev).forEach(([nodeIdRaw, bundles]) => {
                const nodeId = Number(nodeIdRaw);
                if (!Number.isFinite(nodeId) || !Array.isArray(bundles)) {
                  return;
                }
                const nextBundles = bundles.map((bundle) => {
                  const question = bundle?.question;
                  if (!question || question?.id === undefined || question?.id === null) {
                    return bundle;
                  }
                  const metadata = metadataByQuestionId.get(String(question.id));
                  if (!metadata) {
                    return bundle;
                  }
                  const nextQuestion = {
                    ...question,
                    questionDescription:
                      metadata?.questionDescription ?? question?.questionDescription,
                    description:
                      question?.description ??
                      metadata?.questionDescription ??
                      question?.questionDescription,
                  };
                  if (
                    nextQuestion.questionDescription === question?.questionDescription &&
                    nextQuestion.description === question?.description
                  ) {
                    return bundle;
                  }
                  changed = true;
                  return {
                    ...bundle,
                    question: nextQuestion,
                  };
                });
                next[nodeId] = nextBundles;
              });
              return changed ? next : prev;
            });
          }
        }
        if (response && response.selectedControlAreaId !== undefined) {
          if (response.selectedControlAreaId === null) {
            if (response.id != null) {
              setSelectedControlAreaId(undefined);
            }
          } else {
            const persistedControlAreaId = normalizeControlAreaId(response.selectedControlAreaId);
            if (persistedControlAreaId !== undefined) {
              setSelectedControlAreaId(persistedControlAreaId);
            }
          }
        }
        if (response && response.customControlAreaName !== undefined) {
          setCustomControlAreaName(ensureStringValue(response.customControlAreaName));
        } else if (response?.id != null) {
          setCustomControlAreaName('');
        }
        setAttributeStaticValues(normalizeStaticValueList(response?.attributeStaticValues));
        setToolStaticValues(normalizeStaticValueList(response?.toolStaticValues));
        setNoteContents(normalizeNotesMap(response?.notes));
        setInspectionNote(ensureStringValue(response?.inspectionNote));
        const hydratedAttachments = await hydrateInspectionNoteAttachments(
          response?.inspectionNoteAttachmentDocumentIds,
        );
        if (ignore) {
          return;
        }
        replaceInspectionNoteAttachments(hydratedAttachments);
        if (panelDraftStorageKey) {
          const draft = readPanelControlDraft(panelDraftStorageKey, response?.updatedAt, resolveResponseReportFormatId(response));
          if (draft) {
            setEarthingMeasurementsByNode(normalizeEarthingMeasurementsByNode(draft.measurements));
            setRcdMeasurementsByNode(normalizeRcdMeasurementsByNode(draft.rcdMeasurements));
            setControlGroupAnswers(draft.answers);
            setYearDraftValues(draft.yearValues);
            setPendingMultipleAnswerImages(restorePanelDraftImages(draft.answers));
          }
          loadedPanelDraftKeyRef.current = panelDraftStorageKey;
        }
        setSavedResponseMeta(
          response?.id != null ? { id: response.id, updatedAt: response?.updatedAt } : null,
        );
        setResponseReportFormatId(resolveResponseReportFormatId(response));
      } catch (err) {
        if (!ignore) {
          setRcdMeasurementsLoadError(true);
          setPanelLoadError(true);
          console.error('ControlTaskPage: failed to load persisted answers', err);
          setResponseReportFormatId(null);
        }
      }
    };

    loadedPanelDraftKeyRef.current = undefined;
    setSavedResponseMeta(null);
    applyControlGroups([]);
    setResponseReportFormatId(undefined);
    if (!isCorrectionMode) {
      setEarthingMeasurementsByNode({});
      setRcdMeasurementsByNode({});
      setRcdMeasurementsLoadError(false);
      setMeasurementResponseKind(undefined);
    }
    setInspectionNote('');
    replaceInspectionNoteAttachments([]);
    loadPersistedAnswers();

    return () => {
      ignore = true;
    };
  }, [
    proposalIdNumber,
    taskIdNumber,
    attributeIdNumber,
    orderNumber,
    isBulkReportRoute,
    isCorrectionMode,
    applyControlGroups,
    panelDraftStorageKey,
  ]);

  useEffect(() => {
    if (!Object.keys(persistedQuestionMetadataById).length) {
      return;
    }
    setItemsByNode((prev) => {
      let changed = false;
      const next: Record<number, QuestionBundle[]> = {};
      Object.entries(prev).forEach(([nodeIdRaw, bundles]) => {
        const nodeId = Number(nodeIdRaw);
        if (!Number.isFinite(nodeId) || !Array.isArray(bundles)) {
          return;
        }
        const nextBundles = bundles.map((bundle) => {
          const question = bundle?.question;
          if (!question || question?.id === undefined || question?.id === null) {
            return bundle;
          }
          const metadata = persistedQuestionMetadataById[String(question.id)];
          if (!metadata) {
            return bundle;
          }
          const nextQuestion = {
            ...question,
            questionDescription: metadata?.questionDescription ?? question?.questionDescription,
            description:
              question?.description ??
              metadata?.questionDescription ??
              question?.questionDescription,
          };
          if (
            nextQuestion.questionDescription === question?.questionDescription &&
            nextQuestion.description === question?.description
          ) {
            return bundle;
          }
          changed = true;
          return {
            ...bundle,
            question: nextQuestion,
          };
        });
        next[nodeId] = nextBundles;
      });
      return changed ? next : prev;
    });
  }, [persistedQuestionMetadataById]);

  useEffect(() => {
    if (!isBulkReportEntryEnabled || !proposalIdNumber || !taskIdNumber || !attributeIdNumber) {
      setBulkCapacity(null);
      setBulkCapacityError(null);
      setBulkCapacityLoading(false);
      return;
    }
    let ignore = false;
    setBulkCapacityLoading(true);
    setBulkCapacityError(null);
    fetchControlTaskBulkCapacity(proposalIdNumber, taskIdNumber, attributeIdNumber, bulkGroupId)
      .then((capacity) => {
        if (!ignore) {
          setBulkCapacity(capacity ?? null);
        }
      })
      .catch((err) => {
        if (!ignore) {
          console.error('ControlTaskPage: failed to load bulk capacity', err);
          setBulkCapacity(null);
          setBulkCapacityError('Toplu rapor kotası yüklenemedi. Lütfen sayfayı yeniden yükleyiniz.');
        }
      })
      .finally(() => {
        if (!ignore) {
          setBulkCapacityLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [isBulkReportEntryEnabled, proposalIdNumber, taskIdNumber, attributeIdNumber, bulkGroupId]);

  useEffect(() => {
    if (!isBulkReportEntryEnabled || bulkReportCountLimit === undefined) {
      return;
    }
    setBulkReportCount((prev) => {
      if (bulkReportCountLimit <= 0) {
        return 0;
      }
      return Math.min(Math.max(Math.floor(Number(prev) || 1), 1), bulkReportCountLimit);
    });
    setBulkActiveOrder((prev) => clampBulkActiveOrder(prev, Math.max(bulkReportCountLimit, 1)));
  }, [isBulkReportEntryEnabled, bulkReportCountLimit]);

  useEffect(() => {
    if (!isBulkReportEntryEnabled || !proposalIdNumber || !taskIdNumber || !attributeIdNumber) {
      return;
    }
    if (!Object.keys(questionBundlesById).length) {
      return;
    }
    if (!bulkGroupId) {
      setBulkSharedAnswers({});
      setBulkAnswersByOrder({});
      setBulkOverrideSourcesByOrder({});
      setBulkPendingGroupEdit(null);
      setAnswers({});
      setNoteContents({});
      setInspectionNote('');
      replaceInspectionNoteAttachments([]);
      const nextCount = bulkReportCountLimit === undefined
        ? initialBulkReportCount
        : Math.min(initialBulkReportCount, Math.max(bulkReportCountLimit, 0));
      setBulkReportCount(nextCount);
      setBulkActiveOrder((prev) => clampBulkActiveOrder(prev, Math.max(nextCount, 1)));
      setResponseReportFormatId(null);
      return;
    }

    let ignore = false;
    const loadBulkAnswers = async () => {
      try {
        const response = await fetchControlTaskBulkAnswers(proposalIdNumber, taskIdNumber, attributeIdNumber, bulkGroupId);
        if (ignore) {
          return;
        }
        const responses = Array.isArray(response?.responses) ? response.responses : [];
        const nextSharedAnswers: Record<string, any> = {};
        const nextAnswersByOrder: AnswersByOrderMap = {};
        let maxOrderNo = 0;
        responses.forEach((item: any) => {
          const orderNo = toPositiveNumber(item?.orderNo);
          if (!orderNo) {
            return;
          }
          maxOrderNo = Math.max(maxOrderNo, orderNo);
          const orderKey = String(orderNo);
          const orderAnswers: Record<string, any> = {};
          const itemAnswers = item?.answers && typeof item.answers === 'object' && !Array.isArray(item.answers)
            ? item.answers
            : {};
          Object.entries(itemAnswers).forEach(([questionId, value]) => {
            const bundle = questionBundlesById[questionId];
            if (bundle?.question && isSharedBulkQuestion(bundle.question)) {
              if (nextSharedAnswers[questionId] === undefined) {
                nextSharedAnswers[questionId] = value;
              }
              return;
            }
            orderAnswers[questionId] = value;
          });
          nextAnswersByOrder[orderKey] = orderAnswers;
        });
        const loadedCount = maxOrderNo || response?.count || initialBulkReportCount;
        const nextCount = bulkReportCountLimit === undefined
          ? loadedCount
          : Math.min(loadedCount, Math.max(bulkReportCountLimit, 0));
        setBulkReportCount(nextCount);
        setBulkActiveOrder((prev) => clampBulkActiveOrder(prev, Math.max(nextCount, 1)));
        setBulkSharedAnswers(nextSharedAnswers);
        setBulkAnswersByOrder(nextAnswersByOrder);
        setBulkOverrideSourcesByOrder({});
        setBulkPendingGroupEdit(null);
        const activeOrderNo = Math.min(Math.max(orderNumber || 1, 1), nextCount);
        const activeResponse = responses.find((item: any) => toPositiveNumber(item?.orderNo) === activeOrderNo);
        setNoteContents(normalizeNotesMap(activeResponse?.notes));
        setInspectionNote(ensureStringValue(activeResponse?.inspectionNote));
        const hydratedAttachments = await hydrateInspectionNoteAttachments(
          activeResponse?.inspectionNoteAttachmentDocumentIds,
        );
        if (ignore) {
          return;
        }
        replaceInspectionNoteAttachments(hydratedAttachments);
        const controlAreaSelection = resolveBulkActiveControlAreaSelection(responses, activeOrderNo);
        setSelectedControlAreaId(controlAreaSelection.selectedControlAreaId);
        setCustomControlAreaName(controlAreaSelection.customControlAreaName);
        if (activeResponse?.id != null) {
          setResponseReportFormatId(resolveResponseReportFormatId(activeResponse));
        } else {
          setResponseReportFormatId(null);
        }
        setAnswers({
          ...nextSharedAnswers,
          ...(nextAnswersByOrder[String(activeOrderNo)] ?? {}),
        });
      } catch (err) {
        if (!ignore) {
          console.error('ControlTaskPage: failed to load bulk answers', err);
          message.error('Toplu rapor girişleri yüklenemedi.');
          setResponseReportFormatId(null);
        }
      }
    };

    loadBulkAnswers();

    return () => {
      ignore = true;
    };
  }, [
    isBulkReportEntryEnabled,
    proposalIdNumber,
    taskIdNumber,
    attributeIdNumber,
    bulkGroupId,
    questionBundlesById,
    isSharedBulkQuestion,
    orderNumber,
    initialBulkReportCount,
    bulkReportCountLimit,
  ]);

  useEffect(() => {
    if (!isBulkReportEntryEnabled) {
      return;
    }
    setAnswers(resolveBulkActiveAnswers(bulkActiveOrder, bulkSharedAnswers, bulkAnswersByOrder));
  }, [isBulkReportEntryEnabled, bulkSharedAnswers, bulkAnswersByOrder, bulkActiveOrder]);

  useEffect(() => {
    if (!proposalIdNumber) {
      setProposal(null);
      return;
    }

    let ignore = false;

    const loadProposal = async () => {
      try {
        const response = await ProposalService.get(proposalIdNumber);
        if (ignore) {
          return;
        }
        const data = (response as any)?.data ?? response ?? null;
        setProposal(data);
      } catch (err) {
        if (!ignore) {
          console.error('ControlTaskPage: failed to load proposal', err);
          setProposal(null);
        }
      }
    };

    loadProposal();

    return () => {
      ignore = true;
    };
  }, [proposalIdNumber]);

  useEffect(() => {
    if (!taskIdNumber) {
      setInspectionTask(null);
      return;
    }

    let ignore = false;

    const loadInspectionTask = async () => {
      try {
        const response = await getInspectionTask(taskIdNumber);
        if (ignore) {
          return;
        }
        const data = (response as any)?.data ?? response ?? null;
        setInspectionTask(data);
      } catch (err) {
        if (!ignore) {
          console.error('ControlTaskPage: failed to load inspection task', err);
          setInspectionTask(null);
        }
      }
    };

    loadInspectionTask();

    return () => {
      ignore = true;
    };
  }, [taskIdNumber]);

  useEffect(() => {
    if (!attributeIdNumber) {
      setAttributeDetail(null);
      return;
    }

    let ignore = false;
    const loadAttributeDetail = async () => {
      try {
        const response = await apiFetch(`/attributes/${attributeIdNumber}`);
        if (ignore) {
          return;
        }
        setAttributeDetail((response as any)?.data ?? response ?? null);
      } catch (err) {
        if (!ignore) {
          console.error('ControlTaskPage: failed to load attribute detail', err);
          setAttributeDetail(null);
        }
      }
    };

    loadAttributeDetail();

    return () => {
      ignore = true;
    };
  }, [attributeIdNumber]);

  useEffect(() => {
    if (!proposalCompanyName) {
      return;
    }
    const entries = Object.entries(questionBundlesById);
    if (!entries.length) {
      return;
    }
    const titleQuestionIds = entries
      .filter(([, bundle]) => bundle?.question?.questionType === 'TITLE')
      .map(([questionId]) => questionId);
    if (!titleQuestionIds.length) {
      return;
    }
    const hasMismatch = titleQuestionIds.some((questionId) => answers[questionId] !== proposalCompanyName);
    if (!hasMismatch) {
      return;
    }
    setAnswers((prev) => {
      let updated = false;
      const next = { ...prev };
      titleQuestionIds.forEach((questionId) => {
        if (next[questionId] !== proposalCompanyName) {
          next[questionId] = proposalCompanyName;
          updated = true;
        }
      });
      return updated ? next : prev;
    });
  }, [proposalCompanyName, questionBundlesById, answers]);

  useEffect(() => {
    if (!proposalAddressDisplay) {
      return;
    }
    const entries = Object.entries(questionBundlesById);
    if (!entries.length) {
      return;
    }
    const addressQuestionIds = entries
      .filter(([, bundle]) => bundle?.question?.questionType === 'ADDRESS')
      .map(([questionId]) => questionId);
    if (!addressQuestionIds.length) {
      return;
    }
    const hasMismatch = addressQuestionIds.some(
      (questionId) => answers[questionId] !== proposalAddressDisplay,
    );
    if (!hasMismatch) {
      return;
    }
    setAnswers((prev) => {
      let updated = false;
      const next = { ...prev };
      addressQuestionIds.forEach((questionId) => {
        if (next[questionId] !== proposalAddressDisplay) {
          next[questionId] = proposalAddressDisplay;
          updated = true;
        }
      });
      return updated ? next : prev;
    });
  }, [proposalAddressDisplay, questionBundlesById, answers]);

  useEffect(() => {
    if (!proposalCompanyPhone) {
      return;
    }
    const entries = Object.entries(questionBundlesById);
    if (!entries.length) {
      return;
    }
    const phoneQuestionIds = entries
      .filter(([, bundle]) => bundle?.question?.questionType === 'PHONE')
      .map(([questionId]) => questionId);
    if (!phoneQuestionIds.length) {
      return;
    }
    const hasMismatch = phoneQuestionIds.some(
      (questionId) => answers[questionId] !== proposalCompanyPhone,
    );
    if (!hasMismatch) {
      return;
    }
    setAnswers((prev) => {
      let updated = false;
      const next = { ...prev };
      phoneQuestionIds.forEach((questionId) => {
        if (next[questionId] !== proposalCompanyPhone) {
          next[questionId] = proposalCompanyPhone;
          updated = true;
        }
      });
      return updated ? next : prev;
    });
  }, [proposalCompanyPhone, questionBundlesById, answers]);

  useEffect(() => {
    if (!proposalCompanyEmail) {
      return;
    }
    const entries = Object.entries(questionBundlesById);
    if (!entries.length) {
      return;
    }
    const emailQuestionIds = entries
      .filter(([, bundle]) => bundle?.question?.questionType === 'EMAIL')
      .map(([questionId]) => questionId);
    if (!emailQuestionIds.length) {
      return;
    }
    const hasMismatch = emailQuestionIds.some(
      (questionId) => answers[questionId] !== proposalCompanyEmail,
    );
    if (!hasMismatch) {
      return;
    }
    setAnswers((prev) => {
      let updated = false;
      const next = { ...prev };
      emailQuestionIds.forEach((questionId) => {
        if (next[questionId] !== proposalCompanyEmail) {
          next[questionId] = proposalCompanyEmail;
          updated = true;
        }
      });
      return updated ? next : prev;
    });
  }, [proposalCompanyEmail, questionBundlesById, answers]);

  useEffect(() => {
    if (!proposalSgkNumber) {
      return;
    }
    const entries = Object.entries(questionBundlesById);
    if (!entries.length) {
      return;
    }
    const sgkQuestionIds = entries
      .filter(([, bundle]) => bundle?.question?.questionType === 'SGK_REGISTRATION_NUMBER')
      .map(([questionId]) => questionId);
    if (!sgkQuestionIds.length) {
      return;
    }
    const hasMismatch = sgkQuestionIds.some(
      (questionId) => answers[questionId] !== proposalSgkNumber,
    );
    if (!hasMismatch) {
      return;
    }
    setAnswers((prev) => {
      let updated = false;
      const next = { ...prev };
      sgkQuestionIds.forEach((questionId) => {
        if (next[questionId] !== proposalSgkNumber) {
          next[questionId] = proposalSgkNumber;
          updated = true;
        }
      });
      return updated ? next : prev;
    });
  }, [proposalSgkNumber, questionBundlesById, answers]);

  useEffect(() => {
    const taskValues: Record<string, string | undefined> = {
      CONTRACT_ID: inspectionTaskContractId,
      FIRST_NAME: inspectionTaskFirstName,
      LAST_NAME: inspectionTaskLastName,
      PROFESSION: inspectionTaskProfession,
      REGISTRATION_NUMBER: inspectionTaskRegistrationNumber,
      DIPLOMA_NUMBER: inspectionTaskDiplomaNumber,
    };
    const filledEntries = Object.entries(taskValues).filter(([, value]) => value);
    if (!filledEntries.length) {
      return;
    }
    const entries = Object.entries(questionBundlesById);
    if (!entries.length) {
      return;
    }
    const questionIdsByType: Record<string, string[]> = {};
    entries.forEach(([questionId, bundle]) => {
      const type = bundle?.question?.questionType;
      if (!type || taskValues[type] === undefined) {
        return;
      }
      if (!questionIdsByType[type]) {
        questionIdsByType[type] = [];
      }
      questionIdsByType[type].push(questionId);
    });
    const typesWithQuestions = Object.keys(questionIdsByType);
    if (!typesWithQuestions.length) {
      return;
    }
    const hasMismatch = typesWithQuestions.some((type) =>
      (questionIdsByType[type] || []).some(
        (questionId) => answers[questionId] !== taskValues[type],
      ),
    );
    if (!hasMismatch) {
      return;
    }
    setAnswers((prev) => {
      let updated = false;
      const next = { ...prev };
      typesWithQuestions.forEach((type) => {
        const value = taskValues[type];
        if (value === undefined) {
          return;
        }
        (questionIdsByType[type] || []).forEach((questionId) => {
          if (next[questionId] !== value) {
            next[questionId] = value;
            updated = true;
          }
        });
      });
      return updated ? next : prev;
    });
  }, [
    inspectionTaskContractId,
    inspectionTaskFirstName,
    inspectionTaskLastName,
    inspectionTaskProfession,
    inspectionTaskRegistrationNumber,
    inspectionTaskDiplomaNumber,
    questionBundlesById,
    answers,
  ]);

  useEffect(() => {
    let ignore = false;

    const loadControlAreas = async () => {
      setControlAreasLoading(true);
      setControlAreasError(null);
      try {
        const query = attributeIdNumber ? `?attributeId=${attributeIdNumber}` : '';
        const response = await apiFetch(`/control-areas${query}`);
        if (ignore) {
          return;
        }
        const source = Array.isArray(response)
          ? response
          : (response as any)?.result ?? (response as any)?.data?.result ?? (response as any)?.data;
        const mapped: ControlAreaOption[] = Array.isArray(source)
          ? source.reduce<ControlAreaOption[]>((acc, item: any) => {
              const id = normalizeControlAreaId(item?.id);
              if (id === undefined) {
                return acc;
              }
              const rawName = ensureStringValue(item?.name).trim();
              acc.push({ id, name: rawName || `Tip - Özellik #${id}`, isOther: item?.isOther === true });
              return acc;
            }, []).sort(compareControlAreaOptions)
          : [];
        setControlAreas(mapped);
      } catch (err) {
        if (!ignore) {
          console.error('ControlTaskPage: failed to load control areas', err);
          setControlAreas([]);
          setControlAreasError('Kontrol alanları yüklenemedi.');
        }
      } finally {
        if (!ignore) {
          setControlAreasLoading(false);
        }
      }
    };

    loadControlAreas();

    return () => {
      ignore = true;
    };
  }, [attributeIdNumber]);

  const attributeLabel = useMemo(() => {
    return locationState?.attributeName ?? reportFormat?.attribute?.name ?? undefined;
  }, [locationState?.attributeName, reportFormat?.attribute?.name]);

  const qtyDisplay = locationState?.qty ?? undefined;

  const childrenMap = useMemo(() => {
    const map: Record<number, any[]> = {};
    nodes.forEach((node: any) => {
      const parentId = node.parentId;
      if (parentId) {
        if (!map[parentId]) {
          map[parentId] = [];
        }
        map[parentId].push(node);
      }
    });
    Object.keys(map).forEach((key) => {
      map[Number(key)].sort(sortByOrder);
    });
    return map;
  }, [nodes]);

  const rootNodes = useMemo(() => nodes.filter((node: any) => !node.parentId), [nodes]);

  const bulkCriteriaRootNodes = useMemo(
    () => nodes.filter((node: any) => isControlCriteriaNode(node)).sort(sortByOrder),
    [nodes],
  );

  const bulkCriteriaNodeIds = useMemo(() => {
    const ids = new Set<number>();
    const traverse = (node: any) => {
      const nodeId = toPositiveNumber(node?.id);
      if (!nodeId || ids.has(nodeId)) {
        return;
      }
      ids.add(nodeId);
      (childrenMap[nodeId] || []).forEach(traverse);
    };
    bulkCriteriaRootNodes.forEach(traverse);
    return ids;
  }, [bulkCriteriaRootNodes, childrenMap]);

  const bulkCriteriaQuestionIds = useMemo(() => {
    const ids = new Set<string>();
    bulkCriteriaNodeIds.forEach((nodeId) => {
      (itemsByNode[nodeId] || []).forEach((bundle) => {
        const question = bundle?.question;
        const questionId =
          question?.id !== null && question?.id !== undefined ? String(question.id) : undefined;
        if (!questionId || isSharedBulkQuestion(question)) {
          return;
        }
        ids.add(questionId);
      });
    });
    return ids;
  }, [bulkCriteriaNodeIds, itemsByNode, isSharedBulkQuestion]);

  const normalizedQuestionSearchText = useMemo(
    () => normalizeTurkish(questionSearchText.trim()),
    [questionSearchText],
  );

  const checkedSummaryGroups = useMemo(() => {
    const groups: Array<{
      key: string;
      title: string;
      reminders: Array<{ key: string; order: number; controlText: string; reminderText: string }>;
    }> = [];

    const traverse = (node: any, path: string[]) => {
      if (!node?.id) {
        return;
      }
      const bundles = itemsByNode[node.id] || [];
      const checkedItems: Array<{ key: string; order: number; controlText: string; reminderText: string }> = [];
      bundles.forEach((bundle, index) => {
        const question = bundle?.question;
        if (question?.isChecked === true) {
          const controlText =
            ensureStringValue(question?.questionDescription ?? question?.description).trim() ||
            ensureStringValue(question?.title).trim();
          const reminderText = ensureStringValue(question?.checkReminderText).trim();
          const reminderKey =
            question?.id != null ? String(question.id) : `${node.id}-${index}`;
          checkedItems.push({
            key: reminderKey,
            order: index + 1,
            controlText,
            reminderText,
          });
        }
      });
      if (checkedItems.length) {
        const titles = [...path, node.title].filter(Boolean);
        const groupTitle = titles.length ? titles.join(' > ') : `Grup #${node.id}`;
        groups.push({
          key: String(node.id),
          title: groupTitle,
          reminders: checkedItems,
        });
      }
      const childNodes = childrenMap[node.id] || [];
      childNodes.forEach((child: any) => traverse(child, [...path, node.title]));
    };

    rootNodes.forEach((root) => traverse(root, []));
    return groups;
  }, [childrenMap, itemsByNode, rootNodes]);

  const requiredQuestions = useMemo(() => {
    if (shouldBlockReportQuestions) {
      return [];
    }

    const required: Array<{
      key: string;
      question: any;
      order: number;
      titles: string[];
      defaultValue?: any;
    }> = [];

    const traverse = (node: any, pathTitles: string[]) => {
      if (!node?.id) {
        return;
      }
      const bundles = itemsByNode[node.id] || [];
      bundles.forEach((bundle, index) => {
        const question = bundle?.question;
        if (panelSectionByQuestion.has(String(question?.id))) return;
        const isIncludedInInspectorForm = isQuestionIncludedInInspectorForm(question);
        const isMatchedWithSelectedControlArea = isQuestionMatchedWithSelectedControlArea(
          question,
          selectedControlAreaId,
        );
        const isRequired =
          isIncludedInInspectorForm &&
          isMatchedWithSelectedControlArea &&
          isControlTaskQuestionRequired(question, isOtherControlAreaSelected);
        if (isRequired) {
          const titles = [...pathTitles, node?.title].filter(Boolean);
          const questionKey = buildQuestionRenderKey(question, node?.id, index);
          const defaultValue =
            question?.questionType === 'MULTIPLE' && Array.isArray(bundle?.answers)
              ? getDefaultMultipleAnswerPayload(question, bundle.answers)
              : undefined;
          required.push({
            key: questionKey,
            question,
            order: index + 1,
            titles,
            defaultValue,
          });
        }
      });
      const childNodes = childrenMap[node.id] || [];
      childNodes.forEach((child: any) => traverse(child, [...pathTitles, node?.title]));
    };

    rootNodes.forEach((root) => traverse(root, []));
    return required;
  }, [
    childrenMap,
    isOtherControlAreaSelected,
    itemsByNode,
    panelSectionByQuestion,
    rootNodes,
    selectedControlAreaId,
    shouldBlockReportQuestions,
  ]);



  const visibleQuestionMetaByKey = useMemo(() => {
    const map = new Map<string, { displayOrder: number; displayLabel: string; sortIndex: number; questionTitle: string }>();
    if (shouldBlockReportQuestions) {
      return map;
    }

    let questionSortIndex = 0;

    const traverse = (node: any) => {
      if (!node?.id) {
        return;
      }

      const childNodes = childrenMap[node.id] || [];
      if (!childNodes.length) {
        const bundles = itemsByNode[node.id] || [];
        let questionDisplayIndex = 0;
        bundles.forEach((bundle, index) => {
          const question = bundle?.question;
          if (shouldHideControlTaskQuestionForSelectedControlArea(question, selectedControlAreaId)) {
            return;
          }

          questionDisplayIndex += 1;
          questionSortIndex += 1;
          const displayLabel = buildQuestionDisplayLabel(node, questionDisplayIndex);
          const questionKey = buildQuestionRenderKey(question, node?.id, index);
          map.set(questionKey, {
            displayOrder: questionDisplayIndex,
            displayLabel,
            sortIndex: questionSortIndex,
            questionTitle: ensureStringValue(question?.title).trim(),
          });
        });
      }

      childNodes.forEach((child: any) => traverse(child));
    };

    rootNodes.forEach((root) => traverse(root));
    return map;
  }, [
    childrenMap,
    itemsByNode,
    rootNodes,
    selectedControlAreaId,
    shouldBlockReportQuestions,
  ]);

  const convertValueForStorage = (questionType: string, rawValue: any): any => {
    if (rawValue === null || rawValue === undefined) {
      return undefined;
    }
    switch (questionType) {
      case 'NETWORK_TYPE':
        return toNetworkTypeAnswer(rawValue);
      case 'NUMBER': {
        const numeric = Number(rawValue);
        return Number.isFinite(numeric) ? numeric : undefined;
      }
      case 'DATE':
      case 'REGISTRATION_DATE':
      case 'DIPLOMA_DATE':
      case 'REPORT_DATE':
      case 'CONTROL_START_DATE':
      case 'CONTROL_END_DATE':
        return dayjs.isDayjs(rawValue) ? rawValue.format('YYYY-MM-DD') : typeof rawValue === 'string' ? rawValue : undefined;
      case 'TIME':
      case 'STARTED_AT':
      case 'FINISHED_AT':
        return dayjs.isDayjs(rawValue) ? rawValue.format('HH:mm') : typeof rawValue === 'string' ? rawValue : undefined;
      case 'YEAR':
        return dayjs.isDayjs(rawValue) ? rawValue.format('YYYY') : typeof rawValue === 'string' ? rawValue : undefined;
      case 'IMAGE':
        return typeof rawValue === 'object' ? rawValue : undefined;
      case 'FREETEXT':
      case 'TITLE':
      case 'ADDRESS':
      case 'SGK_REGISTRATION_NUMBER':
      case 'PHONE':
      case 'CONTRACT_ID':
      case 'EMAIL':
      case 'IDENTITY_NUMBER':
      case 'HVAC':
      case 'PLUMBING':
      case 'HYDRANT':
      case 'CABINET':
      case 'WORK_EQUIPMENT': {
        const text = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
        const trimmed = text.trim();
        return trimmed.length ? text : undefined;
      }
      default: {
        if (typeof rawValue === 'string') {
          const trimmed = rawValue.trim();
          return trimmed.length ? rawValue : undefined;
        }
        return rawValue;
      }
    }
  };

  const getFieldValue = (questionType: string, storedValue: any): any => {
    if (storedValue === undefined) {
      return undefined;
    }
    switch (questionType) {
      case 'NETWORK_TYPE':
        return getNetworkTypeValue(storedValue);
      case 'MULTIPLE':
        return normalizeMultipleAnswer(storedValue)?.id ?? null;
      case 'NUMBER':
        return typeof storedValue === 'number' ? storedValue : Number(storedValue);
      case 'DATE':
      case 'REGISTRATION_DATE':
      case 'DIPLOMA_DATE':
      case 'REPORT_DATE':
      case 'CONTROL_START_DATE':
      case 'CONTROL_END_DATE':
        return toSafeDayjs(storedValue, 'YYYY-MM-DD');
      case 'TIME':
      case 'STARTED_AT':
      case 'FINISHED_AT':
        return parseTimeValue(storedValue);
      case 'YEAR':
        return toSafeDayjs(storedValue, 'YYYY');
      case 'IMAGE':
        return storedValue;
      default:
        return storedValue;
    }
  };

  const parseNumericLimit = (value: unknown): number | undefined => {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  };

  const isCurrentYearEnabled = (value: unknown): boolean =>
    value === true || value === 'true' || value === 1 || value === '1';

  const isCurrentDayEnabled = (value: unknown): boolean =>
    value === true || value === 'true' || value === 1 || value === '1';

  const getComparableNumericValue = (value: unknown, questionType?: string): number | undefined => {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    if (questionType === 'YEAR') {
      if (dayjs.isDayjs(value)) {
        const year = value.year();
        return Number.isFinite(year) ? year : undefined;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
          return undefined;
        }
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : undefined;
      }
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      return undefined;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  };

  const toSafeDayjs = (value: unknown, format?: string) => {
    if (value === null || value === undefined) {
      return null;
    }
    if (dayjs.isDayjs(value)) {
      return value;
    }
    const parsed = format ? dayjs(value as dayjs.ConfigType, format, true) : dayjs(value as dayjs.ConfigType);
    if (parsed && typeof (parsed as any).isValid === 'function') {
      return parsed.isValid() ? parsed : null;
    }
    return null;
  };

  const parseTimeValue = (value: unknown) => {
    const withSeconds = toSafeDayjs(value, 'HH:mm:ss');
    if (withSeconds) {
      return withSeconds;
    }
    return toSafeDayjs(value, 'HH:mm');
  };

  const handleTimePickerOpenChange = (timePickerKey: string, open: boolean) => {
    setOpenTimePickerKey((currentKey) => {
      if (open) {
        return timePickerKey;
      }
      return currentKey === timePickerKey ? null : currentKey;
    });
  };

  const openTimePickerForSelection = (
    timePickerKey: string,
    currentValue: unknown,
    clearValue: () => void,
  ) => {
    if (openTimePickerKey !== timePickerKey && parseTimeValue(currentValue)) {
      clearValue();
    }
    setOpenTimePickerKey(timePickerKey);
  };

  const resolveStoredAnswerChange = (
    question: any,
    rawValue: any,
    answerOptions?: any[],
  ): { key: string; storedValue: any } | null => {
    const key = getQuestionKey(question);
    if (!key) {
      return null;
    }
    if (question?.questionType === 'FUNCTION') {
      return null;
    }
    if (question?.questionType === 'NUMBER' || question?.questionType === 'YEAR') {
      if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
        const numeric = getComparableNumericValue(rawValue, question?.questionType);
        if (numeric === undefined) {
          message.error(question?.questionType === 'YEAR' ? 'Lütfen geçerli bir yıl seçiniz.' : 'Lütfen geçerli bir sayı giriniz.');
          return null;
        }
        const min = parseNumericLimit(question?.minValue);
        const max =
          isCurrentYearEnabled(question?.isCurrentYear)
            ? new Date().getFullYear()
            : parseNumericLimit(question?.maxValue);
        if (min !== undefined && numeric < min) {
          message.error(`Bu alan için minimum değer ${min}.`);
          return null;
        }
        if (max !== undefined && numeric > max) {
          message.error(`Bu alan için maksimum değer ${max}.`);
          return null;
        }
        if (question?.questionType === 'NUMBER') {
          const digitsLimit = Number(question?.digits);
          if (Number.isFinite(digitsLimit) && digitsLimit >= 0) {
            const decimalPart = String(numeric).split('.')[1] || '';
            if (decimalPart.length > digitsLimit) {
              message.error(`En fazla ${digitsLimit} ondalık basamak girilebilir.`);
              return null;
            }
          }
        }
      }
    }
    const storedValue =
      question?.questionType === 'MULTIPLE'
        ? buildMultipleAnswerPayload(
            rawValue,
            answerOptions,
            isQuestionMultiSelectEnabled(question),
          )
        : convertValueForStorage(question?.questionType, rawValue);
    return { key, storedValue };
  };

  const applyStoredAnswerChangeToMap = (
    source: Record<string, any>,
    key: string,
    storedValue: any,
  ): Record<string, any> => {
    const next = { ...source };
    if (storedValue === undefined) {
      delete next[key];
    } else {
      next[key] = Array.isArray(storedValue)
        ? storedValue.map((item) => ({ ...item }))
        : typeof storedValue === 'object' && storedValue !== null
        ? { ...storedValue }
        : storedValue;
    }
    return next;
  };

  const handleAnswerChange = (
    question: any,
    rawValue: any,
    answerOptions?: any[],
  ) => {
    const resolved = resolveStoredAnswerChange(question, rawValue, answerOptions);
    if (!resolved) {
      return;
    }
    const { key, storedValue } = resolved;
    setAnswers((prev) => {
      return applyStoredAnswerChangeToMap(prev, key, storedValue);
    });
    if (isBulkReportEntryEnabled) {
      if (
        !isSharedBulkQuestion(question) &&
        bulkCriteriaQuestionIds.has(key)
      ) {
        const currentStatus = bulkFormStatusByOrder.get(String(bulkActiveOrder));
        if (currentStatus?.status === 'group' && currentStatus.groupId && currentStatus.groupLabel) {
          setBulkPendingGroupEdit((prev) =>
            prev && prev.orderKey === String(bulkActiveOrder)
              ? prev
              : {
                  orderKey: String(bulkActiveOrder),
                  groupId: currentStatus.groupId as string,
                  groupLabel: currentStatus.groupLabel as string,
                  orderKeys: bulkDataGroups.find((group) => group.id === currentStatus.groupId)?.orderKeys ?? [String(bulkActiveOrder)],
                },
          );
        }
      }
      const cloneStoredValue = (value: any) =>
        Array.isArray(value)
          ? value.map((item) => ({ ...item }))
          : typeof value === 'object' && value !== null
          ? { ...value }
          : value;
      const updateTarget = (previous: Record<string, any>) => {
        const next = { ...previous };
        if (storedValue === undefined) {
          delete next[key];
        } else {
          next[key] = cloneStoredValue(storedValue);
        }
        return next;
      };
      if (isSharedBulkQuestion(question)) {
        setBulkSharedAnswers(updateTarget);
      } else {
        setBulkAnswersByOrder((prev) => ({
          ...prev,
          [String(bulkActiveOrder)]: updateTarget(prev[String(bulkActiveOrder)] ?? {}),
        }));
      }
    }
  };

  const handleBulkAssignmentDraftAnswerChange = (
    question: any,
    rawValue: any,
    answerOptions?: any[],
  ) => {
    const resolved = resolveStoredAnswerChange(question, rawValue, answerOptions);
    if (!resolved) {
      return;
    }
    setBulkAssignmentDraftAnswers((prev) =>
      applyStoredAnswerChangeToMap(prev, resolved.key, resolved.storedValue),
    );
  };

  const handleYearDraftChange = (question: any, value: string, scopeKey?: string) => {
    const questionKey = getQuestionKey(question);
    const key = questionKey && scopeKey ? `${scopeKey}:${questionKey}` : questionKey;
    if (!key || !/^\d{0,4}$/.test(value)) {
      return;
    }
    setYearDraftValues((prev) => ({ ...prev, [key]: value }));
  };

  const commitYearDraft = (
    question: any,
    onAnswerChange: (question: any, rawValue: any, answerOptions?: any[]) => void = handleAnswerChange,
    scopeKey?: string,
  ) => {
    const questionKey = getQuestionKey(question);
    const key = questionKey && scopeKey ? `${scopeKey}:${questionKey}` : questionKey;
    if (!key) {
      return;
    }
    const draftValue = yearDraftValues[key];
    if (draftValue === undefined) {
      return;
    }
    const normalizedValue = draftValue.trim();
    if (normalizedValue !== '' && !/^\d{4}$/.test(normalizedValue)) {
      message.error('Lütfen geçerli bir yıl giriniz.');
      setYearDraftValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    onAnswerChange(question, normalizedValue || undefined);
    setYearDraftValues((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const readFileAsDataUrl = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });

  useEffect(() => {
    inspectionNoteAttachmentsRef.current = inspectionNoteAttachments;
  }, [inspectionNoteAttachments]);

  useEffect(() => () => {
    inspectionNoteAttachmentsRef.current.forEach(revokeInspectionNoteAttachmentPreviewUrl);
  }, []);

  const replaceInspectionNoteAttachments = useCallback((nextAttachments: InspectionNoteAttachment[]) => {
    setInspectionNoteAttachments((previousAttachments) => {
      const nextKeys = new Set(nextAttachments.map((attachment) => attachment.key));
      previousAttachments.forEach((attachment) => {
        if (!nextKeys.has(attachment.key)) {
          revokeInspectionNoteAttachmentPreviewUrl(attachment);
        }
      });
      return nextAttachments;
    });
  }, []);

  const hydrateInspectionNoteAttachments = useCallback(async (rawDocumentIds: unknown): Promise<InspectionNoteAttachment[]> => {
    const documentIds = normalizeInspectionNoteAttachmentDocumentIds(rawDocumentIds);
    return Promise.all(documentIds.map(async (documentId) => {
      try {
        const response: any = await DocumentService.getDocument(documentId);
        const documentPayload = response?.data;
        const url = extractDocumentUrl(documentPayload) ?? undefined;
        const name = resolveInspectionNoteAttachmentName(documentPayload, documentId);
        const type = resolveInspectionNoteAttachmentType(documentPayload);
        return {
          key: buildInspectionNoteAttachmentKey('saved', documentId),
          documentId,
          name,
          type,
          url,
          previewUrl: url,
        };
      } catch (documentError) {
        console.warn('ControlTaskPage: inspection note attachment could not be loaded', documentError);
        return {
          key: buildInspectionNoteAttachmentKey('saved', documentId),
          documentId,
          name: `Ek #${documentId}`,
        };
      }
    }));
  }, []);

  const addInspectionNoteAttachmentFiles = useCallback((files: File[]) => {
    if (!files.length) {
      return;
    }
    const addedAt = Date.now();
    const nextAttachments = files.map((file, index) => {
      const extension = file.type === 'image/png' ? '.png' : '';
      const name = file.name?.trim() || `denetim-notu-ek-${addedAt}-${index + 1}${extension}`;
      const previewUrl = resolveInspectionNoteAttachmentPreviewKind(file.type, name) === 'IMAGE'
        ? URL.createObjectURL(file)
        : undefined;
      return {
        key: buildInspectionNoteAttachmentKey('pending'),
        file,
        name,
        type: file.type,
        size: file.size,
        previewUrl,
      } as InspectionNoteAttachment;
    });
    setInspectionNoteAttachments((previousAttachments) => [...previousAttachments, ...nextAttachments]);
  }, []);

  const uploadPendingInspectionNoteAttachments = useCallback(async (): Promise<number[]> => {
    const attachmentsToUpload = inspectionNoteAttachments;
    if (!attachmentsToUpload.some((attachment) => attachment.file && attachment.documentId === undefined)) {
      return normalizeInspectionNoteAttachmentDocumentIds(
        attachmentsToUpload.map((attachment) => attachment.documentId),
      );
    }

    const uploadedAttachments: InspectionNoteAttachment[] = [];
    for (const attachment of attachmentsToUpload) {
      if (!attachment.file || attachment.documentId !== undefined) {
        uploadedAttachments.push(attachment);
        continue;
      }
      const uploadResponse: any = await DocumentService.postDocument(attachment.file, attachment.name);
      const documentId = toPositiveNumber(uploadResponse?.data?.id ?? uploadResponse?.data?.data?.id);
      if (documentId === undefined) {
        throw new Error('Denetim notu eki belge kimliği alınamadı.');
      }
      let documentPayload: any = uploadResponse?.data;
      try {
        const documentResponse: any = await DocumentService.getDocument(documentId);
        documentPayload = documentResponse?.data ?? documentPayload;
      } catch (documentError) {
        console.warn('ControlTaskPage: uploaded inspection note attachment URL could not be fetched', documentError);
      }
      revokeInspectionNoteAttachmentPreviewUrl(attachment);
      const url = extractDocumentUrl(documentPayload) ?? undefined;
      uploadedAttachments.push({
        key: buildInspectionNoteAttachmentKey('saved', documentId),
        documentId,
        name: resolveInspectionNoteAttachmentName(documentPayload, documentId) || attachment.name,
        type: resolveInspectionNoteAttachmentType(documentPayload) || attachment.type,
        size: attachment.size,
        url,
        previewUrl: url,
      });
    }
    setInspectionNoteAttachments(uploadedAttachments);
    return normalizeInspectionNoteAttachmentDocumentIds(
      uploadedAttachments.map((attachment) => attachment.documentId),
    );
  }, [inspectionNoteAttachments]);

  const handleInspectionNoteAttachmentRemove = useCallback((attachmentKey: string) => {
    setInspectionNoteAttachments((previousAttachments) => {
      const removedAttachment = previousAttachments.find((attachment) => attachment.key === attachmentKey);
      if (removedAttachment) {
        revokeInspectionNoteAttachmentPreviewUrl(removedAttachment);
      }
      return previousAttachments.filter((attachment) => attachment.key !== attachmentKey);
    });
    setInspectionNoteAttachmentPreview((previousPreview) =>
      previousPreview?.key === attachmentKey ? null : previousPreview,
    );
  }, []);

  const handleInspectionNoteAttachmentPaste = useCallback((event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!hasClipboardFiles(event.clipboardData)) {
      return;
    }
    event.preventDefault();
    addInspectionNoteAttachmentFiles(Array.from(event.clipboardData.files));
  }, [addInspectionNoteAttachmentFiles]);

  const handleInspectionNoteAttachmentPreview = useCallback((attachment: InspectionNoteAttachment) => {
    const url = attachment.previewUrl ?? attachment.url;
    if (!url) {
      message.warning('Dosya bağlantısı henüz hazır değil. Kaydedip sayfayı yenileyiniz.');
      return;
    }
    const previewKind = resolveInspectionNoteAttachmentPreviewKind(attachment.type, attachment.name);
    if (previewKind === 'FILE') {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    setInspectionNoteAttachmentPreview(attachment);
  }, []);

  const handleImageBeforeUpload = async (
    question: any,
    file: RcFile,
    onAnswerChange: (question: any, rawValue: any, answerOptions?: any[]) => void = handleAnswerChange,
  ) => {
    try {
      const dataUrl = await readFileAsDataUrl(file);
      onAnswerChange(question, {
        name: file.name,
        type: file.type,
        size: file.size,
        dataUrl,
        uploadedAt: new Date().toISOString(),
      });
      message.success('Görsel kaydedildi.');
    } catch (err) {
      console.error('ControlTaskPage image upload failed', err);
      message.error('Görsel yüklenirken bir hata oluştu.');
    }
    return Upload.LIST_IGNORE;
  };

  const handleAttributeStaticValueChange = useCallback(
    (index: number, nextValue: string) => {
      setAttributeStaticValues((prev) => {
        if (index < 0 || index >= prev.length) {
          return prev;
        }
        const next = [...prev];
        next[index] = { ...next[index], value: nextValue };
        return next;
      });
    },
    [],
  );

  const handleToolStaticValueChange = useCallback(
    (index: number, nextValue: string) => {
      setToolStaticValues((prev) => {
        if (index < 0 || index >= prev.length) {
          return prev;
        }
        const next = [...prev];
        next[index] = { ...next[index], value: nextValue };
        return next;
      });
    },
    [],
  );

  const [summaryDrawerVisible, setSummaryDrawerVisible] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const downloadingReportRef = useRef(false);
  const [statusPanelOpen, setStatusPanelOpen] = useState(true);
  const [nonconformityReasonDrawerVisible, setNonconformityReasonDrawerVisible] = useState(false);
  const [nonconformityReasonCreateDrawerVisible, setNonconformityReasonCreateDrawerVisible] = useState(false);
  const [nonconformityReasonQuestionTitle, setNonconformityReasonQuestionTitle] = useState('');
  const [nonconformityReasonItems, setNonconformityReasonItems] = useState<INonconformityReason[]>([]);
  const [nonconformityReasonLoading, setNonconformityReasonLoading] = useState(false);
  const [nonconformityReasonCreateSubmitting, setNonconformityReasonCreateSubmitting] = useState(false);
  const [nonconformityReasonError, setNonconformityReasonError] = useState<string | null>(null);
  const [nonconformityReasonActiveTab, setNonconformityReasonActiveTab] = useState<'PRIVATE' | 'ALL'>('PRIVATE');
  const [nonconformityReasonSearchText, setNonconformityReasonSearchText] = useState('');
  const [editingNonconformityReason, setEditingNonconformityReason] = useState<INonconformityReason | null>(null);
  const [nonconformityReasonCreateForm] = Form.useForm();
  const [nonconformityReasonTarget, setNonconformityReasonTarget] = useState<{
    question: any;
    answerOptions: any[];
    currentValue: MultipleAnswerPayload | undefined;
    onAnswerChange: AnswerEditContext['onAnswerChange'];
  } | null>(null);
  const currentAccountId = useMemo(() => {
    const storedId = typeof window !== 'undefined' ? localStorage.getItem('id') : null;
    const numericId = Number(storedId);
    return Number.isFinite(numericId) && numericId > 0 ? numericId : undefined;
  }, []);
  const currentAccountLabel = useMemo(() => {
    if (typeof window === 'undefined') {
      return currentAccountId ? `Kullanıcı #${currentAccountId}` : 'Kullanıcı';
    }
    const firstName = ensureStringValue(localStorage.getItem('firstName')).trim();
    const lastName = ensureStringValue(localStorage.getItem('lastName')).trim();
    const email = ensureStringValue(localStorage.getItem('email')).trim();
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName && email) {
      return `${fullName} (${email})`;
    }
    if (fullName) {
      return fullName;
    }
    if (email) {
      return email;
    }
    return currentAccountId ? `Kullanıcı #${currentAccountId}` : 'Kullanıcı';
  }, [currentAccountId]);
  const createNonconformityAttributeLabel = useMemo(() => {
    if (attributeLabel) {
      return attributeLabel;
    }
    return attributeIdNumber ? `Parametre #${attributeIdNumber}` : 'Parametre';
  }, [attributeLabel, attributeIdNumber]);
  const privateNonconformityReasons = useMemo(
    () =>
      currentAccountId
        ? nonconformityReasonItems.filter((item) => {
            const ownerAccountId = item?.accountId ?? item?.account?.id;
            return ownerAccountId === currentAccountId && item?.isGeneral !== true;
          })
        : [],
    [nonconformityReasonItems, currentAccountId],
  );
  const visibleNonconformityReasons = useMemo(
    () => (nonconformityReasonActiveTab === 'PRIVATE' ? privateNonconformityReasons : nonconformityReasonItems),
    [nonconformityReasonActiveTab, privateNonconformityReasons, nonconformityReasonItems],
  );
  const searchedNonconformityReasons = useMemo(() => {
    const normalizedSearch = normalizeTurkish(nonconformityReasonSearchText.trim());
    if (!normalizedSearch.length) {
      return visibleNonconformityReasons;
    }
    return visibleNonconformityReasons.filter((reason) => {
      const ownerName = `${reason.account?.firstName ?? ''} ${reason.account?.lastName ?? ''}`.trim();
      const fields = [
        reason.abbreviation,
        reason.description,
        reason.attributeName,
        ownerName,
        reason.account?.email,
      ];
      return fields.some((field) => normalizeTurkish(String(field ?? '')).includes(normalizedSearch));
    });
  }, [
    nonconformityReasonSearchText,
    visibleNonconformityReasons,
  ]);

  const loadNonconformityReasons = useCallback(async () => {
    if (!attributeIdNumber) {
      setNonconformityReasonItems([]);
      setNonconformityReasonError('Parametre bilgisi bulunamadı.');
      return;
    }
    setNonconformityReasonLoading(true);
    setNonconformityReasonError(null);
    try {
      const response = await NonconformityReasonService.listAll({
        'attributeId.equals': attributeIdNumber,
      });
      const reasons = Array.isArray(response?.data) ? response.data : [];
      setNonconformityReasonItems(reasons);
    } catch (err) {
      console.error('ControlTaskPage: failed to load nonconformity reasons', err);
      setNonconformityReasonItems([]);
      setNonconformityReasonError('Uygunsuzluk nedenleri yüklenirken bir hata oluştu.');
    } finally {
      setNonconformityReasonLoading(false);
    }
  }, [attributeIdNumber]);

  const openNonconformityReasonDrawer = useCallback(
    (
      question: any,
      answerOptions: any[],
      currentValue: MultipleAnswerPayload | undefined,
      onAnswerChange: AnswerEditContext['onAnswerChange'],
    ) => {
      setNonconformityReasonQuestionTitle(ensureStringValue(question?.title).trim());
      setNonconformityReasonActiveTab('PRIVATE');
      setNonconformityReasonSearchText('');
      setNonconformityReasonTarget({
        question,
        answerOptions: Array.isArray(answerOptions) ? answerOptions : [],
        currentValue,
        onAnswerChange,
      });
      setNonconformityReasonDrawerVisible(true);
      void loadNonconformityReasons();
    },
    [loadNonconformityReasons],
  );
  const openNonconformityReasonCreateDrawer = useCallback(() => {
    if (!attributeIdNumber) {
      message.error('Parametre bilgisi bulunamadı.');
      return;
    }
    if (!currentAccountId) {
      message.error('Kullanıcı bilgisi bulunamadı.');
      return;
    }
    nonconformityReasonCreateForm.setFieldsValue({
      attributeId: attributeIdNumber,
      accountId: currentAccountId,
      isGeneral: false,
      abbreviation: '',
      description: '',
    });
    setEditingNonconformityReason(null);
    setNonconformityReasonCreateDrawerVisible(true);
  }, [attributeIdNumber, currentAccountId, nonconformityReasonCreateForm]);
  const openNonconformityReasonEditDrawer = useCallback(
    (reason: INonconformityReason) => {
      const ownerAccountId = Number(reason?.accountId ?? reason?.account?.id);
      if (reason?.isGeneral === true || ownerAccountId !== currentAccountId) {
        return;
      }
      setEditingNonconformityReason(reason);
      nonconformityReasonCreateForm.setFieldsValue({
        attributeId: reason.attributeId ?? attributeIdNumber,
        accountId: ownerAccountId,
        isGeneral: false,
        abbreviation: reason.abbreviation ?? '',
        description: reason.description ?? '',
      });
      setNonconformityReasonCreateDrawerVisible(true);
    },
    [attributeIdNumber, currentAccountId, nonconformityReasonCreateForm],
  );
  const closeNonconformityReasonCreateDrawer = useCallback(() => {
    if (nonconformityReasonCreateSubmitting) {
      return;
    }
    setNonconformityReasonCreateDrawerVisible(false);
    setEditingNonconformityReason(null);
    nonconformityReasonCreateForm.resetFields();
  }, [nonconformityReasonCreateSubmitting, nonconformityReasonCreateForm]);
  const handleCreateNonconformityReason = useCallback(
    async (values: any) => {
      if (!attributeIdNumber) {
        message.error('Parametre bilgisi bulunamadı.');
        return;
      }
      if (!currentAccountId) {
        message.error('Kullanıcı bilgisi bulunamadı.');
        return;
      }
      const payload: INonconformityReasonPayload = {
        abbreviation: ensureStringValue(values?.abbreviation).trim(),
        description: ensureStringValue(values?.description).trim(),
        isGeneral: false,
        attributeId: attributeIdNumber,
        accountId: currentAccountId,
      };
      if (!payload.abbreviation) {
        message.error('Kısaltma zorunludur.');
        return;
      }
      if (!payload.description) {
        message.error('Açıklama zorunludur.');
        return;
      }
      setNonconformityReasonCreateSubmitting(true);
      try {
        if (editingNonconformityReason) {
          await NonconformityReasonService.update(editingNonconformityReason.id, payload);
          message.success('Uygunsuzluk nedeni güncellendi.');
        } else {
          await NonconformityReasonService.create(payload);
          message.success('Uygunsuzluk nedeni oluşturuldu.');
        }
        setNonconformityReasonCreateDrawerVisible(false);
        setEditingNonconformityReason(null);
        nonconformityReasonCreateForm.resetFields();
        await loadNonconformityReasons();
      } catch (err: any) {
        console.error('ControlTaskPage: failed to create nonconformity reason', err);
        const errorMessage =
          ensureStringValue(err?.response?.data?.message).trim() ||
          'Uygunsuzluk nedeni oluşturulurken bir hata oluştu.';
        message.error(errorMessage);
      } finally {
        setNonconformityReasonCreateSubmitting(false);
      }
    },
    [attributeIdNumber, currentAccountId, editingNonconformityReason, nonconformityReasonCreateForm, loadNonconformityReasons],
  );

  const handleDeleteNonconformityReason = useCallback(
    async (reason: INonconformityReason) => {
      try {
        await NonconformityReasonService.remove(reason.id);
        message.success('Uygunsuzluk nedeni silindi.');
        await loadNonconformityReasons();
      } catch (err: any) {
        console.error('ControlTaskPage: failed to delete nonconformity reason', err);
        const errorMessage =
          ensureStringValue(err?.response?.data?.message).trim() ||
          'Uygunsuzluk nedeni silinirken bir hata oluştu.';
        message.error(errorMessage);
      }
    },
    [loadNonconformityReasons],
  );

  const handleSelectNonconformityReason = useCallback(
    (reason: INonconformityReason) => {
      if (!nonconformityReasonTarget) {
        return;
      }
      const reasonText = ensureStringValue(reason?.description).trim();
      if (!reasonText) {
        message.warning('Seçilen nedenin açıklama metni bulunamadı.');
        return;
      }
      const { question, answerOptions, currentValue, onAnswerChange } = nonconformityReasonTarget;
      if (question?.questionType !== MULTIPLE_TYPE || !currentValue) {
        message.warning('Seçili soru için cevap bulunamadı.');
        return;
      }

      const updated = applyNonconformityReasonToMultipleAnswer(
        currentValue,
        answerOptions,
        reasonText,
      );
      onAnswerChange(question, updated, answerOptions);

      setNonconformityReasonDrawerVisible(false);
      setNonconformityReasonTarget(null);
      message.success('Uygunsuzluk nedeni açıklamaya eklendi.');
    },
    [nonconformityReasonTarget],
  );

  const resetResponseDraft = useCallback(() => {
    if (panelDraftStorageKey) {
      try { sessionStorage.removeItem(panelDraftStorageKey); } catch { /* Browser storage may be unavailable. */ }
    }
    setYearDraftValues({});
    applyControlGroups([]);
    setAnswers({});
    setEarthingMeasurementsByNode({});
    setRcdMeasurementsByNode({});
    setRcdMeasurementsLoadError(false);
    setMeasurementResponseKind(undefined);
    setPendingMultipleAnswerImages({});
    setAttributeStaticValues([]);
    setToolStaticValues([]);
    setSelectedControlAreaId(undefined);
    setCustomControlAreaName('');
    setNoteContents({});
    setSavedResponseMeta(null);
    setResponseReportFormatId(null);
    setElapsedSecondsDisplay(0);
    setSummaryDrawerVisible(false);
  }, [applyControlGroups, panelDraftStorageKey]);

  const handleDeleteAnswers = useCallback(async () => {
    if (!proposalIdNumber || !taskIdNumber || !attributeIdNumber || !orderNumber) {
      message.error('Geçersiz kontrol bilgileri. Lütfen sayfayı yeniden yükleyiniz.');
      return;
    }

    if (saving || deleting) {
      return;
    }

    setDeleting(true);
    try {
      await deleteControlTaskAnswers(
        proposalIdNumber,
        taskIdNumber,
        attributeIdNumber,
        orderNumber,
      );
      resetResponseDraft();
      message.success('Kontrol sonuçları silindi.');
    } catch (err) {
      console.error('ControlTaskPage: failed to delete control answers', err);
      message.error('Kontrol sonuçları silinirken bir hata oluştu.');
    } finally {
      setDeleting(false);
    }
  }, [
    proposalIdNumber,
    taskIdNumber,
    attributeIdNumber,
    orderNumber,
    saving,
    deleting,
    resetResponseDraft,
  ]);

  const buildAnswersForSubmit = useCallback((options?: { stripTransientFields?: boolean }): Record<string, any> => {
    const stripTransientFields = options?.stripTransientFields !== false;
    const answersForSubmit: Record<string, any> = { ...answersWithFunctions };
    Object.entries(classicQuestionBundles).forEach(([questionId, bundle]) => {
      if (bundle?.question?.questionType === 'TABLE') {
        answersForSubmit[questionId] = normalizeFixedTableAnswerValue(bundle, answersForSubmit[questionId]);
        return;
      }
      if (bundle?.question?.questionType !== 'MULTIPLE') {
        return;
      }
      const rebuilt = buildMultipleAnswerSubmitPayload(
        answersForSubmit[questionId],
        bundle.question,
        bundle.answers,
        selectedControlAreaId,
      );
      if (rebuilt === undefined) {
        delete answersForSubmit[questionId];
        return;
      }
      answersForSubmit[questionId] = stripTransientFields
        ? stripTransientMultipleAnswerFields(rebuilt)
        : rebuilt;
    });
    panelSectionByQuestion.forEach((_, key) => delete answersForSubmit[key]);
    return answersForSubmit;
  }, [answersWithFunctions, normalizeFixedTableAnswerValue, classicQuestionBundles, panelSectionByQuestion, selectedControlAreaId]);

  const buildAnswersForValidation = useCallback((): Record<string, any> => (
    buildAnswersForSubmit({ stripTransientFields: false })
  ), [buildAnswersForSubmit]);

  const buildAnswersForSubmitFromMap = useCallback((
    sourceAnswers: Record<string, any>,
    options?: {
      includeQuestion?: (questionId: string, bundle: QuestionBundle) => boolean;
      stripTransientFields?: boolean;
    },
  ): Record<string, any> => {
    const includeQuestion = options?.includeQuestion;
    const stripTransientFields = options?.stripTransientFields !== false;
    const shouldIncludeQuestion = (questionId: string, bundle?: QuestionBundle): bundle is QuestionBundle => {
      if (!includeQuestion) {
        return true;
      }
      return !!bundle && includeQuestion(questionId, bundle);
    };
    const answersForSubmit: Record<string, any> = {};
    Object.entries(sourceAnswers).forEach(([questionId, value]) => {
      if (!shouldIncludeQuestion(questionId, questionBundlesById[questionId])) {
        return;
      }
      answersForSubmit[questionId] = value;
    });
    if (Object.keys(questionBundlesById).length) {
      const context = createFunctionEvaluationContext(
        { ...sourceAnswers, ...answersForSubmit },
        questionBundlesById,
        attributeStaticValues,
        toolStaticValues,
      );
      Object.entries(questionBundlesById)
        .filter(([questionId, bundle]) =>
          shouldIncludeQuestion(questionId, bundle) && bundle?.question?.questionType === 'FUNCTION'
        )
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .forEach(([questionId, bundle]) => {
          const result = evaluateFunctionQuestion(bundle.question, context);
          const tag = normalizeFunctionTag(bundle.question?.functionTag);
          if (!result || !result.displayValue) {
            delete answersForSubmit[questionId];
            if (tag) {
              delete context.questionValues[tag];
            }
            return;
          }
          answersForSubmit[questionId] = result.displayValue;
          if (tag) {
            context.questionValues[tag] = {
              text: result.displayValue,
              numeric: result.numericValue ?? tryParseNumericValue(result.displayValue),
            };
          }
        });
    }
    Object.entries(questionBundlesById).forEach(([questionId, bundle]) => {
      if (!shouldIncludeQuestion(questionId, bundle)) {
        return;
      }
      if (bundle?.question?.questionType === 'TABLE') {
        answersForSubmit[questionId] = normalizeFixedTableAnswerValue(bundle, answersForSubmit[questionId]);
        return;
      }
      if (bundle?.question?.questionType !== 'MULTIPLE') {
        return;
      }
      const rebuilt = buildMultipleAnswerSubmitPayload(
        answersForSubmit[questionId],
        bundle.question,
        bundle.answers,
        selectedControlAreaId,
      );
      if (rebuilt === undefined) {
        delete answersForSubmit[questionId];
        return;
      }
      answersForSubmit[questionId] = stripTransientFields
        ? stripTransientMultipleAnswerFields(rebuilt)
        : rebuilt;
    });
    return answersForSubmit;
  }, [attributeStaticValues, normalizeFixedTableAnswerValue, questionBundlesById, selectedControlAreaId, toolStaticValues]);

  const buildSynchronizedBulkAnswerMaps = useCallback(() => {
    return synchronizeBulkReportAnswerMaps({
      activeOrder: bulkActiveOrder,
      activeAnswers: answers,
      bulkSharedAnswers,
      bulkAnswersByOrder,
      bulkReportCount,
      isSharedQuestion: (questionId) => {
        const bundle = questionBundlesById[questionId];
        return !!bundle?.question && isSharedBulkQuestion(bundle.question);
      },
    });
  }, [
    answers,
    bulkActiveOrder,
    bulkAnswersByOrder,
    bulkReportCount,
    bulkSharedAnswers,
    isSharedBulkQuestion,
    questionBundlesById,
  ]);

  const bulkOrderKeys = useMemo(() => resolveBulkReportOrderKeys(bulkReportCount), [bulkReportCount]);

  const synchronizedBulkAnswerMaps = useMemo(() => {
    if (!isBulkReportEntryEnabled) {
      return {
        sharedAnswers: bulkSharedAnswers,
        answersByOrder: bulkAnswersByOrder,
      };
    }
    return buildSynchronizedBulkAnswerMaps();
  }, [
    buildSynchronizedBulkAnswerMaps,
    bulkAnswersByOrder,
    bulkSharedAnswers,
    isBulkReportEntryEnabled,
  ]);

  const bulkDataGroups = useMemo(
    () => resolveBulkDataGroups(
      bulkOrderKeys,
      synchronizedBulkAnswerMaps.answersByOrder,
      bulkCriteriaQuestionIds,
    ),
    [bulkOrderKeys, synchronizedBulkAnswerMaps.answersByOrder, bulkCriteriaQuestionIds],
  );

  const bulkFormStatuses = useMemo(
    () => resolveBulkFormStatuses(
      bulkOrderKeys,
      synchronizedBulkAnswerMaps.answersByOrder,
      bulkCriteriaQuestionIds,
      bulkDataGroups,
      bulkOverrideSourcesByOrder,
    ),
    [
      bulkOrderKeys,
      synchronizedBulkAnswerMaps.answersByOrder,
      bulkCriteriaQuestionIds,
      bulkDataGroups,
      bulkOverrideSourcesByOrder,
    ],
  );

  const bulkFormStatusByOrder = useMemo(() => {
    return new Map(bulkFormStatuses.map((status) => [status.orderKey, status]));
  }, [bulkFormStatuses]);

  const activeBulkFormStatus = useMemo(
    () => bulkFormStatusByOrder.get(String(bulkActiveOrder)),
    [bulkActiveOrder, bulkFormStatusByOrder],
  );

  useEffect(() => {
    const availableKeys = new Set(bulkOrderKeys);
    setSelectedBulkOrderKeys((prev) => prev.filter((orderKey) => availableKeys.has(orderKey)));
    setBulkAssignmentOrderKeys((prev) => prev.filter((orderKey) => availableKeys.has(orderKey)));
    setBulkOverrideSourcesByOrder((prev) => {
      let changed = false;
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([orderKey, groupId]) => {
        if (availableKeys.has(orderKey)) {
          next[orderKey] = groupId;
          return;
        }
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [bulkOrderKeys]);

  const buildBulkValidationAnswersForSubmit = useCallback(() => {
    return resolveBulkValidationAnswerMaps({
      activeOrder: bulkActiveOrder,
      activeAnswers: answers,
      bulkSharedAnswers,
      bulkAnswersByOrder,
      bulkReportCount,
      isSharedQuestion: (questionId) => {
        const bundle = questionBundlesById[questionId];
        return !!bundle?.question && isSharedBulkQuestion(bundle.question);
      },
    }).map((item) => ({
      ...item,
      answersForSubmit: buildAnswersForSubmitFromMap(item.answers, {
        stripTransientFields: false,
      }),
    }));
  }, [
    answers,
    bulkActiveOrder,
    bulkAnswersByOrder,
    bulkReportCount,
    bulkSharedAnswers,
    buildAnswersForSubmitFromMap,
    isSharedBulkQuestion,
    questionBundlesById,
  ]);

  const buildBulkAnswersSubmitPayload = useCallback((sourceMaps?: {
    sharedAnswers?: Record<string, any>;
    answersByOrder?: AnswersByOrderMap;
  }) => {
    const orderKeys = resolveBulkReportOrderKeys(bulkReportCount);
    const count = orderKeys.length;
    const synchronized = {
      ...buildSynchronizedBulkAnswerMaps(),
      ...(sourceMaps?.sharedAnswers ? { sharedAnswers: sourceMaps.sharedAnswers } : {}),
      ...(sourceMaps?.answersByOrder ? { answersByOrder: sourceMaps.answersByOrder } : {}),
    };
    const sharedQuestionIds = new Set(
      Object.entries(questionBundlesById)
        .filter(([, bundle]) => !!bundle?.question && isSharedBulkQuestion(bundle.question))
        .map(([questionId]) => questionId),
    );
    const sharedAnswersForSubmit = buildAnswersForSubmitFromMap(synchronized.sharedAnswers, {
      includeQuestion: (questionId) => sharedQuestionIds.has(questionId),
    });
    const answersByOrderForSubmit: Record<string, Record<string, any>> = {};

    orderKeys.forEach((orderKey) => {
      const mergedAnswers = {
        ...synchronized.sharedAnswers,
        ...(synchronized.answersByOrder[orderKey] ?? {}),
      };
      const fullAnswers = buildAnswersForSubmitFromMap(mergedAnswers);
      answersByOrderForSubmit[orderKey] = omitSharedQuestionAnswers(fullAnswers, sharedQuestionIds);
    });

    return {
      count,
      synchronizedSharedAnswers: synchronized.sharedAnswers,
      synchronizedAnswersByOrder: synchronized.answersByOrder,
      sharedAnswersForSubmit,
      answersByOrderForSubmit,
    };
  }, [
    bulkReportCount,
    buildAnswersForSubmitFromMap,
    buildSynchronizedBulkAnswerMaps,
    isSharedBulkQuestion,
    questionBundlesById,
  ]);

  const syncBulkAnswerState = useCallback(() => {
    const synchronized = buildSynchronizedBulkAnswerMaps();
    setBulkSharedAnswers(synchronized.sharedAnswers);
    setBulkAnswersByOrder(synchronized.answersByOrder);
    return synchronized;
  }, [buildSynchronizedBulkAnswerMaps]);

  const resolveBulkFormLabel = useCallback(
    (orderNo: number) => {
      return resolveBulkFormDisplayLabel(orderNo, bulkReportUnitLabel);
    },
    [bulkReportUnitLabel],
  );

  const handleBulkActiveOrderChange = useCallback(
    (nextOrder: number) => {
      const synchronized = syncBulkAnswerState();
      const clampedOrder = clampBulkActiveOrder(nextOrder, bulkReportCount);
      setBulkActiveOrder(clampedOrder);
      setAnswers(resolveBulkActiveAnswers(clampedOrder, synchronized.sharedAnswers, synchronized.answersByOrder));
    },
    [bulkReportCount, syncBulkAnswerState],
  );

  const handleBulkReportCountChange = useCallback(
    (value: number | null) => {
      if (!hasBulkCapacity) {
        return;
      }
      const requestedCount = Math.max(1, Math.floor(Number(value) || 1));
      const nextCount = bulkReportCountLimit === undefined
        ? requestedCount
        : Math.min(requestedCount, Math.max(bulkReportCountLimit, 0));
      const synchronized = syncBulkAnswerState();
      const nextActiveOrder = clampBulkActiveOrder(bulkActiveOrder, Math.max(nextCount, 1));
      setBulkReportCount(nextCount);
      setBulkActiveOrder(nextActiveOrder);
      setAnswers(resolveBulkActiveAnswers(nextActiveOrder, synchronized.sharedAnswers, synchronized.answersByOrder));
    },
    [bulkActiveOrder, bulkReportCountLimit, hasBulkCapacity, syncBulkAnswerState],
  );

  const toggleBulkOrderSelection = useCallback((orderKey: string, checked: boolean) => {
    setSelectedBulkOrderKeys((prev) => {
      if (checked) {
        return prev.includes(orderKey) ? prev : [...prev, orderKey];
      }
      return prev.filter((key) => key !== orderKey);
    });
  }, []);

  const openBulkAssignmentDrawer = useCallback(
    (orderKeys: string[]) => {
      const availableKeys = new Set(resolveBulkReportOrderKeys(bulkReportCount));
      const targetOrderKeys = orderKeys.filter((orderKey) => availableKeys.has(orderKey));
      if (!targetOrderKeys.length) {
        message.warning('Veri atanacak form seçiniz.');
        return;
      }
      if (!bulkCriteriaQuestionIds.size) {
        message.warning('Kontrol kriterleri altında rapor bazlı alan bulunamadı.');
        return;
      }
      const synchronized = syncBulkAnswerState();
      setBulkAssignmentOrderKeys(targetOrderKeys);
      setBulkAssignmentDraftAnswers(
        resolveCommonBulkCriteriaAnswers(
          targetOrderKeys,
          synchronized.answersByOrder,
          bulkCriteriaQuestionIds,
        ),
      );
      setBulkAssignmentDrawerOpen(true);
    },
    [bulkCriteriaQuestionIds, bulkReportCount, syncBulkAnswerState],
  );

  const closeBulkAssignmentDrawer = useCallback(() => {
    setBulkAssignmentDrawerOpen(false);
    setBulkAssignmentOrderKeys([]);
    setBulkAssignmentDraftAnswers({});
  }, []);

  const scrollToQuestion = useCallback((questionKey: string) => {
    const targetId = buildQuestionAnchorId(questionKey);
    const element = document.getElementById(targetId);
    if (!element) {
      return;
    }
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const bulkAssignmentIncompleteIssues = useMemo(() => {
    if (!bulkAssignmentDrawerOpen) {
      return [];
    }
    const answersForSubmit = buildAnswersForSubmitFromMap(bulkAssignmentDraftAnswers, {
      includeQuestion: (questionId) => bulkCriteriaQuestionIds.has(String(questionId)),
      stripTransientFields: false,
    });
    return collectIncompleteQuestionIssues({
      answersForSubmit,
      questionBundlesById,
      requiredQuestions,
      selectedControlAreaId,
      visibleQuestionMetaByKey,
      includedQuestionIds: bulkCriteriaQuestionIds,
      nonconformityDescriptionQuestionIds: bulkCriteriaQuestionIds,
    });
  }, [
    buildAnswersForSubmitFromMap,
    bulkAssignmentDraftAnswers,
    bulkAssignmentDrawerOpen,
    bulkCriteriaQuestionIds,
    questionBundlesById,
    requiredQuestions,
    selectedControlAreaId,
    visibleQuestionMetaByKey,
  ]);

  const scrollToBulkAssignmentQuestion = useCallback((questionKey: string) => {
    const container = bulkAssignmentDrawerBodyRef.current;
    const targetId = buildQuestionAnchorId(questionKey);
    const element = container
      ? Array.from(container.querySelectorAll<HTMLElement>('[id]')).find((candidate) => candidate.id === targetId)
      : null;
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    scrollToQuestion(questionKey);
  }, [scrollToQuestion]);

  const handleApplyBulkAssignment = useCallback(() => {
    if (!bulkAssignmentOrderKeys.length) {
      message.warning('Veri atanacak form seçiniz.');
      return;
    }
    const firstIncompleteIssue = bulkAssignmentIncompleteIssues[0];
    if (firstIncompleteIssue) {
      scrollToBulkAssignmentQuestion(firstIncompleteIssue.questionKey);
      message.error(buildIncompleteIssueErrorMessage(firstIncompleteIssue));
      return;
    }
    const synchronized = syncBulkAnswerState();
    const criteriaAnswers = extractBulkCriteriaAnswers(
      bulkAssignmentDraftAnswers,
      bulkCriteriaQuestionIds,
    );
    const sanitizedCriteriaAnswers = sanitizeBulkAssignmentCriteriaAnswers(criteriaAnswers);
    const nextAnswersByOrder = applyBulkCriteriaAnswersToOrders(
      synchronized.answersByOrder,
      bulkAssignmentOrderKeys,
      sanitizedCriteriaAnswers,
      bulkCriteriaQuestionIds,
    );
    setBulkAnswersByOrder(nextAnswersByOrder);
    setBulkSharedAnswers(synchronized.sharedAnswers);
    if (bulkAssignmentOrderKeys.includes(String(bulkActiveOrder))) {
      setAnswers(resolveBulkActiveAnswers(bulkActiveOrder, synchronized.sharedAnswers, nextAnswersByOrder));
    }

    const previousStatuses = resolveBulkFormStatuses(
      bulkOrderKeys,
      synchronized.answersByOrder,
      bulkCriteriaQuestionIds,
      bulkDataGroups,
      bulkOverrideSourcesByOrder,
    );
    const previousStatusByOrder = new Map(previousStatuses.map((status) => [status.orderKey, status]));
    setBulkOverrideSourcesByOrder((prev) => {
      const next = { ...prev };
      if (bulkAssignmentOrderKeys.length >= 2) {
        bulkAssignmentOrderKeys.forEach((orderKey) => {
          delete next[orderKey];
        });
      } else {
        const orderKey = bulkAssignmentOrderKeys[0];
        const previousStatus = previousStatusByOrder.get(orderKey);
        if (previousStatus?.groupId) {
          next[orderKey] = previousStatus.groupId;
        }
      }
      return next;
    });
    setBulkPendingGroupEdit((prev) =>
      prev && bulkAssignmentOrderKeys.includes(prev.orderKey) ? null : prev,
    );
    closeBulkAssignmentDrawer();
    message.success(
      bulkAssignmentOrderKeys.length >= 2
        ? 'Seçilen formlara veri atandı.'
        : 'Form özelleştirildi.',
    );
  }, [
    bulkActiveOrder,
    bulkAssignmentDraftAnswers,
    bulkAssignmentIncompleteIssues,
    bulkAssignmentOrderKeys,
    bulkCriteriaQuestionIds,
    bulkDataGroups,
    bulkOrderKeys,
    bulkOverrideSourcesByOrder,
    closeBulkAssignmentDrawer,
    scrollToBulkAssignmentQuestion,
    syncBulkAnswerState,
  ]);

  const handleRevertBulkOrderToGroup = useCallback(
    (orderKey: string) => {
      const sourceGroupId = bulkOverrideSourcesByOrder[orderKey];
      const sourceGroup = sourceGroupId
        ? bulkDataGroups.find((group) => group.id === sourceGroupId)
        : undefined;
      if (!sourceGroup) {
        message.warning('Bağlı veri grubu bulunamadı.');
        return;
      }
      const synchronized = syncBulkAnswerState();
      const nextAnswersByOrder = applyBulkCriteriaAnswersToOrders(
        synchronized.answersByOrder,
        [orderKey],
        sourceGroup.answers,
        bulkCriteriaQuestionIds,
      );
      setBulkAnswersByOrder(nextAnswersByOrder);
      setBulkSharedAnswers(synchronized.sharedAnswers);
      if (String(bulkActiveOrder) === orderKey) {
        setAnswers(resolveBulkActiveAnswers(bulkActiveOrder, synchronized.sharedAnswers, nextAnswersByOrder));
      }
      setBulkOverrideSourcesByOrder((prev) => {
        const next = { ...prev };
        delete next[orderKey];
        return next;
      });
      setBulkPendingGroupEdit((prev) => (prev?.orderKey === orderKey ? null : prev));
      message.success('Form grup verisine döndürüldü.');
    },
    [
      bulkActiveOrder,
      bulkCriteriaQuestionIds,
      bulkDataGroups,
      bulkOverrideSourcesByOrder,
      syncBulkAnswerState,
    ],
  );

  const bulkAssignmentEditContext: AnswerEditContext = {
    answers: bulkAssignmentDraftAnswers,
    onAnswerChange: handleBulkAssignmentDraftAnswerChange,
  };

  const applyResolvedControlTaskAutoFillValues = useCallback((values: ControlTaskAutoFillValues) => {
    const applicableControlAreaId = isOtherControlAreaSelected
      ? undefined
      : selectedControlAreaId;

    if (!isBulkReportEntryEnabled) {
      setAnswers((previous) =>
        applyControlTaskAutoFillAnswers(
          previous,
          questionBundlesById,
          values,
          applicableControlAreaId,
        ),
      );
      message.success('Tarih ve saat alanları güncellendi.');
      return;
    }

    const synchronized = buildSynchronizedBulkAnswerMaps();
    const isSharedQuestion = (_questionId: string, bundle: QuestionBundle) =>
      !!bundle?.question && isSharedBulkQuestion(bundle.question);
    const autoFilledBulkAnswers = applyControlTaskAutoFillToBulkAnswers({
      sharedAnswers: synchronized.sharedAnswers,
      answersByOrder: synchronized.answersByOrder,
      orderKeys: bulkOrderKeys,
      questionBundlesById,
      values,
      selectedControlAreaId: applicableControlAreaId,
      isSharedQuestion,
    });
    const nextSharedAnswers = autoFilledBulkAnswers.sharedAnswers;
    const nextAnswersByOrder = autoFilledBulkAnswers.answersByOrder;

    setBulkSharedAnswers(nextSharedAnswers);
    setBulkAnswersByOrder(nextAnswersByOrder);
    setAnswers(
      resolveBulkActiveAnswers(
        bulkActiveOrder,
        nextSharedAnswers,
        nextAnswersByOrder,
      ),
    );
    if (bulkAssignmentDrawerOpen) {
      setBulkAssignmentDraftAnswers((previous) =>
        applyControlTaskAutoFillAnswers(
          previous,
          questionBundlesById,
          values,
          applicableControlAreaId,
          (questionId, bundle) =>
            !isSharedQuestion(questionId, bundle) &&
            bulkCriteriaQuestionIds.has(questionId),
        ),
      );
    }
    setBulkPendingGroupEdit(null);
    message.success('Tüm raporların tarih ve saat alanları güncellendi.');
  }, [
    buildSynchronizedBulkAnswerMaps,
    bulkActiveOrder,
    bulkAssignmentDrawerOpen,
    bulkCriteriaQuestionIds,
    bulkOrderKeys,
    isBulkReportEntryEnabled,
    isOtherControlAreaSelected,
    isSharedBulkQuestion,
    questionBundlesById,
    selectedControlAreaId,
  ]);

  const handleControlTaskAutoFill = useCallback(() => {
    const fieldControlTime =
      attributeDetail?.fieldControlTime ??
      inspectionTask?.attribute?.fieldControlTime ??
      reportFormat?.attribute?.fieldControlTime;
    const autoFillReportCount = isBulkReportEntryEnabled ? bulkOrderKeys.length : 1;
    const durationMinutes = resolveControlTaskAutoFillDurationMinutes(
      fieldControlTime,
      autoFillReportCount,
    );
    if (durationMinutes === null) {
      message.error('Parametre için geçerli bir Saha Kontrol Dk değeri tanımlanmalıdır.');
      return;
    }

    const applicableControlAreaId = isOtherControlAreaSelected
      ? undefined
      : selectedControlAreaId;
    if (!hasControlTaskAutoFillQuestions(questionBundlesById, applicableControlAreaId)) {
      message.warning('Otomatik doldurulabilecek tarih veya saat sorusu bulunamadı.');
      return;
    }

    Modal.confirm({
      title: 'Tarih ve saat alanları güncellensin mi?',
      content: (
        <Space direction='vertical' size={4}>
          <Text>Kontrol ve rapor tarihleri bugünün tarihiyle doldurulacaktır.</Text>
          <Text>{`Başlangıç saati, bitiş saatinden ${durationMinutes} dakika önce olarak hesaplanacaktır.`}</Text>
          <Text>Bitiş saati onay verdiğiniz anın saati olacaktır.</Text>
          <Text type='secondary'>Bu işlem ilgili alanlardaki mevcut değerlerin üzerine yazacaktır.</Text>
        </Space>
      ),
      okText: 'Evet, güncelle',
      cancelText: 'Vazgeç',
      onOk: () => {
        const confirmedValues = resolveControlTaskAutoFillValues(
          fieldControlTime,
          dayjs(),
          autoFillReportCount,
        );
        if (!confirmedValues) {
          message.error('Parametre için geçerli bir Saha Kontrol Dk değeri tanımlanmalıdır.');
          return;
        }
        applyResolvedControlTaskAutoFillValues(confirmedValues);
      },
    });
  }, [
    applyResolvedControlTaskAutoFillValues,
    attributeDetail?.fieldControlTime,
    bulkOrderKeys.length,
    inspectionTask?.attribute?.fieldControlTime,
    isBulkReportEntryEnabled,
    isOtherControlAreaSelected,
    questionBundlesById,
    reportFormat?.attribute?.fieldControlTime,
    selectedControlAreaId,
  ]);

  const uploadPendingMultipleAnswerImages = useCallback(
    async (answersForSubmit: Record<string, any>, scopeKey?: string): Promise<Record<string, any>> => {
      const pendingKeys = Object.keys(pendingMultipleAnswerImages).filter((key) => scopeKey ? key.startsWith(`${scopeKey}:`) : !key.startsWith('panel:'));
      if (!pendingKeys.length) {
        return answersForSubmit;
      }

      const nextAnswers: Record<string, any> = { ...answersForSubmit };
      const uploadedImageFieldsByPendingKey = new Map<
        string,
        Pick<MultipleAnswerValue, 'documentId' | 'dataUrl' | 'imageUrl' | 'imageName' | 'imageType' | 'imagePreviewUrl'>
      >();

      for (const pendingKey of pendingKeys) {
        const file = pendingMultipleAnswerImages[pendingKey];
        if (!file) {
          continue;
        }

        const uploadResponse: any = await DocumentService.postDocument(file, file.name);
        const documentId = uploadResponse?.data?.id ?? uploadResponse?.data?.data?.id;
        if (!documentId) {
          throw new Error('Görsel belge kimliği alınamadı');
        }

        let imageUrl: string | null = null;
        try {
          const documentResponse: any = await DocumentService.getDocument(documentId);
          imageUrl = extractDocumentUrl(documentResponse?.data);
        } catch (documentError) {
          console.warn('ControlTaskPage: uploaded image URL could not be fetched', documentError);
        }

        uploadedImageFieldsByPendingKey.set(pendingKey, {
          documentId,
          dataUrl: null,
          imageUrl,
          imagePreviewUrl: null,
          imageName: file.name,
          imageType: file.type,
        });
      }

      Object.entries(nextAnswers).forEach(([questionId, value]) => {
        const bundle = questionBundlesById[questionId];
        if (bundle?.question?.questionType !== MULTIPLE_TYPE) {
          return;
        }

        if (Array.isArray(value)) {
          nextAnswers[questionId] = value.map((item) => {
            const normalized = normalizeMultipleAnswer(item);
            if (!normalized?.id) {
              return item;
            }
            let nextItem = item;
            if (Array.isArray(normalized.images) && normalized.images.length) {
              nextItem = {
                ...nextItem,
                images: normalized.images.map((image) => {
                  const imageFields = uploadedImageFieldsByPendingKey.get(
                    buildPendingMultipleAnswerImageKey(scopeKey ? `${scopeKey}:${questionId}` : questionId, normalized.id as string, image.key),
                  );
                  return imageFields ? { ...image, ...imageFields } : image;
                }),
              };
            }
            const fields = uploadedImageFieldsByPendingKey.get(
              buildPendingMultipleAnswerImageKey(scopeKey ? `${scopeKey}:${questionId}` : questionId, normalized.id),
            );
            return fields ? { ...nextItem, ...fields } : nextItem;
          });
          return;
        }

        const normalized = normalizeMultipleAnswer(value);
        if (!normalized?.id) {
          return;
        }
        let nextValue = value;
        if (Array.isArray(normalized.images) && normalized.images.length) {
          nextValue = {
            ...nextValue,
            images: normalized.images.map((image) => {
              const imageFields = uploadedImageFieldsByPendingKey.get(
                buildPendingMultipleAnswerImageKey(scopeKey ? `${scopeKey}:${questionId}` : questionId, normalized.id as string, image.key),
              );
              return imageFields ? { ...image, ...imageFields } : image;
            }),
          };
        }
        const fields = uploadedImageFieldsByPendingKey.get(
          buildPendingMultipleAnswerImageKey(scopeKey ? `${scopeKey}:${questionId}` : questionId, normalized.id),
        );
        if (fields || nextValue !== value) {
          nextAnswers[questionId] = fields ? { ...nextValue, ...fields } : nextValue;
        }
      });

      return nextAnswers;
    },
    [pendingMultipleAnswerImages, questionBundlesById],
  );

  const buildBulkSubmitSourceMapsWithUploadedImages = useCallback(
    async (sourceMaps?: {
      sharedAnswers?: Record<string, any>;
      answersByOrder?: AnswersByOrderMap;
    }) => {
      if (!Object.keys(pendingMultipleAnswerImages).length) {
        return sourceMaps;
      }

      const uploadedActiveAnswers = await uploadPendingMultipleAnswerImages(
        buildAnswersForSubmit({ stripTransientFields: false }),
      );
      const baseSynchronized = {
        ...buildSynchronizedBulkAnswerMaps(),
        ...(sourceMaps?.sharedAnswers ? { sharedAnswers: sourceMaps.sharedAnswers } : {}),
        ...(sourceMaps?.answersByOrder ? { answersByOrder: sourceMaps.answersByOrder } : {}),
      };
      return synchronizeBulkReportAnswerMaps({
        activeOrder: bulkActiveOrder,
        activeAnswers: uploadedActiveAnswers,
        bulkSharedAnswers: baseSynchronized.sharedAnswers,
        bulkAnswersByOrder: baseSynchronized.answersByOrder,
        bulkReportCount,
        isSharedQuestion: (questionId) => {
          const bundle = questionBundlesById[questionId];
          return !!bundle?.question && isSharedBulkQuestion(bundle.question);
        },
      });
    },
    [
      buildAnswersForSubmit,
      buildSynchronizedBulkAnswerMaps,
      bulkActiveOrder,
      bulkReportCount,
      isSharedBulkQuestion,
      pendingMultipleAnswerImages,
      questionBundlesById,
      uploadPendingMultipleAnswerImages,
    ],
  );

  const isPanelQuestionInScope = useCallback((group: PanelControlViewGroup, questionId: string) => {
    if (panelSectionByQuestion.get(questionId) !== group.reportNodeId) return false;
    if (!isCorrectionMode || correctionCaseType === 'FOLLOW_UP') return true;
    return (correctionPanelQuestions[String(group.originControlGroupId)] ?? []).includes(Number(questionId));
  }, [panelSectionByQuestion, isCorrectionMode, correctionCaseType, correctionPanelQuestions]);

  useEffect(() => {
    if (!panelDraftStorageKey || loadedPanelDraftKeyRef.current !== panelDraftStorageKey
      || responseReportFormatId === undefined || loading || panelLoadError || saving || downloadingReport
      || !nodes.some(isPanelControlNode)) return;
    const draft: PanelControlDraft = {
      version: 1, updatedAt: savedResponseMeta?.updatedAt ?? null, reportFormatId: responseReportFormatId,
      measurements: earthingMeasurementsByNode, rcdMeasurements: rcdMeasurementsByNode,
      answers: controlGroupAnswers,
      yearValues: Object.fromEntries(Object.entries(yearDraftValues).filter(([key]) => key.startsWith('panel:'))),
    };
    try {
      sessionStorage.setItem(panelDraftStorageKey, JSON.stringify(draft));
    } catch {
      if (!panelDraftStorageWarningRef.current) {
        panelDraftStorageWarningRef.current = true;
        message.warning('Pano taslağı bu tarayıcıda saklanamadı. Sayfayı kapatmadan önce kaydediniz.');
      }
    }
  }, [panelDraftStorageKey, responseReportFormatId, loading, panelLoadError, saving, downloadingReport,
    nodes, savedResponseMeta?.updatedAt, earthingMeasurementsByNode, rcdMeasurementsByNode,
    controlGroupAnswers, yearDraftValues]);

  const visibleControlGroups = useMemo(() => derivePanelControlGroups(
    nodes, controlGroups, earthingMeasurementsByNode, isCorrectionMode || isBulkReportEntryEnabled,
  ), [nodes, controlGroups, earthingMeasurementsByNode, isCorrectionMode, isBulkReportEntryEnabled]);

  const panelAnswerMaps = useMemo(() => Object.fromEntries(visibleControlGroups.map((group) => [String(group.id),
    group.archived ? group.answers : buildAnswersForSubmitFromMap(
      { ...answersWithFunctions, ...(controlGroupAnswers[String(group.id)] ?? group.answers) },
      { includeQuestion: (key) => isPanelQuestionInScope(group, key), stripTransientFields: false },
    ),
  ])), [visibleControlGroups, controlGroupAnswers, answersWithFunctions, buildAnswersForSubmitFromMap, isPanelQuestionInScope]);

  const panelIncompleteIssues = useMemo(() => visibleControlGroups
    .filter((group) => isCurrentPanelGroup(group, earthingMeasurementsByNode, isCorrectionMode))
    .flatMap((group) => {
      const bundles = Object.fromEntries(Object.entries(questionBundlesById)
        .filter(([key]) => isPanelQuestionInScope(group, key)));
      const required = Object.entries(bundles).filter(([, bundle]) =>
        isQuestionIncludedInInspectorForm(bundle.question)
        && isQuestionMatchedWithSelectedControlArea(bundle.question, selectedControlAreaId)
        && isControlTaskQuestionRequired(bundle.question, isOtherControlAreaSelected))
        .map(([key, bundle], index) => ({ key, question: bundle.question, order: index + 1,
          titles: [group.title], defaultValue: getDefaultMultipleAnswerPayload(bundle.question, bundle.answers) }));
      return collectIncompleteQuestionIssues({
        answersForSubmit: panelAnswerMaps[String(group.id)] ?? {}, questionBundlesById: bundles,
        requiredQuestions: required, selectedControlAreaId, visibleQuestionMetaByKey,
        nonconformityDescriptionQuestionIds: bulkCriteriaQuestionIds,
      }).map((issue) => ({ ...issue, key: panelQuestionKey(group.id, issue.key),
        questionKey: panelQuestionKey(group.id, issue.questionKey), questionTitle: `${group.title} — ${issue.questionTitle}` }));
    }), [visibleControlGroups, earthingMeasurementsByNode, isCorrectionMode, questionBundlesById, isPanelQuestionInScope,
      selectedControlAreaId, isOtherControlAreaSelected, panelAnswerMaps, visibleQuestionMetaByKey, bulkCriteriaQuestionIds]);

  const buildPanelAnswersForSubmit = useCallback(async () => {
    const payload: ControlGroupAnswers = {};
    for (const group of visibleControlGroups) {
      if (!isCurrentPanelGroup(group, earthingMeasurementsByNode, isCorrectionMode)) continue;
      const uploaded = await uploadPendingMultipleAnswerImages(panelAnswerMaps[String(group.id)] ?? {}, `panel:${group.id}`);
      payload[String(group.id)] = buildAnswersForSubmitFromMap({ ...answersWithFunctions, ...uploaded },
        { includeQuestion: (key) => isPanelQuestionInScope(group, key) });
    }
    return splitPanelAnswersForSubmit(visibleControlGroups.filter((group) =>
      isCurrentPanelGroup(group, earthingMeasurementsByNode, isCorrectionMode)), payload);
  }, [visibleControlGroups, earthingMeasurementsByNode, isCorrectionMode, panelAnswerMaps, answersWithFunctions,
    uploadPendingMultipleAnswerImages, buildAnswersForSubmitFromMap, isPanelQuestionInScope]);

  const incompleteQuestionIssues = useMemo(() => {
    if (isBulkReportEntryEnabled) {
      return buildBulkValidationAnswersForSubmit().flatMap((item) =>
        collectIncompleteQuestionIssues({
          answersForSubmit: item.answersForSubmit,
          questionBundlesById: classicQuestionBundles,
          requiredQuestions,
          selectedControlAreaId,
          visibleQuestionMetaByKey,
          bulkOrderNo: item.orderNo,
          bulkReportTableTitle: reportFormat?.bulkReportTableTitle,
          nonconformityDescriptionQuestionIds: bulkCriteriaQuestionIds,
        }),
      ).sort((a, b) => {
        if (a.sortIndex === b.sortIndex) {
          return a.questionTitle.localeCompare(b.questionTitle, 'tr');
        }
        return a.sortIndex - b.sortIndex;
      });
    } else {
      return collectIncompleteQuestionIssues({
        answersForSubmit: buildAnswersForValidation(),
        questionBundlesById: classicQuestionBundles,
        requiredQuestions,
        selectedControlAreaId,
        visibleQuestionMetaByKey,
        nonconformityDescriptionQuestionIds: bulkCriteriaQuestionIds,
      }).concat(panelIncompleteIssues);
    }
  }, [
    buildBulkValidationAnswersForSubmit,
    buildAnswersForValidation,
    bulkCriteriaQuestionIds,
    isBulkReportEntryEnabled,
    questionBundlesById,
    classicQuestionBundles,
    panelIncompleteIssues,
    requiredQuestions,
    reportFormat?.bulkReportTableTitle,
    selectedControlAreaId,
    visibleQuestionMetaByKey,
  ]);

  const focusIncompleteIssue = useCallback((issue: IncompleteQuestionIssue) => {
    if (isBulkReportEntryEnabled && issue.bulkOrderNo) {
      const synchronized = buildSynchronizedBulkAnswerMaps();
      const orderKey = String(issue.bulkOrderNo);
      setBulkActiveOrder(issue.bulkOrderNo);
      setAnswers({
        ...synchronized.sharedAnswers,
        ...(synchronized.answersByOrder[orderKey] ?? {}),
      });
      window.requestAnimationFrame(() => scrollToQuestion(issue.questionKey));
      return;
    }
    scrollToQuestion(issue.questionKey);
  }, [
    buildSynchronizedBulkAnswerMaps,
    isBulkReportEntryEnabled,
    scrollToQuestion,
  ]);

  const incompleteHeaderText = useMemo(() => {
    if (!incompleteQuestionIssues.length) {
      return 'Eksik yok';
    }
    return `Eksik: ${incompleteQuestionIssues.length}`;
  }, [incompleteQuestionIssues]);

  const requestBulkGroupSaveDecision = useCallback((pendingEdit: BulkPendingGroupEdit) => {
    return new Promise<'single' | 'group'>((resolve) => {
      Modal.confirm({
        title: 'Bu değişiklik nasıl uygulansın?',
        content: (
          <Space direction='vertical' size={6}>
            <Text>
              {`${resolveBulkFormLabel(Number(pendingEdit.orderKey))} ${pendingEdit.groupLabel} verilerini kullanıyor.`}
            </Text>
            <Text type='secondary'>
              Değişikliği sadece bu forma uygulayabilir veya aynı veri grubundaki tüm formları güncelleyebilirsiniz.
            </Text>
          </Space>
        ),
        okText: 'Bu veri grubundaki tüm formları güncelle',
        cancelText: 'Sadece bu formu güncelle',
        width: 560,
        onOk: () => resolve('group'),
        onCancel: () => resolve('single'),
      });
    });
  }, [resolveBulkFormLabel]);

  // Reconcile after hydration/source changes, not on every RCD edit or preview response.
  useEffect(() => {
    if (!isSpecialMeasurementEntryEnabled || responseReportFormatId === undefined
      || loading || rcdMeasurementsLoadError || saving || deleting || downloadingReport) return;
    const sources = Object.values(earthingMeasurementsByNode).flat();
    setRcdMeasurementsByNode(previous => {
      let next = previous;
      nodes.filter(isRcdNode).forEach(node => {
        const key = String(node.id);
        const rows = previous[key] ?? [];
        const completed = completeRcdMeasurements(rows, sources);
        if (completed !== rows) {
          if (next === previous) next = { ...previous };
          next[key] = completed;
        }
      });
      return next;
    });
  }, [earthingMeasurementsByNode, nodes, isSpecialMeasurementEntryEnabled, responseReportFormatId,
    loading, rcdMeasurementsLoadError, saving, deleting, downloadingReport]);

  const handleEarthingMeasurementsChange = useCallback(
    (nodeId: number, measurements: EarthingMeasurement[]) => {
      setEarthingMeasurementsByNode((previous) => ({
        ...previous,
        [String(nodeId)]: measurements.map((row) => row.id == null && !row.clientKey
          ? { ...row, clientKey: crypto.randomUUID() } : row),
      }));
    },
    [],
  );

  const handleRcdMeasurementsChange = useCallback((nodeId: number, rows: RcdMeasurement[]) => {
    setRcdMeasurementsByNode((previous) => ({ ...previous, [String(nodeId)]: rows }));
  }, []);

  const validateRcdMeasurements = useCallback(async () => {
    if (rcdMeasurementsLoadError) {
      throw new Error('RCD verileri yüklenemedi. Kaydetmeden önce sayfayı yeniden yükleyiniz.');
    }
    if (responseReportFormatId === undefined) {
      throw new Error('Rapor verilerinin yüklenmesini bekleyiniz.');
    }
    await Promise.all(Object.values(rcdTableRefs.current).map((table) => table?.validate()));
  }, [rcdMeasurementsLoadError, responseReportFormatId]);

  const validateEarthingMeasurements = useCallback(async () => {
    const tables = Object.values(earthingTableRefs.current).filter(
      (table): table is EarthingMeasurementsTableHandle => table !== null,
    );
    await Promise.all(tables.map((table) => table.validate()));
  }, []);

  const handleSaveAnswers = useCallback(async () => {
    if (savingRequestRef.current || downloadingReportRef.current) return;
    if (panelLoadError) { message.error('Pano Kontrol verileri yüklenemedi. Sayfayı yeniden yükleyiniz.'); return; }
    if (shouldBlockReportQuestions) {
      message.warning('Lütfen önce Tip - Özellik seçimi yapınız.');
      return;
    }

    let answersForSubmit = buildAnswersForSubmit();
    const firstIncompleteIssue = incompleteQuestionIssues[0];
    if (firstIncompleteIssue) {
      focusIncompleteIssue(firstIncompleteIssue);
      message.error(buildIncompleteIssueErrorMessage(firstIncompleteIssue));
      return;
    }

    if (!isBulkReportEntryEnabled) {
      const missingRequired = requiredQuestions.filter((item) => {
        const key = getQuestionKey(item.question);
        if (!key) {
          return true;
        }
        if (item.question?.questionType === 'MULTIPLE') {
          const storedIds = getMultipleSelectionIds(answersForSubmit[key]);
          const fallbackIds = getMultipleSelectionIds(item.defaultValue);
          return storedIds.length === 0 && fallbackIds.length === 0;
        }
        if (item.question?.questionType === 'TABLE') {
          const value = answersForSubmit[key];
          return !Array.isArray(value?.rows) || value.rows.length === 0;
        }
        const value = answersForSubmit[key];
        if (value === undefined || value === null) {
          return true;
        }
        if (typeof value === 'string') {
          return value.trim().length === 0;
        }
        return false;
      });
      if (missingRequired.length) {
        const firstMissing = missingRequired[0];
        const pathLabel = firstMissing.titles.length ? firstMissing.titles.join(' > ') : 'Soru';
        const missingQuestionKey = firstMissing.key || getQuestionKey(firstMissing.question);
        const missingDisplayLabel =
          (missingQuestionKey ? visibleQuestionMetaByKey.get(missingQuestionKey)?.displayLabel : undefined) ??
          `${firstMissing.order}.`;
        message.error(
          `Lütfen tüm zorunlu soruları yanıtlayınız. Eksik: ${pathLabel} - ${missingDisplayLabel}`,
        );
        return;
      }

      const missingRequiredDescriptionEntry = Object.entries(classicQuestionBundles)
        .sort(([firstQuestionId], [secondQuestionId]) => {
          const firstSortIndex = visibleQuestionMetaByKey.get(firstQuestionId)?.sortIndex ?? Number.MAX_SAFE_INTEGER;
          const secondSortIndex = visibleQuestionMetaByKey.get(secondQuestionId)?.sortIndex ?? Number.MAX_SAFE_INTEGER;
          return firstSortIndex - secondSortIndex;
        })
        .find(([questionId, bundle]) => {
          if (!isQuestionIncludedInInspectorForm(bundle?.question)) {
            return false;
          }
          if (!isQuestionMatchedWithSelectedControlArea(bundle?.question, selectedControlAreaId)) {
            return false;
          }
          if (bundle?.question?.questionType !== 'MULTIPLE') {
            return false;
          }
          const defaultValue = getDefaultMultipleAnswerPayload(bundle?.question, bundle?.answers);
          const isControlCriteriaQuestion = bulkCriteriaQuestionIds.has(String(questionId));
          const storedItems = isQuestionMultiSelectEnabled(bundle?.question)
            ? normalizeMultipleAnswers(answersForSubmit[questionId])
            : (() => {
                const stored = normalizeMultipleAnswer(answersForSubmit[questionId]);
                return stored?.id ? [stored] : [];
              })();
          const defaultItems = Array.isArray(defaultValue)
            ? defaultValue
            : defaultValue?.id
              ? [defaultValue]
              : [];
          const selectedItems = storedItems.length ? storedItems : defaultItems;
          return selectedItems.some((item) => {
            const selectedOption = Array.isArray(bundle?.answers)
              ? bundle.answers.find((answer: any) => String(answer?.id) === String(item?.id))
              : undefined;
            if (isQuestionMultiSelectEnabled(bundle?.question)) {
              const isNonconforming =
                (toOptionalBoolean(selectedOption?.isTrue) ?? toOptionalBoolean(item?.isTrue)) === false;
              return (
                isControlCriteriaQuestion &&
                isNonconforming &&
                ensureStringValue(item?.description).trim().length === 0
              );
            }
            return (
              requiresMultipleAnswerDescription(selectedOption, item, isControlCriteriaQuestion) &&
              ensureStringValue(item?.description).trim().length === 0
            );
          });
        });

      if (missingRequiredDescriptionEntry) {
        const [, missingBundle] = missingRequiredDescriptionEntry;
        const questionTitle = ensureStringValue(missingBundle?.question?.title).trim() || 'Soru';
        message.error(`Seçilen cevap için açıklama zorunlu: ${questionTitle}`);
        return;
      }
    }

    const customControlAreaNameForSubmit =
      isOtherControlAreaSelected ? customControlAreaName.trim() : '';
    if (isOtherControlAreaSelected && !customControlAreaNameForSubmit) {
      message.error('Diğer Tip - Özellik bölümünün doldurulması zorunludur.');
      return;
    }

    if (!proposalIdNumber || !taskIdNumber || !attributeIdNumber || !orderNumber) {
      message.error('Geçersiz kontrol bilgileri. Lütfen sayfayı yeniden yükleyiniz.');
      return;
    }

    if (saving || deleting) {
      return;
    }

    if (isSpecialMeasurementEntryEnabled) {
      try {
        await Promise.all([validateRcdMeasurements(), validateEarthingMeasurements()]);
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'RCD veya Topraklama ölçüm satırlarındaki hatalı alanları kontrol ediniz.');
        return;
      }
    }

    const elapsedSeconds = normalizeElapsedSeconds(elapsedSecondsDisplay);
    let bulkSubmitSourceMaps: {
      sharedAnswers?: Record<string, any>;
      answersByOrder?: AnswersByOrderMap;
    } | undefined;
    let bulkGroupSaveResult: {
      decision: 'single' | 'group';
      pendingEdit: BulkPendingGroupEdit;
      nextSourceGroupId?: string;
    } | null = null;

    if (isBulkReportEntryEnabled && bulkPendingGroupEdit) {
      const decision = await requestBulkGroupSaveDecision(bulkPendingGroupEdit);
      const synchronized = buildSynchronizedBulkAnswerMaps();
      if (decision === 'group') {
        const editedCriteriaAnswers = extractBulkCriteriaAnswers(
          synchronized.answersByOrder[bulkPendingGroupEdit.orderKey],
          bulkCriteriaQuestionIds,
        );
        const nextAnswersByOrder = applyBulkCriteriaAnswersToOrders(
          synchronized.answersByOrder,
          bulkPendingGroupEdit.orderKeys,
          editedCriteriaAnswers,
          bulkCriteriaQuestionIds,
        );
        const nextGroups = resolveBulkDataGroups(
          resolveBulkReportOrderKeys(bulkReportCount),
          nextAnswersByOrder,
          bulkCriteriaQuestionIds,
        );
        const updatedGroup = nextGroups.find((group) =>
          bulkPendingGroupEdit.orderKeys.every((orderKey) => group.orderKeys.includes(orderKey)),
        );
        bulkSubmitSourceMaps = {
          sharedAnswers: synchronized.sharedAnswers,
          answersByOrder: nextAnswersByOrder,
        };
        bulkGroupSaveResult = {
          decision,
          pendingEdit: bulkPendingGroupEdit,
          nextSourceGroupId: updatedGroup?.id,
        };
      } else {
        bulkSubmitSourceMaps = {
          sharedAnswers: synchronized.sharedAnswers,
          answersByOrder: synchronized.answersByOrder,
        };
        bulkGroupSaveResult = {
          decision,
          pendingEdit: bulkPendingGroupEdit,
        };
      }
    }

    if (savingRequestRef.current || downloadingReportRef.current) return;
    savingRequestRef.current = true;
    setSaving(true);
    try {
      const inspectionNoteAttachmentDocumentIds = await uploadPendingInspectionNoteAttachments();
      if (isBulkReportEntryEnabled) {
        const customControlAreaNameForSubmit =
          isOtherControlAreaSelected ? customControlAreaName.trim() : '';
        const uploadedBulkSubmitSourceMaps = await buildBulkSubmitSourceMapsWithUploadedImages(bulkSubmitSourceMaps);
        const {
          count,
          sharedAnswersForSubmit,
          answersByOrderForSubmit,
        } = buildBulkAnswersSubmitPayload(uploadedBulkSubmitSourceMaps);
        if (!hasBulkCapacity || (bulkReportCountLimit ?? 0) <= 0 || count > (bulkReportCountLimit ?? 0)) {
          message.error('Görev sayısı aşılamaz.');
          return;
        }
        const response = await saveControlTaskBulkAnswers(
          proposalIdNumber,
          taskIdNumber,
          attributeIdNumber,
          {
            bulkGroupId,
            count,
            sharedAnswers: sharedAnswersForSubmit,
            answersByOrder: answersByOrderForSubmit,
            selectedControlAreaId: selectedControlAreaIdForSubmit ?? null,
            customControlAreaName: customControlAreaNameForSubmit || null,
            notes: noteContents,
            inspectionNote,
            inspectionNoteAttachmentDocumentIds,
            elapsedSeconds,
            attributeStaticValues: prepareStaticValuesForSubmit(attributeStaticValues),
            toolStaticValues: prepareStaticValuesForSubmit(toolStaticValues),
          },
        );
        const nextBulkGroupId = toPositiveNumber(response?.bulkGroupId);
        if (nextBulkGroupId && nextBulkGroupId !== bulkGroupId) {
          setBulkGroupId(nextBulkGroupId);
          const nextSearchParams = new URLSearchParams(location.search);
          nextSearchParams.set('bulkGroupId', String(nextBulkGroupId));
          nextSearchParams.set('bulkReportCount', String(count));
          navigate(`${location.pathname}?${nextSearchParams.toString()}`, {
            replace: true,
            state: {
              ...locationState,
              bulkGroupId: nextBulkGroupId,
              bulkReportCount: count,
            },
          });
        }
        const responses = Array.isArray(response?.responses) ? response.responses : [];
        const activeResponse = responses.find((item: any) =>
          toPositiveNumber(item?.orderNo) === bulkActiveOrder,
        );
        if (activeResponse?.inspectionNoteAttachmentDocumentIds !== undefined) {
          replaceInspectionNoteAttachments(await hydrateInspectionNoteAttachments(
            activeResponse.inspectionNoteAttachmentDocumentIds,
          ));
        }
        const nextAnswersByOrder: AnswersByOrderMap = {};
        responses.forEach((item: any) => {
          const orderNo = toPositiveNumber(item?.orderNo);
          if (!orderNo) {
            return;
          }
          const orderAnswers: Record<string, any> = {};
          const itemAnswers = item?.answers && typeof item.answers === 'object' && !Array.isArray(item.answers)
            ? item.answers
            : {};
          Object.entries(itemAnswers).forEach(([questionId, value]) => {
            if (sharedAnswersForSubmit[questionId] !== undefined) {
              return;
            }
            orderAnswers[questionId] = value;
          });
          nextAnswersByOrder[String(orderNo)] = orderAnswers;
        });
        setBulkReportCount(count);
        setBulkSharedAnswers(sharedAnswersForSubmit);
        setBulkAnswersByOrder(nextAnswersByOrder);
        setPendingMultipleAnswerImages({});
        setAnswers({
          ...sharedAnswersForSubmit,
          ...(nextAnswersByOrder[String(bulkActiveOrder)] ?? {}),
        });
        const resolvedBulkGroupSaveResult = bulkGroupSaveResult;
        if (resolvedBulkGroupSaveResult) {
          setBulkOverrideSourcesByOrder((prev) => {
            const next = { ...prev };
            if (resolvedBulkGroupSaveResult.decision === 'single') {
              next[resolvedBulkGroupSaveResult.pendingEdit.orderKey] =
                resolvedBulkGroupSaveResult.pendingEdit.groupId;
            } else {
              resolvedBulkGroupSaveResult.pendingEdit.orderKeys.forEach((orderKey) => {
                delete next[orderKey];
              });
              if (resolvedBulkGroupSaveResult.nextSourceGroupId) {
                Object.keys(next).forEach((orderKey) => {
                  if (next[orderKey] === resolvedBulkGroupSaveResult.pendingEdit.groupId) {
                    next[orderKey] = resolvedBulkGroupSaveResult.nextSourceGroupId as string;
                  }
                });
              }
            }
            return next;
          });
          setBulkPendingGroupEdit(null);
        }
        message.success('Toplu rapor girişleri kaydedildi.');
        setSummaryDrawerVisible(true);
        return;
      }
      answersForSubmit = await uploadPendingMultipleAnswerImages(answersForSubmit);
      const panelPayload = await buildPanelAnswersForSubmit();
      const attributePayload = prepareStaticValuesForSubmit(attributeStaticValues);
      const toolPayload = prepareStaticValuesForSubmit(toolStaticValues);
      const response = correctionCaseIdNumber
        ? await saveControlTaskCorrectionAnswers(
            correctionCaseIdNumber,
            answersForSubmit,
            attributePayload,
            toolPayload,
            selectedControlAreaIdForSubmit ?? null,
            customControlAreaNameForSubmit || null,
            noteContents,
            inspectionNote,
            elapsedSeconds,
            inspectionNoteAttachmentDocumentIds,
            panelPayload.controlGroupAnswers,
          )
        : await saveControlTaskAnswers(
            proposalIdNumber,
            taskIdNumber,
            attributeIdNumber,
            orderNumber,
            answersForSubmit,
            attributePayload,
            toolPayload,
            selectedControlAreaIdForSubmit ?? null,
            customControlAreaNameForSubmit || null,
            noteContents,
            inspectionNote,
            elapsedSeconds,
            inspectionNoteAttachmentDocumentIds,
            isSpecialMeasurementEntryEnabled ? prepareEarthingMeasurementsForSubmit(earthingMeasurementsByNode) : undefined,
            isSpecialMeasurementEntryEnabled ? prepareRcdMeasurementsForSubmit(rcdMeasurementsByNode) : undefined,
            panelPayload.controlGroupAnswers,
            panelPayload.panelControlDraftAnswers,
          );
      applyControlGroups(response?.controlGroups);
      if (!correctionCaseIdNumber && response?.rcdMeasurementsByNode !== undefined) {
        setRcdMeasurementsByNode(normalizeRcdMeasurementsByNode(response.rcdMeasurementsByNode));
      }
      if (!correctionCaseIdNumber && response?.earthingMeasurementsByNode !== undefined) {
        setEarthingMeasurementsByNode(
          normalizeEarthingMeasurementsByNode(response.earthingMeasurementsByNode),
        );
      }
      if (response?.id != null) {
        setSavedResponseMeta({ id: response.id, updatedAt: response?.updatedAt });
        setResponseReportFormatId(resolveResponseReportFormatId(response));
      } else if (response?.updatedAt) {
        setSavedResponseMeta((prev) => (prev ? { ...prev, updatedAt: response.updatedAt } : prev));
      }
      setPendingMultipleAnswerImages({});
      setYearDraftValues({});
      if (response?.attributeStaticValues !== undefined) {
        setAttributeStaticValues(normalizeStaticValueList(response.attributeStaticValues));
      }
      if (response?.toolStaticValues !== undefined) {
        setToolStaticValues(normalizeStaticValueList(response.toolStaticValues));
      }
      if (response?.notes !== undefined) {
        setNoteContents(normalizeNotesMap(response.notes));
      }
      if (response?.inspectionNote !== undefined) {
        setInspectionNote(ensureStringValue(response.inspectionNote));
      }
      if (response?.inspectionNoteAttachmentDocumentIds !== undefined) {
        replaceInspectionNoteAttachments(await hydrateInspectionNoteAttachments(
          response.inspectionNoteAttachmentDocumentIds,
        ));
      }
      if (response && response.selectedControlAreaId !== undefined) {
        if (response.selectedControlAreaId === null) {
          setSelectedControlAreaId(undefined);
        } else {
          const persistedControlAreaId = normalizeControlAreaId(response.selectedControlAreaId);
          if (persistedControlAreaId !== undefined) {
            setSelectedControlAreaId(persistedControlAreaId);
          }
        }
      }
      if (response && response.customControlAreaName !== undefined) {
        setCustomControlAreaName(ensureStringValue(response.customControlAreaName));
      } else if (response?.id != null) {
        setCustomControlAreaName('');
      }
      if (response?.answers) {
        setAnswers(response.answers);
      }
      message.success(correctionCaseIdNumber ? 'Kontrol yanıtları kaydedildi.' : 'Kontrol yanıtları kaydedildi.');
      setSummaryDrawerVisible(true);
    } catch (err) {
      console.error('ControlTaskPage: failed to persist answers', err);
      message.error(err instanceof Error ? err.message : 'Kontrol yanıtları kaydedilirken bir hata oluştu.');
    } finally {
      savingRequestRef.current = false;
      setSaving(false);
    }
  }, [
    buildAnswersForSubmit,
    requiredQuestions,
    proposalIdNumber,
    taskIdNumber,
    attributeIdNumber,
    orderNumber,
    correctionCaseIdNumber,
    saving,
    deleting,
    attributeStaticValues,
    toolStaticValues,
    selectedControlAreaId,
    selectedControlAreaIdForSubmit,
    isOtherControlAreaSelected,
    customControlAreaName,
    noteContents,
    inspectionNote,
    uploadPendingInspectionNoteAttachments,
    hydrateInspectionNoteAttachments,
    replaceInspectionNoteAttachments,
    elapsedSecondsDisplay,
    questionBundlesById,
    visibleQuestionMetaByKey,
    uploadPendingMultipleAnswerImages,
    shouldBlockReportQuestions,
    isBulkReportEntryEnabled,
    bulkGroupId,
    bulkActiveOrder,
    buildBulkAnswersSubmitPayload,
    buildBulkSubmitSourceMapsWithUploadedImages,
    buildSynchronizedBulkAnswerMaps,
    buildIncompleteIssueErrorMessage,
    focusIncompleteIssue,
    incompleteQuestionIssues,
    answers,
    bulkCriteriaQuestionIds,
    bulkPendingGroupEdit,
    bulkReportCount,
    bulkReportCountLimit,
    hasBulkCapacity,
    requestBulkGroupSaveDecision,
    navigate,
    location.pathname,
    location.search,
    locationState,
    earthingMeasurementsByNode,
    buildPanelAnswersForSubmit,
    applyControlGroups,
    panelLoadError,
    panelIncompleteIssues,
    classicQuestionBundles,
    rcdMeasurementsByNode,
    validateEarthingMeasurements,
    validateRcdMeasurements,
    isSpecialMeasurementEntryEnabled,
  ]);

  const persistSelectedControlAreaToServer = useCallback(
    async (nextSelectedControlAreaId: ControlAreaValue | undefined, nextCustomControlAreaName = customControlAreaName) => {
      if (!proposalIdNumber || !taskIdNumber || !attributeIdNumber || !orderNumber) {
        return;
      }
      const nextSelectedOption = controlAreas.find((area) => area.id === nextSelectedControlAreaId);
      const nextIsOther =
        nextSelectedControlAreaId === OTHER_CONTROL_AREA_VALUE || nextSelectedOption?.isOther === true;
      const nextSelectedControlAreaIdForSubmit = nextIsOther
        ? OTHER_CONTROL_AREA_VALUE
        : nextSelectedControlAreaId;
      const customNameForSubmit =
        nextIsOther ? nextCustomControlAreaName.trim() : '';
      if (nextIsOther && !customNameForSubmit) {
        return;
      }
      const inspectionNoteAttachmentDocumentIds = normalizeInspectionNoteAttachmentDocumentIds(
        inspectionNoteAttachments.map((attachment) => attachment.documentId),
      );
      try {
        const response = correctionCaseIdNumber
          ? await saveControlTaskCorrectionAnswers(
              correctionCaseIdNumber,
              buildAnswersForSubmit(),
              prepareStaticValuesForSubmit(attributeStaticValues),
              prepareStaticValuesForSubmit(toolStaticValues),
              nextSelectedControlAreaIdForSubmit ?? null,
              customNameForSubmit || null,
              noteContents,
              inspectionNote,
              normalizeElapsedSeconds(elapsedSecondsDisplay),
              inspectionNoteAttachmentDocumentIds,
            )
          : await saveControlTaskAnswers(
              proposalIdNumber,
              taskIdNumber,
              attributeIdNumber,
              orderNumber,
              buildAnswersForSubmit(),
              prepareStaticValuesForSubmit(attributeStaticValues),
              prepareStaticValuesForSubmit(toolStaticValues),
              nextSelectedControlAreaIdForSubmit ?? null,
              customNameForSubmit || null,
              noteContents,
              inspectionNote,
              normalizeElapsedSeconds(elapsedSecondsDisplay),
              inspectionNoteAttachmentDocumentIds,
            );
        const refreshedGroups = response?.controlGroups;
        if (refreshedGroups) {
          setControlGroups(refreshedGroups);
          const nextGroups = derivePanelControlGroups(nodes, refreshedGroups, earthingMeasurementsByNode, isCorrectionMode);
          setControlGroupAnswers((previous) => ({ ...previous,
            ...reconcilePanelAnswers(visibleControlGroups, previous, nextGroups),
          }));
          setPendingMultipleAnswerImages((previous) => remapPanelScopedValues(previous, visibleControlGroups, nextGroups));
          setYearDraftValues((previous) => remapPanelScopedValues(previous, visibleControlGroups, nextGroups));
        }
        if (response?.attributeStaticValues !== undefined) {
          setAttributeStaticValues(normalizeStaticValueList(response.attributeStaticValues));
        }
        if (response?.toolStaticValues !== undefined) {
          setToolStaticValues(normalizeStaticValueList(response.toolStaticValues));
        }
        if (response?.notes !== undefined) {
          setNoteContents(normalizeNotesMap(response.notes));
        }
        if (response?.inspectionNote !== undefined) {
          setInspectionNote(ensureStringValue(response.inspectionNote));
        }
        if (response?.inspectionNoteAttachmentDocumentIds !== undefined) {
          replaceInspectionNoteAttachments(await hydrateInspectionNoteAttachments(
            response.inspectionNoteAttachmentDocumentIds,
          ));
        }
        if (response?.id != null) {
          setSavedResponseMeta({ id: response.id, updatedAt: response?.updatedAt });
          setResponseReportFormatId(resolveResponseReportFormatId(response));
        } else if (response?.updatedAt) {
          setSavedResponseMeta((prev) => (prev ? { ...prev, updatedAt: response.updatedAt } : prev));
        }
        if (response && response.selectedControlAreaId !== undefined) {
          if (response.selectedControlAreaId === null) {
            setSelectedControlAreaId(undefined);
          } else {
            const persistedControlAreaId = normalizeControlAreaId(response.selectedControlAreaId);
            if (persistedControlAreaId !== undefined) {
              setSelectedControlAreaId(persistedControlAreaId);
            }
          }
        }
        if (response && response.customControlAreaName !== undefined) {
          setCustomControlAreaName(ensureStringValue(response.customControlAreaName));
        } else if (response?.id != null) {
          setCustomControlAreaName('');
        }
      } catch (err) {
        console.error('ControlTaskPage: failed to persist selected control area', err);
      }
    },
    [
      proposalIdNumber,
      taskIdNumber,
      attributeIdNumber,
      orderNumber,
      correctionCaseIdNumber,
      buildAnswersForSubmit,
      controlAreas,
      attributeStaticValues,
      toolStaticValues,
      customControlAreaName,
      noteContents,
      inspectionNote,
      inspectionNoteAttachments,
      hydrateInspectionNoteAttachments,
      replaceInspectionNoteAttachments,
      elapsedSecondsDisplay,
      nodes,
      earthingMeasurementsByNode,
      isCorrectionMode,
      visibleControlGroups,
    ],
  );

  const renderTableCellEditor = (
    question: any,
    column: any,
    cellValue: any,
    updateCell: (value: any) => void,
    rowIndex?: number,
    scopeKey?: string,
  ) => {
    const columnType = column?.columnType ?? column?.questionType;
    const options = Array.isArray(column?.options) ? column.options : Array.isArray(column?.answers) ? column.answers : [];
    const pseudoQuestion = {
      ...question,
      questionType: columnType,
      minValue: column?.minValue,
      maxValue: column?.maxValue,
      isCurrentYear: column?.isCurrentYear,
      digits: column?.digits,
      functionText: column?.functionText,
      functionTag: column?.functionTag,
    };
    const validateTableCellValue = (rawValue: any) => {
      if (columnType !== 'NUMBER' && columnType !== 'YEAR') {
        return true;
      }
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        return true;
      }
      const numeric = getComparableNumericValue(rawValue, columnType);
      if (numeric === undefined) {
        message.error(columnType === 'YEAR' ? 'Lütfen geçerli bir yıl seçiniz.' : 'Lütfen geçerli bir sayı giriniz.');
        return false;
      }
      const min = parseNumericLimit(pseudoQuestion.minValue);
      const max = isCurrentYearEnabled(pseudoQuestion.isCurrentYear) ? new Date().getFullYear() : parseNumericLimit(pseudoQuestion.maxValue);
      if (min !== undefined && numeric < min) {
        message.error(`Bu alan için minimum değer ${min}.`);
        return false;
      }
      if (max !== undefined && numeric > max) {
        message.error(`Bu alan için maksimum değer ${max}.`);
        return false;
      }
      if (columnType === 'NUMBER') {
        const digitsLimit = Number(pseudoQuestion.digits);
        if (Number.isFinite(digitsLimit) && digitsLimit >= 0) {
          const decimalPart = String(numeric).split('.')[1] || '';
          if (decimalPart.length > digitsLimit) {
            message.error(`En fazla ${digitsLimit} ondalık basamak girilebilir.`);
            return false;
          }
        }
      }
      return true;
    };
    switch (columnType) {
      case 'NETWORK_TYPE':
        return (
          <Select
            aria-label='Şebeke Tipi'
            className='w-full'
            value={getNetworkTypeValue(cellValue)}
            options={NETWORK_TYPE_OPTIONS}
            allowClear
            placeholder='Seçiniz'
            onChange={(value) => updateCell(toNetworkTypeAnswer(value))}
          />
        );
      case 'MULTIPLE': {
        const normalized = normalizeMultipleAnswer(cellValue);
        const selectedId = normalized?.id ?? null;
        return (
          <Select
            value={selectedId}
            allowClear
            placeholder='Seçenek'
            style={{ minWidth: 160, width: '100%' }}
            onChange={(value) => {
              const option = options.find((item: any) => String(item?.id) === String(value));
              if (!option) {
                updateCell(undefined);
                return;
              }
              updateCell({
                id: String(option.id),
                value: option.title,
                abbreviation: option.abbreviation ?? null,
                description: null,
                isTrue: option.isTrue ?? null,
                type: MULTIPLE_TYPE,
              });
            }}
          >
            {options.map((option: any) => (
              <Option key={option?.id} value={String(option?.id)}>{option?.title}</Option>
            ))}
          </Select>
        );
      }
      case 'NUMBER':
        return (
          <InputNumber
            value={typeof cellValue === 'number' ? cellValue : cellValue !== undefined && cellValue !== null && cellValue !== '' ? Number(cellValue) : undefined}
            style={{ width: '100%' }}
            onChange={(value) => {
              if (validateTableCellValue(value)) {
                updateCell(value);
              }
            }}
          />
        );
      case 'DATE':
      case 'REGISTRATION_DATE':
      case 'DIPLOMA_DATE':
      case 'REPORT_DATE':
      case 'CONTROL_START_DATE':
      case 'CONTROL_END_DATE':
        return (
          <DatePicker
            value={toSafeDayjs(cellValue, 'YYYY-MM-DD')}
            format='DD/MM/YYYY'
            style={{ width: '100%' }}
            onChange={(value) => updateCell(convertValueForStorage(columnType, value))}
          />
        );
      case 'TIME':
      case 'STARTED_AT':
      case 'FINISHED_AT':
        const tableTimePickerKey = `table:${scopeKey ? `${scopeKey}:` : ''}${getQuestionKey(question) ?? 'question'}:${rowIndex ?? 'row'}:${column?.id ?? columnType}`;
        return (
          <TimePicker
            value={parseTimeValue(cellValue)}
            format='HH:mm'
            allowClear
            open={openTimePickerKey === tableTimePickerKey}
            onOpenChange={(open) => handleTimePickerOpenChange(tableTimePickerKey, open)}
            onClick={() => openTimePickerForSelection(tableTimePickerKey, cellValue, () => updateCell(undefined))}
            onFocus={() => openTimePickerForSelection(tableTimePickerKey, cellValue, () => updateCell(undefined))}
            style={{ width: '100%' }}
            onChange={(value) => updateCell(convertValueForStorage(columnType, value))}
          />
        );
      case 'YEAR':
        return (
          <DatePicker
            picker='year'
            value={toSafeDayjs(cellValue, 'YYYY')}
            style={{ width: '100%' }}
            disabledDate={isCurrentYearEnabled(pseudoQuestion.isCurrentYear) ? (current: any) => current?.year?.() > dayjs().year() : undefined}
            onChange={(value) => {
              if (validateTableCellValue(value)) {
                updateCell(convertValueForStorage(columnType, value));
              }
            }}
          />
        );
      case 'FUNCTION':
        return <Input value={cellValue !== undefined && cellValue !== null ? String(cellValue) : ''} readOnly placeholder='Bu hücre otomatik hesaplanır' />;
      case 'FREETEXT':
      default:
        return (
          <Input
            value={typeof cellValue === 'string' || typeof cellValue === 'number' ? String(cellValue) : ''}
            placeholder='Hücre'
            onChange={(event) => updateCell(event.target.value)}
          />
        );
    }
  };

  const renderTableAnswerField = (
    question: any,
    tableQuestions: QuestionBundle[],
    editContext?: AnswerEditContext,
  ) => {
    const key = getQuestionKey(question);
    if (!key) {
      return <Alert type='warning' message='Soru kimliği bulunamadı.' showIcon />;
    }
    const contextAnswers = editContext?.answers ?? answersWithFunctions;
    const changeAnswer = editContext?.onAnswerChange ?? handleAnswerChange;
    const columns = Array.isArray(tableQuestions)
      ? tableQuestions
          .map((bundle) => ({ ...(bundle?.question || {}), answers: Array.isArray(bundle?.answers) ? bundle.answers : [] }))
          .sort(sortByOrder)
      : [];
    if (!columns.length) {
      return <Alert type='info' message='Bu tablo sorusu için tablo kolonu tanımlı değil.' showIcon />;
    }
    const storedValue = contextAnswers[key];
    const setTableRows = (nextRows: any[]) => {
      changeAnswer(question, { rows: nextRows });
    };
    const fixedRowCount = resolveFixedTableRowCount(question);
    const buildDefaultTableCells = () => {
      const cells: Record<string, any> = {};
      columns.forEach((column) => {
        const columnType = String(column?.questionType ?? column?.columnType ?? '').toUpperCase();
        if (columnType !== 'MULTIPLE') {
          return;
        }
        const options = Array.isArray(column?.answers) ? column.answers : Array.isArray(column?.options) ? column.options : [];
        const option = options.find((answer: any) => answer?.isDefault || answer?.default);
        const payload = createMultipleAnswerFromOption(option);
        if (payload && !Array.isArray(payload) && column?.id !== undefined && column?.id !== null) {
          cells[String(column.id)] = payload;
        }
      });
      return cells;
    };
    const rawRows = Array.isArray(storedValue?.rows) ? storedValue.rows : [];
    const rows = fixedRowCount
      ? normalizeTableRowsToFixedCount(rawRows, fixedRowCount, buildDefaultTableCells)
      : rawRows;
    const rowCount = rows.length;
    const setRowCount = (countValue: number | null) => {
      const count = Number.isFinite(Number(countValue)) && Number(countValue) > 0 ? Number(countValue) : 0;
      const nextRows = Array.from({ length: count }, (_, index) => {
        const existing = rows[index];
        return existing && typeof existing === 'object'
          ? { ...existing, rowOrder: index + 1, cells: existing.cells || {} }
          : { rowOrder: index + 1, cells: buildDefaultTableCells() };
      });
      setTableRows(nextRows);
    };
    const updateCell = (rowIndex: number, columnId: any, value: any) => {
      const nextRows = rows.map((row: any, index: number) => ({
        rowOrder: index + 1,
        cells: { ...(row?.cells || {}) },
      }));
      while (nextRows.length <= rowIndex) {
        nextRows.push({ rowOrder: nextRows.length + 1, cells: {} });
      }
      const columnKey = String(columnId);
      if (value === undefined || value === null || value === '') {
        delete nextRows[rowIndex].cells[columnKey];
      } else {
        nextRows[rowIndex].cells[columnKey] = value;
      }
      setTableRows(nextRows);
    };
    return (
      <Space direction='vertical' size={12} style={{ width: '100%' }}>
        {!fixedRowCount ? (
          <InputNumber
            min={0}
            precision={0}
            value={rowCount}
            addonBefore='Satır Sayısı'
            onChange={setRowCount}
            style={{ width: 220 }}
          />
        ) : null}
        {rowCount > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: columns.length * 180 }}>
              <thead>
                <tr>
                  <th style={{ width: 72, border: '1px solid #f0f0f0', padding: 8, textAlign: 'left' }}></th>
                  {columns.map((column) => (
                    <th key={column.id} style={{ border: '1px solid #f0f0f0', padding: 8, textAlign: 'left' }}>
                      {column.title || 'Tablo Kolonu'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row: any, rowIndex: number) => (
                  <tr key={rowIndex}>
                    <td style={{ border: '1px solid #f0f0f0', padding: 8 }}>{rowIndex + 1}</td>
                    {columns.map((column) => {
                      const cellValue = row?.cells?.[String(column.id)];
                      return (
                        <td key={column.id} style={{ border: '1px solid #f0f0f0', padding: 8, minWidth: 160 }}>
                          {renderTableCellEditor(question, column, cellValue, (value) => updateCell(rowIndex, column.id, value), rowIndex, editContext?.scopeKey)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Space>
    );
  };

  const renderAnswerField = (
    question: any,
    answerOptions: any[],
    tableQuestions?: QuestionBundle[],
    editContext?: AnswerEditContext,
    isControlCriteriaQuestion = false,
  ) => {
    const key = getQuestionKey(question);
    if (!key) {
      return <Alert type='warning' message='Soru kimliği bulunamadı.' showIcon />;
    }
    const scopedKey = editContext?.scopeKey ? `${editContext.scopeKey}:${key}` : key;
    const contextAnswers = editContext?.answers ?? answersWithFunctions;
    const changeAnswer = editContext?.onAnswerChange ?? handleAnswerChange;
    const hideFalseMultipleAnswerImages = editContext?.hideFalseMultipleAnswerImages === true;
    const storedValue = contextAnswers[key];
    const fieldValue = getFieldValue(question?.questionType, storedValue);
    const isMultiSelectMultiple = isQuestionMultiSelectEnabled(question);
    const storedMultipleValue =
      question?.questionType === 'MULTIPLE' && !isMultiSelectMultiple
        ? normalizeMultipleAnswer(storedValue)
        : undefined;
    const storedMultipleValues =
      question?.questionType === 'MULTIPLE' && isMultiSelectMultiple
        ? normalizeMultipleAnswers(storedValue)
        : [];
    const defaultMultipleValue =
      question?.questionType === 'MULTIPLE'
        ? getDefaultMultipleAnswerPayload(question, answerOptions)
        : undefined;
    const defaultMultipleSingle = Array.isArray(defaultMultipleValue) ? defaultMultipleValue[0] : defaultMultipleValue;
    const defaultMultipleValues = Array.isArray(defaultMultipleValue)
      ? defaultMultipleValue
      : defaultMultipleValue
      ? [defaultMultipleValue]
      : [];
    const effectiveMultipleValue =
      question?.questionType === 'MULTIPLE'
        ? isMultiSelectMultiple
          ? undefined
          : storedMultipleValue?.id ?? defaultMultipleSingle?.id ?? null
        : fieldValue;
    const effectiveMultipleValues =
      question?.questionType === 'MULTIPLE' && isMultiSelectMultiple
        ? (storedMultipleValues.length ? storedMultipleValues : defaultMultipleValues)
            .map((answer) => answer?.id)
            .filter((id): id is string => typeof id === 'string' && id.length > 0)
        : [];
    const selectedMultipleOption =
      question?.questionType === 'MULTIPLE' &&
      !isMultiSelectMultiple &&
      Array.isArray(answerOptions) &&
      effectiveMultipleValue
        ? answerOptions.find((answer: any) => String(answer?.id) === String(effectiveMultipleValue))
        : undefined;
    const multipleDescriptionValue = ensureStringValue(storedMultipleValue?.description);
    const storedMultipleById = new Map(
      storedMultipleValues
        .filter((item) => typeof item?.id === 'string' && item.id.length > 0)
        .map((item) => [String(item.id), item] as const),
    );
    const hasFalseSelectedInSingle =
      question?.questionType === 'MULTIPLE' &&
      !isMultiSelectMultiple &&
      effectiveMultipleValue != null
        ? (() => {
            const selectedOption = Array.isArray(answerOptions)
              ? answerOptions.find((answer: any) => String(answer?.id) === String(effectiveMultipleValue))
              : undefined;
            if (selectedOption) {
              return toOptionalBoolean(selectedOption?.isTrue) === false;
            }
            return toOptionalBoolean(storedMultipleValue?.isTrue) === false;
          })()
        : false;
    const hasFalseSelectedInMultiple =
      question?.questionType === 'MULTIPLE' && isMultiSelectMultiple
        ? effectiveMultipleValues.some((selectedId) => {
            const selectedOption = Array.isArray(answerOptions)
              ? answerOptions.find((answer: any) => String(answer?.id) === String(selectedId))
              : undefined;
            if (selectedOption) {
              return toOptionalBoolean(selectedOption?.isTrue) === false;
            }
            return toOptionalBoolean(storedMultipleById.get(String(selectedId))?.isTrue) === false;
          })
        : false;
    const requiresMultipleDescription =
      question?.questionType === 'MULTIPLE' &&
      !isMultiSelectMultiple &&
      requiresMultipleAnswerDescription(
        selectedMultipleOption,
        storedMultipleValue,
        isControlCriteriaQuestion,
      );
    const shouldShowNonconformityReasonButton =
      question?.questionType === 'MULTIPLE' &&
      (isMultiSelectMultiple ? hasFalseSelectedInMultiple : hasFalseSelectedInSingle);
    const currentValueForNonconformityDrawer: MultipleAnswerPayload | undefined =
      question?.questionType === 'MULTIPLE'
        ? isMultiSelectMultiple
          ? effectiveMultipleValues
              .map((selectedId) => {
                const selectedOption = Array.isArray(answerOptions)
                  ? answerOptions.find((answer: any) => String(answer?.id) === String(selectedId))
                  : undefined;
                const stored = storedMultipleById.get(String(selectedId));
                return {
                  id: String(selectedId),
                  value: selectedOption?.title ?? stored?.value ?? null,
                  abbreviation: selectedOption?.abbreviation ?? stored?.abbreviation ?? null,
                  description: stored?.description ?? null,
                  isTrue: toOptionalBoolean(selectedOption?.isTrue) ?? stored?.isTrue ?? null,
                  type: MULTIPLE_TYPE,
                } as MultipleAnswerValue;
              })
          : {
              id: effectiveMultipleValue != null ? String(effectiveMultipleValue) : null,
              value: selectedMultipleOption?.title ?? storedMultipleValue?.value ?? null,
              abbreviation:
                selectedMultipleOption?.abbreviation ?? storedMultipleValue?.abbreviation ?? null,
              description: storedMultipleValue?.description ?? null,
              isTrue:
                toOptionalBoolean(selectedMultipleOption?.isTrue) ??
                storedMultipleValue?.isTrue ??
                null,
              type: MULTIPLE_TYPE,
            }
        : undefined;
    const nonconformityReasonActionButton =
      shouldShowNonconformityReasonButton && !editContext?.hideNonconformityActions ? (
      <Button
        size='small'
        style={{color:"red"}}
        onClick={() =>
          openNonconformityReasonDrawer(
            question,
            answerOptions,
            currentValueForNonconformityDrawer,
            changeAnswer,
          )
        }
      >
        Uygunsuzluk Nedenleri
      </Button>
    ) : null;

    const buildImageValuesForSelection = (
      selectedOption: any,
      stored?: MultipleAnswerValue,
    ): MultipleAnswerImageValue[] | undefined => {
      const configuredFields = normalizeAnswerImageFields(selectedOption);
      if (!configuredFields.length) {
        return stored?.images;
      }
      const storedByKey = new Map(
        (stored?.images ?? []).map((image) => [String(image.key), image] as const),
      );
      return configuredFields.map((field) => ({
        ...field,
        ...(storedByKey.get(String(field.key)) ?? {}),
        title: field.title,
        required: field.required,
      }));
    };

    const buildMultipleAnswerForSelection = (selectedId: string): MultipleAnswerValue => {
      const selectedOption = Array.isArray(answerOptions)
        ? answerOptions.find((answer: any) => String(answer?.id) === String(selectedId))
        : undefined;
      const stored = isMultiSelectMultiple ? storedMultipleById.get(String(selectedId)) : storedMultipleValue;
      return {
        id: String(selectedId),
        value: selectedOption?.title ?? stored?.value ?? null,
        abbreviation: selectedOption?.abbreviation ?? stored?.abbreviation ?? null,
        description: stored?.description ?? null,
        isTrue: toOptionalBoolean(selectedOption?.isTrue) ?? stored?.isTrue ?? null,
        documentId: stored?.documentId ?? null,
        dataUrl: stored?.dataUrl ?? null,
        imageUrl: stored?.imageUrl ?? null,
        imagePreviewUrl: stored?.imagePreviewUrl ?? null,
        imageName: stored?.imageName ?? null,
        imageType: stored?.imageType ?? null,
        images: buildImageValuesForSelection(selectedOption, stored),
        type: MULTIPLE_TYPE,
      };
    };

    const updateMultipleAnswerDescription = (selectedId: string, nextDescription: string) => {
      if (isMultiSelectMultiple) {
        const nextValue = effectiveMultipleValues.map((id) => {
          const item = buildMultipleAnswerForSelection(String(id));
          return String(id) === String(selectedId)
            ? { ...item, description: nextDescription }
            : item;
        });
        changeAnswer(question, nextValue, answerOptions);
        return;
      }

      const selectedValue = effectiveMultipleValue != null ? String(effectiveMultipleValue) : String(selectedId);
      changeAnswer(question, {
        ...buildMultipleAnswerForSelection(selectedValue),
        description: nextDescription,
      }, answerOptions);
    };

    const updateMultipleAnswerImage = (
      selectedId: string,
      imageFields: Pick<MultipleAnswerValue, 'documentId' | 'dataUrl' | 'imageUrl' | 'imagePreviewUrl' | 'imageName' | 'imageType'>,
      imageKey?: string,
    ) => {
      const pendingKey = buildPendingMultipleAnswerImageKey(scopedKey, selectedId, imageKey);
      if (imageFields.documentId === null && imageFields.imagePreviewUrl === null) {
        setPendingMultipleAnswerImages((prev) => {
          if (!(pendingKey in prev)) {
            return prev;
          }
          const next = { ...prev };
          delete next[pendingKey];
          return next;
        });
      }

      if (isMultiSelectMultiple) {
        const nextValue = effectiveMultipleValues.map((id) => {
          const item = buildMultipleAnswerForSelection(String(id));
          if (String(id) !== String(selectedId)) {
            return item;
          }
          if (imageKey) {
            const images = (item.images ?? []).map((image) =>
              String(image.key) === String(imageKey) ? { ...image, ...imageFields } : image,
            );
            return { ...item, images };
          }
          return { ...item, ...imageFields };
        });
        changeAnswer(question, nextValue, answerOptions);
        return;
      }

      const selectedValue = effectiveMultipleValue != null ? String(effectiveMultipleValue) : String(selectedId);
      const current = buildMultipleAnswerForSelection(selectedValue);
      changeAnswer(question, imageKey
        ? {
            ...current,
            images: (current.images ?? []).map((image) =>
              String(image.key) === String(imageKey) ? { ...image, ...imageFields } : image,
            ),
          }
        : {
            ...current,
            ...imageFields,
          }, answerOptions);
    };

    const handleMultipleAnswerImageBeforeUpload = async (selectedId: string, file: RcFile, imageKey?: string) => {
      try {
        const imagePreviewUrl = await readFileAsDataUrl(file);
        const pendingKey = buildPendingMultipleAnswerImageKey(scopedKey, selectedId, imageKey);
        setPendingMultipleAnswerImages((prev) => ({ ...prev, [pendingKey]: file }));
        updateMultipleAnswerImage(selectedId, {
          documentId: null,
          dataUrl: imagePreviewUrl,
          imageUrl: null,
          imagePreviewUrl,
          imageName: file.name,
          imageType: file.type,
        }, imageKey);
        message.success('Görsel seçildi. Veritabanına kaydetmek için Kaydet butonuna basınız.');
      } catch (err) {
        console.error('ControlTaskPage multiple answer image read failed', err);
        message.error('Görsel seçilirken bir hata oluştu.');
      }
      return Upload.LIST_IGNORE;
    };

    const renderSingleMultipleAnswerImageUpload = (
      answerValue: MultipleAnswerValue,
      title?: string | null,
      image?: MultipleAnswerImageValue,
    ) => {
      const imageKey = image?.key;
      const hasImage = image ? hasMultipleAnswerImageValue(image) : !!(answerValue.documentId || answerValue.imagePreviewUrl || answerValue.dataUrl);
      return (
        <Space
          key={`multiple-image-${answerValue.id}-${imageKey ?? 'legacy'}`}
          direction='vertical'
          size={8}
          style={{ width: '100%', padding: '8px 0' }}
        >
          <Space direction='vertical' size={2}>
            <Text strong>{image?.title || title || answerValue.value || 'Cevap görseli'}</Text>
            <Text type='secondary'>{image?.required ? 'Görsel eklemek zorunludur.' : 'Görsel eklemek isteğe bağlıdır.'}</Text>
          </Space>
          <Space size={8} wrap>
            <Upload
              accept='image/*'
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file) => handleMultipleAnswerImageBeforeUpload(String(answerValue.id), file, imageKey)}
            >
              <Button icon={<UploadOutlined />}>
                {hasImage ? 'Görseli Değiştir' : 'Görsel Yükle'}
              </Button>
            </Upload>
            {hasImage ? (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() =>
                  updateMultipleAnswerImage(String(answerValue.id), {
                    documentId: null,
                    dataUrl: null,
                    imageUrl: null,
                    imagePreviewUrl: null,
                    imageName: null,
                    imageType: null,
                  }, imageKey)
                }
              >
                Görseli Kaldır
              </Button>
            ) : null}
          </Space>
          {image?.imagePreviewUrl || image?.dataUrl || image?.imageUrl || answerValue.imagePreviewUrl || answerValue.dataUrl || answerValue.imageUrl ? (
            <Image
              src={image?.imagePreviewUrl || image?.dataUrl || image?.imageUrl || answerValue.imagePreviewUrl || answerValue.dataUrl || answerValue.imageUrl || undefined}
              alt={image?.imageName ?? answerValue.imageName ?? title ?? 'Cevap görseli'}
              width={240}
              style={{ borderRadius: 8 }}
            />
          ) : image?.documentId || answerValue.documentId ? (
            <Text type='secondary'>{image?.imageName || answerValue.imageName || 'Görsel veritabanına kaydedildi.'}</Text>
          ) : null}
        </Space>
      );
    };

    const renderMultipleAnswerImageUpload = (answerValue: MultipleAnswerValue, title?: string | null) => {
      if (answerValue.images?.length) {
        return (
          <Space key={`multiple-images-${answerValue.id}`} direction='vertical' size={8} style={{ width: '100%' }}>
            {answerValue.images.map((image) => renderSingleMultipleAnswerImageUpload(answerValue, title, image))}
          </Space>
        );
      }
      return renderSingleMultipleAnswerImageUpload(answerValue, title);
    };

    const renderMultipleAnswerOptionLabel = (answer: any) => {
      const title = answer?.title ?? '';
      const imageUrl = typeof answer?.imageUrl === 'string' && answer.imageUrl.trim() ? answer.imageUrl.trim() : null;
      if (!imageUrl) {
        return title;
      }
      return (
        <Space size={8} align='center'>
          <Image
            src={imageUrl}
            alt={title || 'Cevap görseli'}
            width={36}
            height={36}
            preview={false}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
          <span>{title}</span>
        </Space>
      );
    };

    switch (question?.questionType) {
      case 'NETWORK_TYPE':
        return (
          <Select
            aria-label='Şebeke Tipi'
            style={{ width: '100%', maxWidth: 160 }}
            value={fieldValue}
            options={NETWORK_TYPE_OPTIONS}
            allowClear
            placeholder='Seçiniz'
            onChange={(value) => changeAnswer(question, value)}
          />
        );
      case 'TABLE':
        return renderTableAnswerField(question, tableQuestions || [], editContext);
      case 'MULTIPLE':
        if (!answerOptions?.length) {
          return <Alert type='info' message='Bu soru için seçenek tanımlı değil.' showIcon />;
        }
        if (isMultiSelectMultiple) {
          return (
            <Space direction='vertical' size={8} style={{ width: '100%' }}>
              <Checkbox.Group
                value={effectiveMultipleValues}
                onChange={(values) => changeAnswer(question, values, answerOptions)}
                style={{ width: '100%' }}
              >
                <Space direction='vertical'>
                  {answerOptions.map((answer: any) => (
                    <Checkbox key={answer?.id ?? answer?.title} value={String(answer?.id)}>
                      {renderMultipleAnswerOptionLabel(answer)}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
              {isControlCriteriaQuestion
                ? effectiveMultipleValues
                    .map((selectedId) => {
                      const selectedOption = answerOptions.find(
                        (answer: any) => String(answer?.id) === String(selectedId),
                      );
                      const answerValue = buildMultipleAnswerForSelection(String(selectedId));
                      const isNonconforming =
                        (toOptionalBoolean(selectedOption?.isTrue) ?? toOptionalBoolean(answerValue.isTrue)) === false;
                      if (!isNonconforming) {
                        return null;
                      }
                      const answerTitle = selectedOption?.title ?? answerValue.value ?? 'Uygunsuz cevap';
                      return (
                        <TextArea
                          key={`multiple-description-${selectedId}`}
                          value={ensureStringValue(answerValue.description)}
                          rows={3}
                          placeholder='Açıklama giriniz'
                          aria-label={`${answerTitle} açıklaması`}
                          onChange={(event) =>
                            updateMultipleAnswerDescription(String(selectedId), event.target.value)
                          }
                          onBlur={handleDraftFieldBlur}
                        />
                      );
                    })
                    .filter(Boolean)
                : null}
              {nonconformityReasonActionButton}
              {effectiveMultipleValues
                .map((selectedId) => {
                  const selectedOption = answerOptions.find((answer: any) => String(answer?.id) === String(selectedId));
                  const answerValue = buildMultipleAnswerForSelection(String(selectedId));
                  const selectedIsTrue = toOptionalBoolean(selectedOption?.isTrue) ?? answerValue.isTrue;
                  return selectedIsTrue === false
                    ? hideFalseMultipleAnswerImages
                      ? null
                      : renderMultipleAnswerImageUpload(answerValue, selectedOption?.title)
                    : !!answerValue.images?.length
                    ? renderMultipleAnswerImageUpload(answerValue, selectedOption?.title)
                    : null;
                })
                .filter(Boolean)}
            </Space>
          );
        }
        return (
          <Space direction='vertical' size={8} style={{ width: '100%' }}>
            <Radio.Group
              value={effectiveMultipleValue ?? null}
              onChange={(event) => changeAnswer(question, event.target.value, answerOptions)}
            >
              <Space direction='vertical'>
                {answerOptions.map((answer: any) => (
                  <Radio key={answer?.id ?? answer?.title} value={String(answer?.id)}>
                    {renderMultipleAnswerOptionLabel(answer)}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
            {requiresMultipleDescription ? (
              <TextArea
                value={multipleDescriptionValue}
                rows={3}
                placeholder='Açıklama giriniz'
                onChange={(event) =>
                  effectiveMultipleValue != null
                    ? updateMultipleAnswerDescription(String(effectiveMultipleValue), event.target.value)
                    : undefined
                }
                onBlur={handleDraftFieldBlur}
              />
            ) : null}
            {nonconformityReasonActionButton}
            {effectiveMultipleValue != null
              ? (() => {
                  const answerValue = buildMultipleAnswerForSelection(String(effectiveMultipleValue));
                  return hasFalseSelectedInSingle
                    ? hideFalseMultipleAnswerImages
                      ? null
                      : renderMultipleAnswerImageUpload(answerValue, selectedMultipleOption?.title)
                    : !!answerValue.images?.length
                    ? renderMultipleAnswerImageUpload(answerValue, selectedMultipleOption?.title)
                    : null;
                })()
              : null}
          </Space>
        );
      case 'NUMBER':
        return (
          <InputNumber
            value={typeof fieldValue === 'number' ? fieldValue : undefined}
            style={{ width: '100%' }}
            onChange={(value) => changeAnswer(question, value)}
            onBlur={handleDraftFieldBlur}
          />
        );
      case 'DATE':
      case 'REGISTRATION_DATE':
      case 'REPORT_DATE':
      case 'CONTROL_START_DATE':
      case 'DIPLOMA_DATE':
      case 'CONTROL_END_DATE': {
        const dateValue = toSafeDayjs(fieldValue, 'YYYY-MM-DD');
        return (
          <DatePicker
            value={dateValue}
            format='DD/MM/YYYY'
            style={{ width: '100%' }}
            onChange={(value) => changeAnswer(question, value)}
          />
        );
      }
      case 'TIME':
      case 'STARTED_AT':
      case 'FINISHED_AT': {
        const timeValue = parseTimeValue(storedValue);
        const questionKey = getQuestionKey(question) ?? question?.title ?? 'question';
        const timePickerKey = `question:${editContext?.scopeKey ? scopedKey : questionKey}`;
        return (
          <TimePicker
            value={timeValue}
            format='HH:mm'
            allowClear
            open={openTimePickerKey === timePickerKey}
            onOpenChange={(open) => handleTimePickerOpenChange(timePickerKey, open)}
            onClick={() => openTimePickerForSelection(timePickerKey, storedValue, () => changeAnswer(question, null))}
            onFocus={() => openTimePickerForSelection(timePickerKey, storedValue, () => changeAnswer(question, null))}
            style={{ width: '100%' }}
            onChange={(value) => changeAnswer(question, value)}
          />
        );
      }
      case 'YEAR': {
        const storedYearValue =
          typeof storedValue === 'string' || typeof storedValue === 'number'
            ? String(storedValue)
            : dayjs.isDayjs(fieldValue)
            ? fieldValue.format('YYYY')
            : '';
        const yearInputValue = yearDraftValues[scopedKey] ?? storedYearValue;
        return (
          <Input
            inputMode='numeric'
            maxLength={4}
            value={yearInputValue}
            placeholder='YYYY'
            style={{ width: '100%' }}
            onChange={(event) => handleYearDraftChange(question, event.target.value, editContext?.scopeKey)}
            onBlur={() => commitYearDraft(question, changeAnswer, editContext?.scopeKey)}
            onPressEnter={() => commitYearDraft(question, changeAnswer, editContext?.scopeKey)}
          />
        );
      }
      case 'IDENTITY_NUMBER':
        return (
          <Input
            type='number'
            value={typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : ''}
            onChange={(event) => changeAnswer(question, event.target.value)}
            onBlur={handleDraftFieldBlur}
          />
        );
      case 'SGK_REGISTRATION_NUMBER': {
        const sgkValue =
          proposalSgkNumber ??
          (typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : '');
        return (
          <Input
            type='number'
            value={sgkValue}
            readOnly
            placeholder='SGK numarası otomatik gelir'
          />
        );
      }
      case 'PHONE': {
        const phoneValue =
          proposalCompanyPhone ??
          (typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : '');
        return (
          <Input
            value={phoneValue}
            readOnly
            placeholder='Firma telefonu otomatik gelir'
          />
        );
      }
      case 'EMAIL': {
        const emailValue =
          proposalCompanyEmail ??
          (typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : '');
        return (
          <Input
            value={emailValue}
            readOnly
            placeholder='Firma e-postası otomatik gelir'
          />
        );
      }
      case 'CONTRACT_ID': {
        const contractValue =
          inspectionTaskContractId ??
          (typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : '');
        return (
          <Input
            value={contractValue}
            readOnly
            placeholder='Sözleşme bilgisi otomatik gelir'
          />
        );
      }
      case 'FIRST_NAME': {
        const firstNameValue =
          inspectionTaskFirstName ??
          (typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : '');
        return (
          <Input
            value={firstNameValue}
            readOnly
            placeholder='Ad bilgisi otomatik gelir'
          />
        );
      }
      case 'LAST_NAME': {
        const lastNameValue =
          inspectionTaskLastName ??
          (typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : '');
        return (
          <Input
            value={lastNameValue}
            readOnly
            placeholder='Soyad bilgisi otomatik gelir'
          />
        );
      }
      case 'PROFESSION': {
        const professionValue =
          inspectionTaskProfession ??
          (typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : '');
        return (
          <Input
            value={professionValue}
            readOnly
            placeholder='Meslek bilgisi otomatik gelir'
          />
        );
      }
      case 'REGISTRATION_NUMBER': {
        const registrationValue =
          inspectionTaskRegistrationNumber ??
          (typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : '');
        return (
          <Input
            value={registrationValue}
            readOnly
            placeholder='Kayıt numarası otomatik gelir'
          />
        );
      }
      case 'DIPLOMA_NUMBER': {
        const diplomaValue =
          inspectionTaskDiplomaNumber ??
          (typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : '');
        return (
          <Input
            value={diplomaValue}
            readOnly
            placeholder='Diploma numarası otomatik gelir'
          />
        );
      }
      case 'TITLE': {
        const titleValue =
          proposalCompanyName ??
          (typeof fieldValue === 'string' || typeof fieldValue === 'number' ? fieldValue : '');
        return (
          <Input
            value={titleValue}
            readOnly
            placeholder='Firma ünvanı otomatik gelir'
          />
        );
      }
      case 'ADDRESS': {
        const addressValue = proposalAddressDisplay ?? (typeof fieldValue === 'string' ? fieldValue : '');
        return (
          <TextArea
            value={addressValue}
            rows={3}
            placeholder='Adres otomatik gelir'
            readOnly
          />
        );
      }
      case 'IMAGE': {
        const imageValue = fieldValue;
        return (
          <Space direction='vertical' size={8} style={{ width: '100%' }}>
            <Upload
              accept='image/*'
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file) => handleImageBeforeUpload(question, file, changeAnswer)}
            >
              <Button icon={<UploadOutlined />}>Görsel Yükle</Button>
            </Upload>
            {imageValue?.dataUrl ? (
              <Space direction='vertical' size={6}>
                <Image
                  src={imageValue.dataUrl}
                  alt={imageValue.name ?? question?.title}
                  width={240}
                  style={{ borderRadius: 8 }}
                />
                <Space size={8}>
                  <Text type='secondary'>{imageValue?.name}</Text>
                  <Button
                    danger
                    size='small'
                    onClick={() => changeAnswer(question, null)}
                  >
                    Görseli Kaldır
                  </Button>
                </Space>
              </Space>
            ) : null}
          </Space>
        );
      }
      case 'FUNCTION': {
        const displayText =
          fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : '';
        let correctAnswerTitle: string | undefined;
        const scopedFunctionContext = editContext?.scopeKey
          ? createFunctionEvaluationContext({ ...answersWithFunctions, ...contextAnswers }, questionBundlesById, attributeStaticValues, toolStaticValues) : undefined;
        const scopedMatch = scopedFunctionContext ? answerOptions.find((answer: any) =>
          ensureStringValue(answer?.functionText).trim() && deriveBooleanFromFunctionResult(
            evaluateFunctionTemplate(answer.functionText, scopedFunctionContext)) === true) : undefined;
        const match = editContext?.scopeKey ? (scopedMatch ? { answerId: String(scopedMatch.id), title: scopedMatch.title } : undefined)
          : functionAnswerMatches[key];
        if (match) {
          if (match.title) {
            correctAnswerTitle = match.title;
          } else if (match.answerId && Array.isArray(answerOptions)) {
            const answerMatch = answerOptions.find((answer: any) => String(answer?.id) === match.answerId);
            const resolvedTitle = ensureStringValue(answerMatch?.title).trim();
            if (resolvedTitle) {
              correctAnswerTitle = resolvedTitle;
            }
          }
        }
        if (!correctAnswerTitle && Array.isArray(answerOptions)) {
          const fallback = answerOptions.find((answer: any) => answer?.isTrue);
          const resolvedTitle = ensureStringValue(fallback?.title).trim();
          if (resolvedTitle) {
            correctAnswerTitle = resolvedTitle;
          }
        }
        return (
          <Space direction='vertical' size={8} style={{ width: '100%' }}>
            <TextArea
              value={displayText}
              rows={3}
              readOnly
              placeholder='Bu alan otomatik hesaplanır'
            />
            {correctAnswerTitle ? (
              <Text type='success' strong>
                Doğru Cevap: {correctAnswerTitle}
              </Text>
            ) : null}
          </Space>
        );
      }
      case 'FREETEXT':
      case 'HVAC':
      case 'PLUMBING':
      case 'HYDRANT':
      case 'CABINET':
      case 'WORK_EQUIPMENT':
      default:
        return (
          <TextArea
            value={typeof fieldValue === 'string' ? fieldValue : ''}
            rows={question?.questionType === 'FREETEXT' ? 4 : 3}
            placeholder='Yanıtınızı giriniz'
            onChange={(event) => changeAnswer(question, event.target.value)}
            onBlur={handleDraftFieldBlur}
            lang="tr"
            style={{ textTransform: 'uppercase' }}
          />
        );
    }
  };

  const isQuestionHiddenByControlArea = useCallback(
    (question: any) => shouldHideControlTaskQuestionForSelectedControlArea(question, selectedControlAreaId),
    [selectedControlAreaId],
  );

  const isQuestionMatchedWithSearch = useCallback((question: any) => {
    if (!normalizedQuestionSearchText) {
      return true;
    }
    const searchableText = [
      question?.title,
      question?.description,
      question?.questionDescription,
    ]
      .map((value) => ensureStringValue(value).trim())
      .filter(Boolean)
      .join(' ');

    return normalizeTurkish(searchableText).includes(normalizedQuestionSearchText);
  }, [normalizedQuestionSearchText]);

  const isQuestionMatchedWithRequiredFilter = useCallback(
    (question: any) =>
      shouldShowControlTaskQuestionForRequiredFilter(
        question,
        showOnlyRequiredQuestions,
        isOtherControlAreaSelected,
      ),
    [isOtherControlAreaSelected, showOnlyRequiredQuestions],
  );

  const firstMatchedQuestionKey = useMemo(() => {
    if (shouldBlockReportQuestions) {
      return null;
    }

    if (!normalizedQuestionSearchText) {
      return null;
    }

    const findInNode = (node: any): string | null => {
      if (!node?.id) {
        return null;
      }

      const childNodes = childrenMap[node.id] || [];
      if (!childNodes.length) {
        const bundles = itemsByNode[node.id] || [];
        for (let index = 0; index < bundles.length; index += 1) {
          const question = bundles[index]?.question;
          if (
            !isQuestionHiddenByControlArea(question) &&
            isQuestionMatchedWithRequiredFilter(question) &&
            isQuestionMatchedWithSearch(question)
          ) {
            const baseKey = buildQuestionRenderKey(question, node?.id, index);
            const panelSection = panelNodeSections.get(Number(node.id));
            if (panelSection != null) {
              const group = visibleControlGroups.find((item) => item.reportNodeId === panelSection
                && isCurrentPanelGroup(item, earthingMeasurementsByNode, isCorrectionMode));
              if (!group) continue;
              return panelQuestionKey(group.id, baseKey);
            }
            return baseKey;
          }
        }
      }

      for (const child of childNodes) {
        const matchedKey = findInNode(child);
        if (matchedKey) {
          return matchedKey;
        }
      }
      return null;
    };

    for (const root of rootNodes) {
      const matchedKey = findInNode(root);
      if (matchedKey) {
        return matchedKey;
      }
    }
    return null;
  }, [
    childrenMap,
    panelNodeSections,
    visibleControlGroups,
    earthingMeasurementsByNode,
    isCorrectionMode,
    isQuestionHiddenByControlArea,
    isQuestionMatchedWithRequiredFilter,
    isQuestionMatchedWithSearch,
    itemsByNode,
    normalizedQuestionSearchText,
    rootNodes,
    shouldBlockReportQuestions,
  ]);

  useEffect(() => {
    if (!firstMatchedQuestionKey) {
      return;
    }
    window.setTimeout(() => scrollToQuestion(firstMatchedQuestionKey), 0);
  }, [firstMatchedQuestionKey, scrollToQuestion]);

  const renderNode = (
    node: any,
    depth: number,
    bulkRenderMode?: 'shared' | 'perReport',
    options?: {
      editContext?: AnswerEditContext;
      controlGroup?: PanelControlViewGroup;
      hideNotes?: boolean;
    },
    isWithinControlCriteria = false,
  ): React.ReactNode => {
    if (isPanelControlNode(node) && !options?.controlGroup) {
      const groups = visibleControlGroups.filter((group) => group.reportNodeId === Number(node.id));
      const active = groups.filter((group) => isCurrentPanelGroup(group, earthingMeasurementsByNode, isCorrectionMode));
      return (
        <section key={node.id} className='mb-8 min-w-0' aria-busy={saving || downloadingReport}>
          <Title level={Math.min(3 + depth, 5) as 3 | 4 | 5}>{node.title}</Title>
          {panelLoadError ? <Alert type='error' showIcon message='Pano Kontrol verileri yüklenemedi. Sayfayı yeniden yükleyiniz.' />
            : responseReportFormatId === undefined ? <Skeleton active />
            : !active.length ? <Alert type='info' showIcon message={isBulkReportEntryEnabled
              ? 'Pano Kontrol, tekil rapor veri girişinde kullanılabilir.'
              : isCorrectionMode ? 'Düzeltme gruplarını oluşturmak için raporu kaydediniz.'
              : 'Pano sorularını görüntülemek için Topraklama bölümünde Ölçüm Noktası Grubu alanından bir pano seçiniz.'} /> : null}
          <div className='flex flex-col gap-4'>
            {active.map((group) => (
              <Card key={group.id} title={group.title} size='small' className='min-w-0'>
                <Form component={false} disabled={saving || deleting || downloadingReport || panelLoadError}>
                  {renderNode(node, depth + 1, bulkRenderMode, { ...options, controlGroup: group, hideNotes: true,
                    editContext: { scopeKey: `panel:${group.id}`, answers: panelAnswerMaps[String(group.id)] ?? {},
                      onAnswerChange: (question, value, answerOptions) => {
                        if (savingRequestRef.current || downloadingReportRef.current || deleting || panelLoadError) return;
                        const resolved = resolveStoredAnswerChange(question, value, answerOptions);
                        if (resolved) setControlGroupAnswers((previous) => ({ ...previous,
                          [String(group.id)]: applyStoredAnswerChangeToMap(previous[String(group.id)] ?? {}, resolved.key, resolved.storedValue) }));
                      } },
                  }, isWithinControlCriteria)}
                </Form>
              </Card>
            ))}
          </div>
        </section>
      );
    }
    const isControlCriteriaQuestion = isWithinControlCriteria || isControlCriteriaNode(node);
    const isSpecialRcdNode = isRcdNode(node);
    const isSpecialEarthingNode = isEarthingNode(node);
    const isSpecialMeasurementNode = isSpecialRcdNode || isSpecialEarthingNode;
    const shouldRenderRcdTable = shouldRenderRcdMeasurementsTable(
      node,
      isBulkReportEntryEnabled,
      isCorrectionMode,
      measurementResponseKind,
    );
    const shouldRenderStandaloneEarthingTable = shouldRenderEarthingMeasurementsTable(
      node,
      isBulkReportEntryEnabled,
      isCorrectionMode,
      measurementResponseKind,
    );
    const shouldRenderLegacyEarthingTable = shouldRenderRcdTable
      && Object.prototype.hasOwnProperty.call(earthingMeasurementsByNode, String(node.id));
    const shouldRenderEarthingTable = shouldRenderStandaloneEarthingTable
      || shouldRenderLegacyEarthingTable;
    const headingLevel = Math.min(3 + depth, 5);
    const bundles = isSpecialMeasurementNode ? [] : (itemsByNode[node.id] || []);
    const children = childrenMap[node.id] || [];
    const visibleBundleEntries = bundles.map((bundle, index) => ({ bundle, index })).filter(({ bundle }) => {
      const question = bundle?.question;
      if (options?.controlGroup && !isPanelQuestionInScope(options.controlGroup, String(question?.id))) return false;
      if (isBulkReportEntryEnabled && bulkRenderMode === 'shared' && !isSharedBulkQuestion(question)) {
        return false;
      }
      if (isBulkReportEntryEnabled && bulkRenderMode === 'perReport' && isSharedBulkQuestion(question)) {
        return false;
      }
      return (
        !isQuestionHiddenByControlArea(question) &&
        isQuestionMatchedWithRequiredFilter(question) &&
        isQuestionMatchedWithSearch(question)
      );
    });
    const renderedChildren = children
      .map((child: any) => renderNode(child, depth + 1, bulkRenderMode, options, isControlCriteriaQuestion))
      .filter(Boolean);
    const isNoteNode = node?.type === 'NOTE';
    const noteValue =
      isNoteNode && node?.id !== undefined && node?.id !== null && Number.isFinite(Number(node.id))
        ? noteContents[Number(node.id)] ?? ''
        : '';
    const shouldRenderNoteNode = shouldRenderControlTaskNoteInput(
      node,
      normalizedQuestionSearchText,
      showOnlyRequiredQuestions,
      options?.hideNotes,
    );

    if (
      !visibleBundleEntries.length &&
      !renderedChildren.length &&
      !shouldRenderNoteNode &&
      !shouldRenderRcdTable &&
      !shouldRenderEarthingTable
    ) {
      return null;
    }

    return (
      <div key={node.id} style={{ marginBottom: depth === 0 ? 32 : 24 }}>
        {!(options?.controlGroup && isPanelControlNode(node)) ? <Title level={headingLevel as 3 | 4 | 5} style={{ marginBottom: 0 }}>
          {node.title}
        </Title> : null}
        {shouldRenderRcdTable || shouldRenderEarthingTable ? (
          <>
            <Divider />
            {rcdMeasurementsLoadError ? (
              <Alert type='error' showIcon message='RCD ve Topraklama verileri yüklenemedi. Sayfayı yeniden yükleyiniz.' />
            ) : null}
            {shouldRenderRcdTable ? (
              <RcdMeasurementsTable
                ref={(table) => {
                  const nodeKey = String(node.id);
                  if (table) rcdTableRefs.current[nodeKey] = table;
                  else delete rcdTableRefs.current[nodeKey];
                }}
                value={rcdMeasurementsByNode[String(node.id)] ?? []}
                sources={Object.values(earthingMeasurementsByNode).flat()}
                networkType={activeNetworkType}

                onChange={(rows) => handleRcdMeasurementsChange(Number(node.id), rows)}
                disabled={saving || deleting || downloadingReport || rcdMeasurementsLoadError}
                loading={responseReportFormatId === undefined}
              />
            ) : null}
            {shouldRenderRcdTable && shouldRenderEarthingTable ? <Divider /> : null}
            {shouldRenderEarthingTable ? (
              <EarthingMeasurementsTable
                ref={(table) => {
                  const nodeKey = String(node.id);
                  if (table) {
                    earthingTableRefs.current[nodeKey] = table;
                  } else {
                    delete earthingTableRefs.current[nodeKey];
                  }
                }}
                value={earthingMeasurementsByNode[String(node.id)] ?? []}
                networkType={activeNetworkType}
                onChange={(measurements) =>
                  handleEarthingMeasurementsChange(Number(node.id), measurements)
                }
                disabled={saving || deleting || downloadingReport || rcdMeasurementsLoadError || responseReportFormatId === undefined}
              />
            ) : null}
          </>
        ) : null}
        {!isSpecialMeasurementNode && (children.length === 0 || !!options?.controlGroup) ? (
          <>
            <Divider style={{ margin: depth === 0 ? '12px 0 16px' : '8px 0 12px' }} />
            {shouldRenderNoteNode ? (
              <div style={{ marginBottom: 16 }}>
                <Space direction='vertical' size={6} style={{ width: '100%' }}>
                  <Text strong>Not</Text>
                  <TextArea
                    rows={4}
                    placeholder='Not giriniz'
                    value={noteValue}
                    onChange={(event) =>
                      handleNoteChange(
                        Number(node.id),
                        typeof event.target.value === 'string' ? event.target.value : '',
                      )
                    }
                    onBlur={handleDraftFieldBlur}
                    lang="tr"
                    style={{ textTransform: 'uppercase' }}
                  />
                </Space>
              </div>
            ) : null}
            <Row gutter={[16, 16]} wrap>
              {(() => {
                let questionDisplayIndex = 0;
                return visibleBundleEntries.map(({ bundle, index }) => {
                  const question = bundle.question;
                  const questionDescription = ensureStringValue(
                    question?.description ?? question?.questionDescription,
                  ).trim();
                  const hasQuestionDescription = questionDescription.length > 0;
                  const baseQuestionKey = buildQuestionRenderKey(question, node?.id, index);
                  const questionKey = options?.controlGroup ? panelQuestionKey(options.controlGroup.id, baseQuestionKey) : baseQuestionKey;
                  const questionAnchorId = buildQuestionAnchorId(questionKey);
                  const isRequiredQuestion = isControlTaskQuestionRequired(
                    question,
                    isOtherControlAreaSelected,
                  );
                  const typeInfo = question?.questionType ? questionTypeMap[question.questionType] : undefined;
                  // const span = getColSpan(question?.col);
                  const span = 24;
                  const questionIdNumeric = toPositiveNumber(question?.id);
                  const isFilteringByControlArea =
                    isNumericControlAreaId(selectedControlAreaId);
                  const isControlAreaHighlighted =
                    isFilteringByControlArea &&
                    questionIdNumeric !== undefined &&
                    highlightedControlAreaQuestionIds.has(questionIdNumeric);
                  const sourceAnswerPreview =
                    isCorrectionMode && questionIdNumeric !== undefined
                      ? formatAnswerPreview(options?.controlGroup
                          ? sourceControlGroupAnswers[String(options.controlGroup.originControlGroupId)]?.[String(questionIdNumeric)]
                          : sourceAnswersByQuestionId[String(questionIdNumeric)])
                      : '';
                  const shouldShowSourceAnswerPreview =
                    isCorrectionMode &&
                    questionIdNumeric !== undefined &&
                    (options?.controlGroup ? isPanelQuestionInScope(options.controlGroup, String(questionIdNumeric)) : correctionQuestionIds?.has(questionIdNumeric));
                  questionDisplayIndex += 1;
                  const questionDisplayLabel = buildQuestionDisplayLabel(node, questionDisplayIndex);
                  const questionContent = (
                    <>
                      <div style={{ marginBottom: 8 }}>
                        <Space size={8} wrap>
                          <Text strong>{questionDisplayLabel}</Text>
                          <Text>{question?.title}</Text>
                          {hasQuestionDescription ? (
                            <Tooltip title={renderQuestionDescriptionTooltip(questionDescription)}>
                              <InformationCircleIcon
                                size={16}
                                style={{ color: '#1677ff', cursor: 'pointer' }}
                              />
                            </Tooltip>
                          ) : null}
                          {typeInfo ? <Tag color={typeInfo.color}>{typeInfo.label}</Tag> : null}
                          {isRequiredQuestion ? <Tag color='red'>Zorunlu</Tag> : null}
                          {question?.isCritical ? <Tag color='volcano'>Kritik</Tag> : null}
                          {isControlAreaHighlighted ? (
                            <Tag color='gold'>
                              {selectedControlArea?.name
                                ? `${selectedControlArea.name} ile eşleşti`
                                : 'Tip - Özellik eşleşti'}
                            </Tag>
                          ) : null}
                        </Space>
                      </div>
                      <div style={{ paddingLeft: 24, paddingRight: 8 }}>
                        {shouldShowSourceAnswerPreview ? (
                          <Alert
                            type="warning"
                            showIcon
                            message="Önce"
                            description={sourceAnswerPreview || '-'}
                            style={{ marginBottom: 12 }}
                          />
                        ) : null}
                        {renderAnswerField(
                          question,
                          bundle.answers,
                          bundle.tableQuestions,
                          options?.editContext,
                          isControlCriteriaQuestion,
                        )}
                      </div>
                    </>
                  );
                  return (
                    <Col key={questionKey} span={span}>
                      <div id={questionAnchorId}>
                        {isControlAreaHighlighted ? (
                          <div
                            style={{
                              borderRadius: 12,
                              border: '1px solid #faad14',
                              padding: 12,
                              backgroundColor: 'rgba(250, 173, 20, 0.08)',
                            }}
                          >
                            {questionContent}
                          </div>
                        ) : (
                          questionContent
                        )}
                      </div>
                    </Col>
                  );
                });
              })()}
            </Row>
          </>
        ) : null}
        {renderedChildren}
      </div>
    );
  };

  const renderStaticValuesSection = (
    title: string,
    values: StaticValueFormEntry[],
    onChange: (index: number, value: string) => void,
    onInputBlur?: () => void,
  ) => {
    if (!values.length) {
      return null;
    }
    return (
      <Space direction='vertical' size={12} style={{ width: '100%' }}>
        {values.map((item, index) => {
          const key = buildStaticValueKey(item, index);
          const label = item.name && item.name.trim().length ? item.name : `Sabit Değer #${index + 1}`;
          return (
            <div key={key} style={{ width: '100%' }}>
              <Space direction='vertical' size={4} style={{ width: '100%' }}>
                <Space size={8} wrap>
                  <Text strong>{label}</Text>
                  {item.functionTag ? <Tag color='geekblue'>{item.functionTag}</Tag> : null}
                </Space>
                <Input
                  value={item.value ?? ''}
                  onChange={(event) => onChange(index, event.target.value)}
                  onBlur={onInputBlur}
                  placeholder={`${title} değeri giriniz`}
                  allowClear
                />
              </Space>
            </div>
          );
        })}
      </Space>
    );
  };

  const hasSavedResponse = savedResponseMeta?.id != null;
  const canShowReportDownload = hasSavedResponse || isBulkReportEntryEnabled
    || visibleControlGroups.some((group) => isCurrentPanelGroup(group, earthingMeasurementsByNode, isCorrectionMode));

  const handleDownloadReport = useCallback(async () => {
    if (downloadingReportRef.current) {
      return;
    }

    if (shouldBlockReportQuestions) {
      message.warning('Lütfen önce Tip - Özellik seçimi yapınız.');
      return;
    }

    if (panelLoadError) { message.error('Pano Kontrol verileri yüklenemedi. Sayfayı yeniden yükleyiniz.'); return; }
    if (panelIncompleteIssues.length) {
      focusIncompleteIssue(panelIncompleteIssues[0]);
      message.error(buildIncompleteIssueErrorMessage(panelIncompleteIssues[0]));
      return;
    }

    if (!canShowReportDownload) {
      message.warning('Henüz kaydedilmiş kontrol yanıtı bulunmuyor.');
      return;
    }

    if (!proposalIdNumber || !taskIdNumber || !attributeIdNumber || !orderNumber) {
      message.error('Geçersiz kontrol bilgileri. Lütfen sayfayı yeniden yükleyiniz.');
      return;
    }

    if (isSpecialMeasurementEntryEnabled) {
      try {
        await Promise.all([validateRcdMeasurements(), validateEarthingMeasurements()]);
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'RCD veya Topraklama ölçüm satırlarındaki hatalı alanları kontrol ediniz.');
        return;
      }
    }

    if (downloadingReportRef.current || savingRequestRef.current) return;
    downloadingReportRef.current = true;
    setDownloadingReport(true);
    let objectUrlToRevoke: string | null = null;
    try {
      let publishedBulkGroupId = bulkGroupId;
      let answersForSubmit = buildAnswersForSubmit();
      const elapsedSeconds = normalizeElapsedSeconds(elapsedSecondsDisplay);
      const customControlAreaNameForSubmit =
        isOtherControlAreaSelected ? customControlAreaName.trim() : '';
      if (isOtherControlAreaSelected && !customControlAreaNameForSubmit) {
        message.error('Diğer Tip - Özellik bölümünün doldurulması zorunludur.');
        setDownloadingReport(false);
        return;
      }
      const inspectionNoteAttachmentDocumentIds = await uploadPendingInspectionNoteAttachments();
      if (isBulkReportEntryEnabled) {
        const uploadedBulkSubmitSourceMaps = await buildBulkSubmitSourceMapsWithUploadedImages();
        const {
          count,
          sharedAnswersForSubmit,
          answersByOrderForSubmit,
        } = buildBulkAnswersSubmitPayload(uploadedBulkSubmitSourceMaps);
        if (!hasBulkCapacity || (bulkReportCountLimit ?? 0) <= 0 || count > (bulkReportCountLimit ?? 0)) {
          message.error('Görev sayısı aşılamaz.');
          setDownloadingReport(false);
          return;
        }
        const bulkResponse = await saveControlTaskBulkAnswers(
          proposalIdNumber,
          taskIdNumber,
          attributeIdNumber,
          {
            bulkGroupId,
            count,
            sharedAnswers: sharedAnswersForSubmit,
            answersByOrder: answersByOrderForSubmit,
            selectedControlAreaId: selectedControlAreaIdForSubmit ?? null,
            customControlAreaName: customControlAreaNameForSubmit || null,
            notes: noteContents,
            inspectionNote,
            inspectionNoteAttachmentDocumentIds,
            elapsedSeconds,
            attributeStaticValues: prepareStaticValuesForSubmit(attributeStaticValues),
            toolStaticValues: prepareStaticValuesForSubmit(toolStaticValues),
          },
        );
        const nextBulkGroupId = toPositiveNumber(bulkResponse?.bulkGroupId);
        publishedBulkGroupId = nextBulkGroupId ?? bulkGroupId;
        if (nextBulkGroupId && nextBulkGroupId !== bulkGroupId) {
          setBulkGroupId(nextBulkGroupId);
          const nextSearchParams = new URLSearchParams(location.search);
          nextSearchParams.set('bulkGroupId', String(nextBulkGroupId));
          nextSearchParams.set('bulkReportCount', String(count));
          navigate(`${location.pathname}?${nextSearchParams.toString()}`, {
            replace: true,
            state: {
              ...locationState,
              bulkGroupId: nextBulkGroupId,
              bulkReportCount: count,
            },
          });
        }
        setPendingMultipleAnswerImages({});
        const responses = Array.isArray(bulkResponse?.responses) ? bulkResponse.responses : [];
        const activeResponse = responses.find((item: any) => toPositiveNumber(item?.orderNo) === orderNumber);
        if (activeResponse?.id != null) {
          setSavedResponseMeta({ id: activeResponse.id, updatedAt: activeResponse?.updatedAt });
          setResponseReportFormatId(resolveResponseReportFormatId(activeResponse));
        } else {
          setResponseReportFormatId(null);
        }
      }
      if (isBulkReportEntryEnabled && !publishedBulkGroupId) {
        throw new Error('Toplu rapor grubu oluşturulamadı.');
      }
      if (!isBulkReportEntryEnabled) answersForSubmit = await uploadPendingMultipleAnswerImages(answersForSubmit);
      const panelPayload = await buildPanelAnswersForSubmit();
      const publishedReport = correctionCaseIdNumber
        ? await publishControlTaskCorrectionReport(
            correctionCaseIdNumber,
            answersForSubmit,
            selectedControlAreaIdForSubmit ?? null,
            customControlAreaNameForSubmit || null,
            noteContents,
            inspectionNote,
            elapsedSeconds,
            inspectionNoteAttachmentDocumentIds,
            panelPayload.controlGroupAnswers,
          )
        : isBulkReportEntryEnabled
          ? await publishBulkControlTaskReport(
              proposalIdNumber,
              taskIdNumber,
              attributeIdNumber,
              orderNumber,
              publishedBulkGroupId!,
            )
          : await publishControlTaskReport(
              proposalIdNumber,
              taskIdNumber,
              attributeIdNumber,
              orderNumber,
              answersForSubmit,
              selectedControlAreaIdForSubmit ?? null,
              customControlAreaNameForSubmit || null,
              noteContents,
              inspectionNote,
              elapsedSeconds,
              inspectionNoteAttachmentDocumentIds,
              undefined,
              isSpecialMeasurementEntryEnabled ? prepareEarthingMeasurementsForSubmit(earthingMeasurementsByNode) : undefined,
              isSpecialMeasurementEntryEnabled ? prepareRcdMeasurementsForSubmit(rcdMeasurementsByNode) : undefined,
              panelPayload.controlGroupAnswers,
              panelPayload.panelControlDraftAnswers,
            );
      if (!isBulkReportEntryEnabled && !correctionCaseIdNumber) {
        const nextMeasurements = applyEarthingSourceIds(earthingMeasurementsByNode,
          publishedReport.earthingMeasurementIdsByClientKey);
        const nextGroups = derivePanelControlGroups(nodes, controlGroups, nextMeasurements);
        setEarthingMeasurementsByNode(nextMeasurements);
        setRcdMeasurementsByNode(previous => Object.fromEntries(Object.entries(previous).map(([node, rows]) => [
          node, completeRcdMeasurements(rows, [], publishedReport.earthingMeasurementIdsByClientKey),
        ])));
        setControlGroupAnswers(reconcilePanelAnswers(visibleControlGroups, panelAnswerMaps, nextGroups,
          publishedReport.earthingMeasurementIdsByClientKey));
        // The POST has committed. Keep resolved source IDs even if the refresh fails.
        try {
          const refreshed = await fetchControlTaskAnswers(proposalIdNumber, taskIdNumber, attributeIdNumber, orderNumber);
          applyControlGroups(refreshed.controlGroups);
          if (refreshed.id != null) setSavedResponseMeta({ id: refreshed.id, updatedAt: refreshed.updatedAt });
          setResponseReportFormatId(resolveResponseReportFormatId(refreshed));
          if (refreshed.answers) setAnswers(refreshed.answers);
          if (refreshed.earthingMeasurementsByNode) {
            setEarthingMeasurementsByNode(normalizeEarthingMeasurementsByNode(refreshed.earthingMeasurementsByNode));
          }
          if (refreshed.rcdMeasurementsByNode) setRcdMeasurementsByNode(normalizeRcdMeasurementsByNode(refreshed.rcdMeasurementsByNode));
          setPendingMultipleAnswerImages({});
          setYearDraftValues({});
        } catch {
          message.warning('Rapor oluşturuldu; güncel pano verileri yüklenemedi. Yeniden kayıt yapmadan önce sayfayı yenileyiniz.');
          setPanelLoadError(true);
        }
      }
      const reportUrl = publishedReport?.url;
      if (!reportUrl) {
        message.error('Kontrol raporu bağlantısı alınamadı.');
        return;
      }
      if (publishedReport?.objectUrl) {
        objectUrlToRevoke = reportUrl;
      }
      const anchor = document.createElement('a');
      anchor.href = reportUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      message.success('Kontrol raporu yeni sekmede açıldı.');
    } catch (err) {
      console.error('ControlTaskPage: failed to publish control report', err);
      message.error(err instanceof Error ? err.message : 'Kontrol raporu oluşturulurken bir hata oluştu.');
    } finally {
      if (objectUrlToRevoke) {
        const urlToRevoke = objectUrlToRevoke;
        objectUrlToRevoke = null;
        window.setTimeout(() => URL.revokeObjectURL(urlToRevoke), 60000);
      }
      setDownloadingReport(false);
      downloadingReportRef.current = false;
    }
  }, [
    buildAnswersForSubmit,
    canShowReportDownload,
    uploadPendingMultipleAnswerImages,
    proposalIdNumber,
    taskIdNumber,
    attributeIdNumber,
    orderNumber,
    correctionCaseIdNumber,
    selectedControlAreaId,
    selectedControlAreaIdForSubmit,
    isOtherControlAreaSelected,
    customControlAreaName,
    noteContents,
    inspectionNote,
    uploadPendingInspectionNoteAttachments,
    elapsedSecondsDisplay,
    shouldBlockReportQuestions,
    isBulkReportEntryEnabled,
    bulkGroupId,
    bulkReportCountLimit,
    hasBulkCapacity,
    buildBulkAnswersSubmitPayload,
    buildBulkSubmitSourceMapsWithUploadedImages,
    attributeStaticValues,
    toolStaticValues,
    navigate,
    location.pathname,
    location.search,
    locationState,
    earthingMeasurementsByNode,
    buildPanelAnswersForSubmit,
    panelLoadError,
    panelIncompleteIssues,
    nodes,
    controlGroups,
    visibleControlGroups,
    panelAnswerMaps,
    applyControlGroups,
    focusIncompleteIssue,
    buildIncompleteIssueErrorMessage,
    rcdMeasurementsByNode,
    validateEarthingMeasurements,
    validateRcdMeasurements,
    isSpecialMeasurementEntryEnabled,
  ]);

  const handleControlAreaSelect = useCallback(
    (value: string | number | null) => {
      if (value === null || value === undefined || value === '') {
        setSelectedControlAreaId(undefined);
        setCustomControlAreaName('');
        void persistSelectedControlAreaToServer(undefined, '');
        return;
      }
      const normalized = normalizeControlAreaId(value);
      setSelectedControlAreaId(normalized);
      const selectedOption = controlAreas.find((area) => area.id === normalized);
      const nextIsOther = normalized === OTHER_CONTROL_AREA_VALUE || selectedOption?.isOther === true;
      if (!nextIsOther) {
        setCustomControlAreaName('');
      }
      const nextCustomControlAreaName = nextIsOther ? customControlAreaName : '';
      void persistSelectedControlAreaToServer(nextIsOther ? OTHER_CONTROL_AREA_VALUE : normalized, nextCustomControlAreaName);
    },
    [controlAreas, customControlAreaName, persistSelectedControlAreaToServer],
  );

  const handleCustomControlAreaNameCommit = useCallback((nextValue: string) => {
    setCustomControlAreaName(nextValue);
    void persistSelectedControlAreaToServer(selectedControlAreaId, nextValue);
  }, [
    persistSelectedControlAreaToServer,
    selectedControlAreaId,
  ]);

  const handleNoteChange = useCallback(
    (nodeId: number, value: string) => {
      if (!Number.isFinite(nodeId)) {
        return;
      }
      setNoteContents((prev) => {
        const next = {
          ...prev,
          [nodeId]: value,
        };
        return next;
      });
    },
    [],
  );

  const handleBackToPlanTasks = useCallback(() => {
    if (proposalIdNumber) {
      navigate(`/inspector/planning/detail/${proposalIdNumber}?tab=tasks`);
      return;
    }
    navigate(-1);
  }, [navigate, proposalIdNumber]);

  return (
    <Space direction='vertical' size={16} style={{ width: '100%' }}>
      <Card
        className='br-30px'
        title='Kontrol Bilgileri'
        extra={
          <Space>
            <Button onClick={handleBackToPlanTasks}>Geri</Button>
          </Space>
        }
      >
        {futureInspectionTaskDate ? (
          <Alert
            showIcon
            type='warning'
            message={`Bu görev ${dayjs(futureInspectionTaskDate).format('DD.MM.YYYY')} tarihine planlanmıştır.`}
            description='Kontrol girişi ve rapor kaydı bu göreve bağlanır. Devam etmeden önce planlanan tarihi kontrol ediniz.'
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Descriptions
          bordered
          size='small'
          column={1}
          labelStyle={{ width: 220, maxWidth: 220, fontWeight: 600, color: 'rgba(0,0,0,0.88)' }}
        >
          <Descriptions.Item label='Kontrol ID'>{taskIdNumber ?? '-'}</Descriptions.Item>
          <Descriptions.Item label='Kontrol Sırası'>{orderNumber ?? '-'}</Descriptions.Item>
          <Descriptions.Item label='Parametre'>{attributeLabel ?? '-'}</Descriptions.Item>
          {isCorrectionMode ? (
            <Descriptions.Item label='Kontrol Türü'>
              <Tag color={resolveCorrectionCaseTypeLabel(correctionCaseType).color}>
                {resolveCorrectionCaseTypeLabel(correctionCaseType).label}
              </Tag>
            </Descriptions.Item>
          ) : null}
          <Descriptions.Item label='Kontrol Süresi'>{formatElapsedSeconds(elapsedSecondsDisplay)}</Descriptions.Item>
        </Descriptions>
        <div style={{ marginTop: 16 }}>
          <Space direction='vertical' size={6} style={{ width: '100%' }}>
            <Text strong>Denetim Notu</Text>
            <TextArea
              rows={4}
              placeholder='Denetim notu giriniz'
              value={inspectionNote}
              onChange={(event) => setInspectionNote(event.target.value)}
              onPaste={handleInspectionNoteAttachmentPaste}
              onBlur={handleDraftFieldBlur}
              lang="tr"
              style={{ textTransform: 'uppercase' }}
            />
            <Upload.Dragger
              multiple
              showUploadList={false}
              beforeUpload={(file) => {
                addInspectionNoteAttachmentFiles([file]);
                return Upload.LIST_IGNORE;
              }}
            >
              <p className='ant-upload-drag-icon'>
                <UploadOutlined />
              </p>
              <p className='ant-upload-text'>Dosyaları buraya bırakın veya seçin</p>
              <p className='ant-upload-hint'>
                Birden fazla dosya ekleyebilir, panodaki dosyaları bu not alanına Ctrl+V ile yapıştırabilirsiniz.
              </p>
            </Upload.Dragger>
            {inspectionNoteAttachments.length ? (
              <Space direction='vertical' size={8} style={{ width: '100%' }}>
                {inspectionNoteAttachments.map((attachment) => {
                  const previewKind = resolveInspectionNoteAttachmentPreviewKind(attachment.type, attachment.name);
                  const attachmentUrl = attachment.previewUrl ?? attachment.url;
                  return (
                    <Card size='small' key={attachment.key}>
                      <Space align='center' wrap style={{ width: '100%' }}>
                        {previewKind === 'IMAGE' && attachmentUrl ? (
                          <Image
                            src={attachmentUrl}
                            alt={attachment.name}
                            width={48}
                            height={48}
                            style={{ objectFit: 'cover' }}
                            preview={false}
                          />
                        ) : (
                          <Tag>{previewKind === 'PDF' ? 'PDF' : 'DOSYA'}</Tag>
                        )}
                        <Space direction='vertical' size={0}>
                          <Text>{attachment.name}</Text>
                          {attachment.file ? <Text type='secondary'>Kaydedilmedi</Text> : null}
                        </Space>
                        <Button
                          size='small'
                          onClick={() => handleInspectionNoteAttachmentPreview(attachment)}
                          disabled={!attachmentUrl}
                        >
                          {previewKind === 'FILE' ? 'Aç / İndir' : 'Ön İzle'}
                        </Button>
                        <Popconfirm
                          title='Bu eki Denetim Notu’ndan kaldırmak istiyor musunuz?'
                          onConfirm={() => handleInspectionNoteAttachmentRemove(attachment.key)}
                          okText='Kaldır'
                          cancelText='Vazgeç'
                        >
                          <Button size='small' danger icon={<DeleteOutlined />} aria-label={`${attachment.name} ekini kaldır`}>
                            Kaldır
                          </Button>
                        </Popconfirm>
                      </Space>
                    </Card>
                  );
                })}
              </Space>
            ) : null}
          </Space>
        </div>
      </Card>

      {isBulkReportEntryEnabled ? (
        <Card className='br-30px' title='Toplu Rapor Girişi'>
          {bulkCapacityLoading ? <Text type='secondary'>Toplu rapor kotası yükleniyor…</Text> : null}
          {bulkCapacityError ? <Alert type='error' message={bulkCapacityError} showIcon className='mb-4' /> : null}
          {hasBulkCapacity ? (
            <Text type='secondary' className='block mb-4'>
              Güncel toplam adet: {bulkCapacity?.totalQuantity ?? '-'} · Diğer toplu girişlerde kullanılan adet: {bulkCapacity?.usedReportCount ?? '-'} · Kalan adet: {bulkReportCountLimit}
            </Text>
          ) : null}
          <Form.Item label={bulkReportCountLabel} style={{ marginBottom: 0 }}>
            <InputNumber
              min={hasBulkCapacity && (bulkReportCountLimit ?? 0) <= 0 ? 0 : 1}
              max={bulkReportCountLimit}
              precision={0}
              value={bulkReportCount}
              disabled={!hasBulkCapacity || (bulkReportCountLimit ?? 0) <= 0}
              style={{ width: 220 }}
              onChange={handleBulkReportCountChange}
            />
          </Form.Item>
        </Card>
      ) : null}

      {(controlAreasLoading || controlAreasError || controlAreas.length > 0) && (
        <Card className='br-30px' title='Tip - Özellik'>
          {controlAreasLoading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : controlAreasError ? (
            <Alert type='warning' message={controlAreasError} showIcon />
          ) : (
            <Space direction='vertical' size={12} style={{ width: '100%' }}>
              <Select
                showSearch
                allowClear
                placeholder='Tip - Özellik seçin'
                style={{ width: '100%' }}
                value={selectedControlAreaId ?? undefined}
                optionFilterProp='label'
                onChange={(value) =>
                  handleControlAreaSelect(value === undefined ? null : (value as string | number))
                }
                options={controlAreas.map((area) => ({ label: area.name, value: area.id }))}
              />
              {isOtherControlAreaSelected ? (
                <Form.Item
                  label='Tip - Özellik'
                  required
                  style={{ marginBottom: 0 }}
                >
                  <CustomControlAreaNameInput
                    value={customControlAreaName}
                    onCommit={handleCustomControlAreaNameCommit}
                  />
                </Form.Item>
              ) : null}
            </Space>
          )}
        </Card>
      )}

      {(toolStaticValues.length > 0 || attributeStaticValues.length > 0) && (
        <Row gutter={[16, 16]}>
          {toolStaticValues.length > 0 ? (
            <Col xs={24} md={12}>
              <Card className='br-30px' title='Bileşen Sabit Değerleri'>
                {renderStaticValuesSection(
                  'Bileşen',
                  toolStaticValues,
                  handleToolStaticValueChange,
                  handleDraftFieldBlur,
                )}
              </Card>
            </Col>
          ) : null}
          {attributeStaticValues.length > 0 ? (
            <Col xs={24} md={12}>
              <Card className='br-30px' title='Parametre Sabit Değerleri'>
                {renderStaticValuesSection(
                  'Parametre',
                  attributeStaticValues,
                  handleAttributeStaticValueChange,
                  handleDraftFieldBlur,
                )}
              </Card>
            </Col>
          ) : null}
        </Row>
      )}

      <Card
        className='br-30px'
        title='Rapor Soruları'
        extra={
          <Space size={12} wrap>
            {canViewAutoFillButton ? (
              <Button
                onClick={handleControlTaskAutoFill}
                disabled={loading || !!error || shouldBlockReportQuestions}
              >
                Tarih ve Saatleri Otomatik Doldur
              </Button>
            ) : null}
            <Space size={6}>
              <Switch
                checked={showOnlyRequiredQuestions}
                onChange={setShowOnlyRequiredQuestions}
                disabled={loading || !!error || shouldBlockReportQuestions}
                aria-label='Yalnızca Zorunlu Sorular'
              />
              <Text>Yalnızca Zorunlu Sorular</Text>
            </Space>
          </Space>
        }
      >
        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : error ? (
          <Alert type='warning' message={error} showIcon />
        ) : shouldBlockReportQuestions ? (
          <Alert
            type='info'
            message='Rapor sorularını görüntülemek için Tip - Özellik seçimi yapılmalıdır.'
            showIcon
          />
        ) : isBulkReportEntryEnabled ? (
          <Space direction='vertical' size={24} style={{ width: '100%' }}>
            <Card size='small' title='Ortak Cevaplar'>
              {(() => {
                const renderedNodes = rootNodes
                  .map((node: any) => renderNode(node, 0, 'shared'))
                  .filter(Boolean);
                return renderedNodes.length ? (
                  renderedNodes
                ) : (
                  <Alert type='info' message='Ortak cevap alanı bulunan soru yok.' showIcon />
                );
              })()}
            </Card>
            <Card
              size='small'
              title={resolveBulkFormLabel(bulkActiveOrder)}
              extra={activeBulkFormStatus ? (
                <Space size={6} align='center'>
                  <span
                    aria-label={activeBulkFormStatus.hasAnswers ? 'Veri girildi' : 'Veri girilmedi'}
                    title={activeBulkFormStatus.hasAnswers ? 'Veri girildi' : 'Veri girilmedi'}
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 5,
                      background: activeBulkFormStatus.hasAnswers ? '#52c41a' : '#ff4d4f',
                      display: 'inline-block',
                    }}
                  />
                  {activeBulkFormStatus.status !== 'empty' ? (
                    <Tag color={activeBulkFormStatus.status === 'group' ? 'blue' : 'gold'}>
                      {activeBulkFormStatus.label}
                    </Tag>
                  ) : null}
                </Space>
              ) : null}
            >
              <Space direction='vertical' size={16} style={{ width: '100%' }}>
                {activeBulkFormStatus?.status === 'group' && activeBulkFormStatus.groupLabel ? (
                  <Alert
                    type='info'
                    showIcon
                    message={`Bu form ${activeBulkFormStatus.groupLabel} verilerini kullanıyor.`}
                  />
                ) : null}
                {activeBulkFormStatus?.status === 'custom' ? (
                  <Alert
                    type='warning'
                    showIcon
                    message='Bu form özelleştirildi.'
                    action={
                      activeBulkFormStatus.sourceGroupId ? (
                        <Button
                          size='small'
                          onClick={() => handleRevertBulkOrderToGroup(String(bulkActiveOrder))}
                        >
                          Grup verisine geri dön
                        </Button>
                      ) : undefined
                    }
                  />
                ) : null}
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      flexWrap: 'wrap',
                      marginBottom: 12,
                    }}
                  >
                    <Text strong>Form Listesi</Text>
                    <Space size={8} wrap>
                      <Button
                        type='primary'
                        disabled={!selectedBulkOrderKeys.length}
                        onClick={() => openBulkAssignmentDrawer(selectedBulkOrderKeys)}
                      >
                        Seçilenlere Veri Ata
                      </Button>
                      <Button onClick={() => openBulkAssignmentDrawer(bulkOrderKeys)}>
                        Tümüne Uygula
                      </Button>
                      <Button
                        disabled={!selectedBulkOrderKeys.length}
                        onClick={() => setSelectedBulkOrderKeys([])}
                      >
                        Seçimi Temizle
                      </Button>
                    </Space>
                  </div>
                  <Row gutter={[8, 8]}>
                    {bulkFormStatuses.map((status) => {
                      const isActive = status.orderNo === bulkActiveOrder;
                      const isSelected = selectedBulkOrderKeys.includes(status.orderKey);
                      const indicatorColor = status.hasAnswers ? '#52c41a' : '#ff4d4f';
                      const borderColor = isActive
                        ? '#1677ff'
                        : status.hasAnswers
                        ? '#b7eb8f'
                        : '#ffa39e';
                      const backgroundColor = isActive
                        ? 'rgba(22, 119, 255, 0.06)'
                        : status.hasAnswers
                        ? '#f6ffed'
                        : '#fff1f0';
                      return (
                        <Col key={status.orderKey} xs={24} sm={12} md={8} lg={6} xl={4} xxl={3}>
                          <div
                            role='button'
                            tabIndex={0}
                            onClick={() => handleBulkActiveOrderChange(status.orderNo)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleBulkActiveOrderChange(status.orderNo);
                              }
                            }}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 8,
                              minHeight: 84,
                              height: '100%',
                              padding: '10px 12px',
                              border: `1px solid ${borderColor}`,
                              borderLeft: `4px solid ${indicatorColor}`,
                              borderRadius: 8,
                              background: backgroundColor,
                              cursor: 'pointer',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                minWidth: 0,
                              }}
                            >
                              <Checkbox
                                checked={isSelected}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) => toggleBulkOrderSelection(status.orderKey, event.target.checked)}
                              />
                              <span
                                aria-label={status.hasAnswers ? 'Veri girildi' : 'Veri girilmedi'}
                                title={status.hasAnswers ? 'Veri girildi' : 'Veri girilmedi'}
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: 4,
                                  background: indicatorColor,
                                  flex: '0 0 auto',
                                }}
                              />
                              <Text
                                strong={isActive}
                                ellipsis
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                {resolveBulkFormLabel(status.orderNo)}
                              </Text>
                            </div>
                            {status.status !== 'empty' || status.sourceGroupLabel ? (
                              <Space size={4} wrap style={{ paddingLeft: 30 }}>
                                {status.status !== 'empty' ? (
                                  <Tag
                                    color={status.status === 'group' ? 'blue' : 'gold'}
                                    style={{ marginInlineEnd: 0 }}
                                  >
                                    {status.label}
                                  </Tag>
                                ) : null}
                                {status.status === 'custom' && status.sourceGroupLabel ? (
                                  <Text type='secondary' style={{ fontSize: 12 }}>
                                    {`${status.sourceGroupLabel} kaynağından ayrıldı`}
                                  </Text>
                                ) : null}
                              </Space>
                            ) : null}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
                {(() => {
                  const renderedNodes = rootNodes
                    .map((node: any) => renderNode(node, 0, 'perReport', { hideNotes: true }))
                    .filter(Boolean);
                  return renderedNodes.length ? (
                    renderedNodes
                  ) : (
                    <Alert type='info' message='Rapor bazlı cevap alanı bulunan soru yok.' showIcon />
                  );
                })()}
              </Space>
            </Card>
          </Space>
        ) : rootNodes.length ? (
          (() => {
            const renderedNodes = rootNodes
              .map((node: any) => renderNode(node, 0))
              .filter(Boolean);

            return renderedNodes.length ? (
              renderedNodes
            ) : (
              <Alert
                type='info'
                message={
                  normalizedQuestionSearchText
                    ? 'Arama kriterine uygun soru bulunamadı.'
                    : 'Bu rapor formatı için tanımlı soru bulunamadı.'
                }
                showIcon
              />
            );
          })()
        ) : (
          <Alert type='info' message='Bu rapor formatı için tanımlı soru bulunamadı.' showIcon />
        )}
      </Card>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Space size={8}>
          <Popconfirm
            title='Sonuç girişleri silinsin mi?'
            okText='Evet, sil'
            cancelText='Vazgeç'
            okButtonProps={{ danger: true, loading: deleting }}
            onConfirm={handleDeleteAnswers}
          >
            <Button danger loading={deleting} disabled={saving}>
              Sonuçları Sil
            </Button>
          </Popconfirm>
          <Tooltip
            title={shouldBlockReportQuestions ? 'Kaydetmek için lütfen önce Tip - Özellik seçimi yapınız.' : undefined}
          >
            <span>
              <Button
                type='primary'
                onClick={handleSaveAnswers}
                style={{ width: 120 }}
                loading={saving}
                disabled={deleting || shouldBlockReportQuestions}
              >
                Kaydet
              </Button>
            </span>
          </Tooltip>
        </Space>
      </div>

      <Drawer
        width={760}
        destroyOnClose
        open={bulkAssignmentDrawerOpen}
        onClose={closeBulkAssignmentDrawer}
        title='Seçilenlere Veri Ata'
        extra={
          <Space>
            <Button onClick={closeBulkAssignmentDrawer}>Vazgeç</Button>
            <Button type='primary' onClick={handleApplyBulkAssignment}>
              Kaydet
            </Button>
          </Space>
        }
      >
        <div ref={bulkAssignmentDrawerBodyRef}>
          <Space direction='vertical' size={16} style={{ width: '100%' }}>
            <Alert
              type='info'
              showIcon
              message={`${bulkAssignmentOrderKeys.length} form seçildi.`}
            />
            {bulkAssignmentIncompleteIssues.length ? (
              <Alert
                type='error'
                showIcon
                message={`Eksik: ${bulkAssignmentIncompleteIssues.length}`}
                description={
                  <Space direction='vertical' size={6} style={{ width: '100%' }}>
                    {bulkAssignmentIncompleteIssues.map((issue) => (
                      <Button
                        key={issue.key}
                        type='text'
                        danger
                        style={{
                          padding: 0,
                          justifyContent: 'flex-start',
                          whiteSpace: 'normal',
                          height: 'auto',
                          textAlign: 'left',
                        }}
                        onClick={() => scrollToBulkAssignmentQuestion(issue.questionKey)}
                      >
                        <Text strong>{issue.displayLabel} </Text>
                        <Text>{issue.questionTitle}</Text>
                      </Button>
                    ))}
                  </Space>
                }
              />
            ) : null}
            {bulkCriteriaRootNodes.length ? (
              bulkCriteriaRootNodes
                .map((node: any) =>
                  renderNode(node, 0, 'perReport', {
                    editContext: {
                      ...bulkAssignmentEditContext,
                      hideFalseMultipleAnswerImages: true,
                    },
                    hideNotes: true,
                  }),
                )
                .filter(Boolean)
            ) : (
              <Alert
                type='warning'
                showIcon
                message='Kontrol kriterleri altında veri atanabilecek rapor bazlı alan bulunamadı.'
              />
            )}
          </Space>
        </div>
      </Drawer>

      {!loading && !error && !shouldBlockReportQuestions ? (
        <>
          <div
            style={{
              position: 'fixed',
              right: 40,
              top: 190,
              zIndex: 901,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            {questionSearchOpen ? (
              <Input
                allowClear
                autoFocus
                prefix={<SearchOutlined style={{ fontSize: 18 }} />}
                value={questionSearchText}
                placeholder='Soru ara'
                onChange={(event) => setQuestionSearchText(event.target.value)}
                suffix={
                  <Button
                    type='text'
                    size='small'
                    icon={<CloseOutlined />}
                    onClick={() => {
                      setQuestionSearchText('');
                      setQuestionSearchOpen(false);
                    }}
                    aria-label='Aramayı kapat'
                    title='Aramayı kapat'
                  />
                }
                style={{
                  width: 'min(280px, calc(100vw - 80px))',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                }}
              />
            ) : (
              <Tooltip title='Soru ara' placement='left'>
                <Button
                  type={questionSearchText.trim().length ? 'primary' : 'default'}
                  shape='circle'
                  size='large'
                  icon={<SearchOutlined style={{ fontSize: 20 }} />}
                  onClick={() => setQuestionSearchOpen(true)}
                  aria-label='Soru ara'
                  style={{
                    width: 48,
                    minWidth: 48,
                    height: 48,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  }}
                />
              </Tooltip>
            )}
          </div>

          {statusPanelOpen ? (
            <Card
            size='small'
            title={<Text strong>Kontrol Durumu</Text>}
            extra={
              <Space size={8}>
                <Text
                  type={incompleteQuestionIssues.length ? 'danger' : 'success'}
                  style={{ maxWidth: 150 }}
                  ellipsis={{ tooltip: incompleteHeaderText }}
                >
                  {incompleteHeaderText}
                </Text>
                <Button
                  size='small'
                  type='text'
                  onClick={() => setStatusPanelOpen(false)}
                >
                  Kapat
                </Button>
              </Space>
            }
            style={{
              position: 'fixed',
              right: 16,
              top: 248,
              width: 500,
              maxHeight: 'calc(100vh - 268px)',
              overflow: 'hidden',
              zIndex: 900,
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            }}
            bodyStyle={{ maxHeight: 'calc(100vh - 328px)', overflowY: 'auto' }}
          >
            {incompleteQuestionIssues.length ? (
              <Space direction='vertical' size={8} style={{ width: '100%' }}>
                <Space direction='vertical' size={4} style={{ width: '100%' }}>
                  {incompleteQuestionIssues.map((issue) => (
                    <Button
                      key={issue.key}
                      type='text'
                      style={{
                        padding: 0,
                        justifyContent: 'flex-start',
                        whiteSpace: 'normal',
                        height: 'auto',
                        textAlign: 'left',
                      }}
                      onClick={() => focusIncompleteIssue(issue)}
                    >
                      <Space size={4} wrap>
                        <Text style={{marginBottom: 10}} strong>{issue.displayLabel} {' '}<Text style={{color: "#555"}}>{issue.questionTitle}</Text></Text>
                      </Space>
                    </Button>
                  ))}
                </Space>
              </Space>
            ) : (
              <Space direction='vertical' size={10} style={{ width: '100%' }}>
                <Alert type='success' showIcon message='Tüm zorunlu alanlar tamamlandı.' />
                <Button
                  type='primary'
                  block
                  onClick={handleSaveAnswers}
                  loading={saving}
                  disabled={deleting}
                >
                  Kaydet
                </Button>
              </Space>
            )}
            </Card>
          ) : (
            <div
              style={{
                position: 'fixed',
                right: 40,
                top: 248,
                zIndex: 900,
              }}
            >
              <Button
                type='primary'
                danger={incompleteQuestionIssues.length > 0}
                size='large'
                onClick={() => setStatusPanelOpen(true)}
                icon={<CheckListIcon size={20} />}
                title='Kontrol durumunu aç'
                aria-label='Kontrol durumunu aç'
                style={{
                  width: 48,
                  minWidth: 48,
                  height: 48,
                  backgroundColor:
                    incompleteQuestionIssues.length > 0 ? undefined : '#52c41a',
                  borderColor:
                    incompleteQuestionIssues.length > 0 ? undefined : '#52c41a',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                }}
              />
            </div>
          )}
        </>
      ) : null}

      <Drawer
        title='Uygunsuzluk Nedenleri'
        open={nonconformityReasonDrawerVisible}
        zIndex={1100}
        extra={ <Button
            type='primary'
            onClick={openNonconformityReasonCreateDrawer}
            disabled={!attributeIdNumber || !currentAccountId}
          >
            Yeni Uygunsuzluk Nedeni Ekle
          </Button>}
        width={520}
        onClose={() => {
          setNonconformityReasonDrawerVisible(false);
          setNonconformityReasonActiveTab('PRIVATE');
          setNonconformityReasonSearchText('');
          setNonconformityReasonTarget(null);
        }}
      >
        <Space direction='vertical' size={12} style={{ width: '100%' }}>
          {nonconformityReasonQuestionTitle ? (
            <Text type='secondary'>Soru: {nonconformityReasonQuestionTitle}</Text>
          ) : null}
         
          <Tabs
            activeKey={nonconformityReasonActiveTab}
            onChange={(key) =>
              setNonconformityReasonActiveTab(key as 'PRIVATE' | 'ALL')
            }
            items={[
              {
                key: 'PRIVATE',
                label: `Özel (${privateNonconformityReasons.length})`,
              },
              {
                key: 'ALL',
                label: `Tümü (${nonconformityReasonItems.length})`,
              },
            ]}
          />
          <Input
            allowClear
            value={nonconformityReasonSearchText}
            onChange={(event) => setNonconformityReasonSearchText(event.target.value)}
            placeholder='Kısaltma veya açıklamada ara'
          />
          {nonconformityReasonLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : nonconformityReasonError ? (
            <Alert type='error' showIcon message={nonconformityReasonError} />
          ) : searchedNonconformityReasons.length ? (
            <Space direction='vertical' size={10} style={{ width: '100%' }}>
              {searchedNonconformityReasons.map((reason) => {
                const ownerName = `${reason.account?.firstName ?? ''} ${reason.account?.lastName ?? ''}`.trim();
                const ownerText = ownerName || reason.account?.email || null;
                const ownerAccountId = Number(reason.accountId ?? reason.account?.id);
                const isOwnPrivateReason =
                  reason.isGeneral !== true && Number.isFinite(ownerAccountId) && ownerAccountId === currentAccountId;
                const canEditReason = isOwnPrivateReason;
                const canDeleteReason = isOwnPrivateReason;
                const editTooltip = canEditReason
                  ? 'Düzenle'
                  : reason.isGeneral === true
                      ? 'Genel uygunsuzluk nedenleri bu ekrandan düzenlenemez.'
                      : 'Yalnızca kendi eklediğiniz uygunsuzluk nedenlerini düzenleyebilirsiniz.';
                const deleteTooltip = canDeleteReason
                  ? 'Sil'
                  : reason.isGeneral === true
                      ? 'Genel uygunsuzluk nedenleri bu ekrandan silinemez.'
                      : 'Yalnızca kendi eklediğiniz uygunsuzluk nedenlerini silebilirsiniz.';
                return (
                  <Card
                  title={reason.abbreviation || `Uygunsuzluk Nedeni #${reason.id}`}
                    key={reason.id}
                    size='small'
                    extra={
                      <Space size={6}>
                        <Tooltip title={editTooltip}>
                          <span>
                            <Button
                              size='small'
                              icon={<EditOutlined />}
                              disabled={!canEditReason}
                              onClick={() => openNonconformityReasonEditDrawer(reason)}
                            />
                          </span>
                        </Tooltip>
                        <Popconfirm
                          title='Bu uygunsuzluk nedenini silmek istediğinize emin misiniz?'
                          onConfirm={() => handleDeleteNonconformityReason(reason)}
                          okText='Evet'
                          cancelText='Hayır'
                          disabled={!canDeleteReason}
                        >
                          <Tooltip title={deleteTooltip}>
                            <span>
                              <Button
                                danger
                                size='small'
                                icon={<DeleteOutlined />}
                                disabled={!canDeleteReason}
                              />
                            </span>
                          </Tooltip>
                        </Popconfirm>
                        <Button type='primary' size='small' onClick={() => handleSelectNonconformityReason(reason)}>
                          Seç
                        </Button>
                      </Space>
                    }
                  >
                    <Space direction='vertical' size={6} style={{ width: '100%' }}>
                      <Text>{reason.description || '-'}</Text>
                    </Space>
                  </Card>
                );
              })}
            </Space>
          ) : (
            <Alert
              type='info'
              showIcon
              message={nonconformityReasonSearchText.trim().length ? 'Arama kriterine uygun uygunsuzluk nedeni bulunamadı.' : 'Bu sekme için tanımlı uygunsuzluk nedeni bulunamadı.'}
            />
          )}
        </Space>
      </Drawer>
      <Drawer
        title={editingNonconformityReason ? 'Uygunsuzluk Nedeni Düzenle' : 'Yeni Uygunsuzluk Nedeni'}
        open={nonconformityReasonCreateDrawerVisible}
        zIndex={1200}
        width={520}
        destroyOnClose
        onClose={closeNonconformityReasonCreateDrawer}
      >
        <Form
          layout='vertical'
          form={nonconformityReasonCreateForm}
          onFinish={handleCreateNonconformityReason}
          initialValues={{
            isGeneral: false,
            attributeId: attributeIdNumber,
            accountId: currentAccountId,
          }}
        >
          <Form.Item
            name='attributeId'
            label='Parametre'
            rules={[{ required: true, message: 'Parametre seçiniz.' }]}
          >
            <Select disabled>
              {attributeIdNumber ? (
                <Option value={attributeIdNumber}>{createNonconformityAttributeLabel}</Option>
              ) : null}
            </Select>
          </Form.Item>
          <Form.Item
            name='accountId'
            label='Kullanıcı'
            rules={[{ required: true, message: 'Kullanıcı seçiniz.' }]}
          >
            <Select disabled>
              {currentAccountId ? <Option value={currentAccountId}>{currentAccountLabel}</Option> : null}
            </Select>
          </Form.Item>
          <Form.Item
            name='isGeneral'
            label='Kapsam'
            rules={[{ required: true, message: 'Kapsam seçiniz.' }]}
          >
            <Select disabled>
              <Option value={false}>Özel</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name='abbreviation'
            label='Kısaltma'
            rules={[{ required: true, message: 'Kısaltma zorunludur.' }]}
          >
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item
            name='description'
            label='Açıklama'
            rules={[{ required: true, message: 'Açıklama zorunludur.' }]}
          >
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button block type='primary' htmlType='submit' loading={nonconformityReasonCreateSubmitting}>
              {editingNonconformityReason ? 'Güncelle' : 'Kaydet'}
            </Button>
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title='Kontrol Özeti'
        open={summaryDrawerVisible}
        width={520}
        onClose={() => setSummaryDrawerVisible(false)}
        extra={
          canShowReportDownload ? (
            <Button
              type='primary'
              icon={<DownloadOutlined />}
              onClick={handleDownloadReport}
              loading={downloadingReport}
            >
              Raporu İndir
            </Button>
          ) : undefined
        }
      >
        {checkedSummaryGroups.length ? (
          <Space direction='vertical' size={12} style={{ width: '100%' }}>
            {checkedSummaryGroups.map((group) => (
              <Card key={group.key} size='small' title={group.title} bordered>
                <Space direction='vertical' size={6} style={{ width: '100%' }}>
                  {group.reminders.map((item) => (
                    <Space key={item.key} direction='vertical' size={0} style={{ width: '100%' }}>
                      <Text>
                        {`${item.order}. Kontrol Metni: ${item.controlText || 'Kontrol metni bulunamadı.'}`}
                      </Text>
                      <Text>
                        {`Kontrol Hatırlatma Metni: ${item.reminderText || 'Kontrol hatırlatma metni bulunamadı.'}`}
                      </Text>
                    </Space>
                  ))}
                </Space>
              </Card>
            ))}
          </Space>
        ) : (
          <Text>Kontrol hatırlatması tanımlanmamış.</Text>
        )}
      </Drawer>
      <Modal
        open={inspectionNoteAttachmentPreview !== null}
        title={inspectionNoteAttachmentPreview?.name ?? 'Denetim Notu Eki'}
        footer={null}
        width={900}
        destroyOnClose
        onCancel={() => setInspectionNoteAttachmentPreview(null)}
      >
        {inspectionNoteAttachmentPreview ? (
          resolveInspectionNoteAttachmentPreviewKind(
            inspectionNoteAttachmentPreview.type,
            inspectionNoteAttachmentPreview.name,
          ) === 'IMAGE' ? (
            <Image
              src={inspectionNoteAttachmentPreview.previewUrl ?? inspectionNoteAttachmentPreview.url}
              alt={inspectionNoteAttachmentPreview.name}
              style={{ width: '100%' }}
            />
          ) : (
            <iframe
              title={inspectionNoteAttachmentPreview.name}
              src={inspectionNoteAttachmentPreview.previewUrl ?? inspectionNoteAttachmentPreview.url}
              style={{ height: '70vh', width: '100%', border: 0 }}
            />
          )
        ) : null}
      </Modal>
    </Space>
  );
};

export default ControlTaskPage;
